import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, Button, Card, Badge, ButtonGroup, Nav, OverlayTrigger, Tooltip, Form, Modal, Row, Col, Alert, Popover, ProgressBar, Spinner, InputGroup } from 'react-bootstrap';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { getTrips, getClients, getDrivers, updateTrip, getVehicleTypes, getContracts, createTrip, getTripLogs, getVehicles, bulkImportTrips, bulkImportPreview, voidTrip, unvoidTrip, bulkSendPassengerConfirmations, sendDriverDailySchedule, getAreas, getCostCenters, createCostCenter, getRequesters, getBases, getProviders, getDriverNotificationLogs, triggerTomorrowAgendaNotifications, getPendingConfirmationsCount, resendDriverNotification, createDLocalPaymentLink, resetDLocalPayment, getDLocalPaymentStatus, sendDLocalPaymentLinkWhatsApp, getExchangeRates } from '../api';
import apiClient from '../../api';
import TripModal from './TripModal';
import DriverAssignModal from './DriverAssignModal';
import EquityRadarWidget from './EquityRadarWidget';
import CompleteTripModal from './CompleteTripModal';
import TripAuditModal from './TripAuditModal';
import GPSEvidenceGeneratorModal from './GPSEvidenceGeneratorModal';
import SmartAssignmentView from './SmartAssignmentView';
import SearchableMultiSelect from './SearchableMultiSelect';
import SearchableSelect from './SearchableSelect';
import TripDetailsAccordion from './TripDetailsAccordion';
import BulkVoucherModal from './BulkVoucherModal';
import { AlertTriangle, CheckCircle, Upload, Clock, Plane, Zap, Phone, MessageCircle, Loader, ShieldCheck, HelpCircle, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/useAuthStore';
import { downloadBulkImportTemplate } from '../utils/excelTemplateGenerator';

const COUNTRY_NAMES = {
    'CL': 'Chile',
    'AR': 'Argentina',
    'DO': 'Rep. Dominicana',
    'UY': 'Uruguay',
    'PE': 'Perú',
    'CO': 'Colombia',
    'BR': 'Brasil',
    'MX': 'México',
    'EC': 'Ecuador',
    'BO': 'Bolivia',
    'VE': 'Venezuela'
};

const getFlagEmoji = (timezone, localCurrencyCode, lat, lng, originBase = null) => {
    let tz = timezone;
    if (!tz) {
        if (localCurrencyCode === 'PEN') tz = 'America/Lima';
        else if (localCurrencyCode === 'ARS') tz = 'America/Argentina/Mendoza';
        else if (localCurrencyCode === 'EUR') tz = 'Europe/Madrid';
        else if (localCurrencyCode === 'UYU') tz = 'America/Montevideo';
        else if (lat < 0 && lat > -18 && lng < -68 && lng > -82) tz = 'America/Lima';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) tz = 'America/Montevideo';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) tz = 'Europe/Madrid';
        else tz = 'America/Santiago';
    }
    const tzLower = (tz || '').toLowerCase();
    let countryCode = originBase?.country_code ? originBase.country_code.toLowerCase().trim() : '';

    if (!countryCode) {
        if (tzLower.includes('montevideo') || tzLower.includes('uruguay')) countryCode = 'uy';
        else if (tzLower.includes('madrid') || tzLower.includes('barcelona') || tzLower.includes('spain')) countryCode = 'es';
        else if (tzLower.includes('santiago')) countryCode = 'cl';
        else if (tzLower.includes('argentina') || tzLower.includes('mendoza') || tzLower.includes('buenos_aires')) countryCode = 'ar';
        else if (tzLower.includes('lima')) countryCode = 'pe';
        else if (tzLower.includes('santo_domingo') || tzLower.includes('punta_cana')) countryCode = 'do';
        else if (tzLower.includes('bogota') || tzLower.includes('colombia')) countryCode = 'co';
        else if (tzLower.includes('new_york')) countryCode = 'us';
        else if (tzLower.includes('london')) countryCode = 'gb';
        else if (tzLower.includes('sao_paulo') || tzLower.includes('rio')) countryCode = 'br';
        else if (localCurrencyCode === 'EUR') countryCode = 'es';
        else if (localCurrencyCode === 'UYU') countryCode = 'uy';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) countryCode = 'uy';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) countryCode = 'es';
        else countryCode = 'cl';
    }

    return (
        <img 
            src={`https://flagcdn.com/w20/${countryCode}.png`} 
            srcSet={`https://flagcdn.com/w40/${countryCode}.png 2x`}
            width="20" 
            alt={countryCode.toUpperCase()}
            className="align-middle me-1 shadow-sm"
            style={{ borderRadius: '2px', border: '1px solid rgba(0,0,0,0.15)' }}
        />
    );
};

const ACTIVE_STATUSES = ['draft', 'pending', 'assigned', 'confirmed', 'en_route', 'arrived', 'on_board', 'scheduled'];
const HISTORY_STATUSES = ['completed', 'cancelled', 'no_show', 'cancelled_paid', 'anulado'];
const LOCKED_STATUSES = ['on_board'];

const STATUS_LABELS = {
    'draft': 'Pre-Reserva', 'DRAFT': 'Pre-Reserva',
    'pending': 'Pendiente', 'PENDING': 'Pendiente',
    'scheduled': 'Programada', 'SCHEDULED': 'Programada',
    'assigned': 'Asignado', 'ASSIGNED': 'Asignado',
    'confirmed': 'Confirmado', 'CONFIRMED': 'Confirmado',
    'en_route': 'En Camino', 'EN_ROUTE': 'En Camino',
    'arrived': 'En Posición', 'ARRIVED': 'En Posición',
    'on_board': 'En Ruta', 'ON_BOARD': 'En Ruta',
    'completed': 'Finalizado', 'COMPLETED': 'Finalizado',
    'cancelled': 'Cancelado', 'CANCELLED': 'Cancelado',
    'cancelled_paid': 'Cancelado con Cargo', 'CANCELLED_PAID': 'Cancelado con Cargo',
    'no_show': 'No Show', 'NO_SHOW': 'No Show',
    'anulado': 'ANULADO', 'ANULADO': 'ANULADO'
};

const translateLogDetails = (text) => {
    if (!text) return '';
    // Normalizar todas las posibles flechas unicode y texto a un formato estándar '->'
    let result = text.replace(/➝|➔|→|—>/g, '->');
    if (result.includes('Estado:')) {
        const regex = /Estado:\s*([a-zA-Z_]+)\s*->\s*([a-zA-Z_]+)/gi;
        result = result.replace(regex, (match, fromState, toState) => {
            const translatedFrom = STATUS_LABELS[fromState.toLowerCase()] || fromState;
            const translatedTo = STATUS_LABELS[toState.toLowerCase()] || toState;
            return `Estado: ${translatedFrom} ➔ ${translatedTo}`;
        });
    }
    return result;
};

const getStatusColor = (status, isDelayed = false) => {
    if (isDelayed) return '#dc3545'; // Rojo fuerte
    switch (status?.toLowerCase()) {
        case 'draft': return '#d97706'; // Ámbar para Pre-Reserva (Por Pagar)
        case 'assigned': return '#fd7e14'; // Naranja
        case 'confirmed': return '#198754'; // Verde
        case 'en_route': return '#ffc107'; // Amarillo
        case 'arrived': return '#0dcaf0'; // Celeste
        case 'on_board': return '#0d6efd'; // Azul
        case 'completed': return '#28a745'; // Verde finalizado
        case 'cancelled_paid': return '#e83e8c'; // Rosa/magenta para cancelado con cargo
        case 'pending': return '#6f42c1'; // Morado
        case 'anulado': return '#6c757d'; // Gris para anulados
        default: return '#adb5bd';
    }
};

const VehicleTypeIcon = ({ type }) => {
    const name = type?.name?.toLowerCase() || '';
    let icon = '🚗';
    let label = 'S';
    let fullName = 'Standard';

    if (name.includes('sprinter')) { icon = '🚐'; label = 'SPR'; fullName = 'Sprinter'; }
    else if (name.includes('large')) { icon = '🚐'; label = 'LPC'; fullName = 'Large People Carrier'; }
    else if (name.includes('people')) { icon = '🚐'; label = 'PC'; fullName = 'People Carrier'; }
    else if (name.includes('bus')) { icon = '🚌'; label = 'BUS'; fullName = 'Minibus / Bus'; }
    else if (name.includes('luxury')) { icon = '💎'; label = 'LUX'; fullName = 'Luxury'; }
    else if (name.includes('executive')) { icon = '⭐'; label = 'EXE'; fullName = 'Executive'; }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{fullName}</Tooltip>}>
            <div className="d-flex flex-column align-items-center" style={{lineHeight: 1, cursor: 'help'}}>
                <span style={{fontSize: '1.2rem'}}>{icon}</span>
                <small className="fw-bold text-muted" style={{fontSize: '0.65rem'}}>{label}</small>
            </div>
        </OverlayTrigger>
    );
};

const AuditSidebar = ({
    trip,
    onClose,
    onSave,
    clients,
    drivers,
    vehicles,
    vehicleTypes,
    areas,
    costCenters,
    onRefreshCostCenters,
}) => {
    const [selectedCC, setSelectedCC] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [driverNotesReviewed, setDriverNotesReviewed] = useState(false);
    const [waitingTimeMinutes, setWaitingTimeMinutes] = useState(0);
    const [waitingTimeAmount, setWaitingTimeAmount] = useState(0);
    const [waitingTimeProviderCost, setWaitingTimeProviderCost] = useState(0);
    const [waitingTimeManualFix, setWaitingTimeManualFix] = useState(false);
    const [finalPrice, setFinalPrice] = useState(0);
    const [driverEarnings, setDriverEarnings] = useState(0);
    const [providerCostBase, setProviderCostBase] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [auditOverride, setAuditOverride] = useState(false);
    const [auditOverrideReason, setAuditOverrideReason] = useState('');
    
    const [isSaving, setIsSaving] = useState(false);
    const [showWaitingForm, setShowWaitingForm] = useState(false);
    const [showOverrideForm, setShowOverrideForm] = useState(false);

    // Estados para la creación rápida de CC
    const [showCCModal, setShowCCModal] = useState(false);
    const [newCCCode, setNewCCCode] = useState('');
    const [newCCName, setNewCCName] = useState('');
    const [isCreatingCC, setIsCreatingCC] = useState(false);

    useEffect(() => {
        setSelectedCC(trip.cost_center_id || '');
        setSelectedArea(trip.area_id || '');
        setDriverNotesReviewed(trip.driver_notes_reviewed || false);
        setWaitingTimeMinutes(trip.waiting_time_minutes || 0);
        setWaitingTimeAmount(trip.waiting_time_amount || 0);
        setWaitingTimeProviderCost(trip.waiting_time_provider_cost || 0);
        setWaitingTimeManualFix(trip.waiting_time_manual_fix || false);
        setFinalPrice(trip.final_price || trip.estimated_price || 0);
        setDriverEarnings(trip.driver_earnings || 0);
        setProviderCostBase(trip.provider_cost_base || 0);
        setPaymentMethod(trip.payment_method || 'VOUCHER');
        setSelectedDriverId(trip.driver_id || '');
        setAuditOverride(trip.audit_override || false);
        setAuditOverrideReason(trip.audit_override_reason || '');
        
        setShowWaitingForm(false);
        setShowOverrideForm(false);
    }, [trip]);

    const clientCCs = useMemo(() => {
        return costCenters.filter(cc => cc.client_id === trip.client_id && cc.is_active !== false);
    }, [costCenters, trip.client_id]);

    const clientAreas = useMemo(() => {
        return areas.filter(a => a.client_id === trip.client_id && a.is_active !== false);
    }, [areas, trip.client_id]);

    // Opciones formateadas para SearchableSelect
    const ccOptions = useMemo(() => {
        return clientCCs.map(cc => ({
            id: cc.id,
            name: `${cc.code}${cc.name ? ` - ${cc.name}` : ''}`
        }));
    }, [clientCCs]);

    const areaOptions = useMemo(() => {
        return clientAreas.map(a => ({
            id: a.id,
            name: a.name
        }));
    }, [clientAreas]);

    const handleQuickCreateCC = async () => {
        if (!newCCCode.trim()) return;
        setIsCreatingCC(true);
        try {
            const newCC = await createCostCenter({
                code: newCCCode,
                name: newCCName,
                client_id: trip.client_id
            });
            if (onRefreshCostCenters) {
                onRefreshCostCenters(prev => [...prev, newCC]);
            }
            setSelectedCC(newCC.id);
            setShowCCModal(false);
            setNewCCCode('');
            setNewCCName('');
        } catch (err) {
            alert("Error al crear centro de costo.");
        } finally {
            setIsCreatingCC(false);
        }
    };

    const handleSave = async (extraPayload = {}) => {
        setIsSaving(true);
        try {
            const payload = {
                cost_center_id: selectedCC ? parseInt(selectedCC) : null,
                area_id: selectedArea ? parseInt(selectedArea) : null,
                driver_notes_reviewed: driverNotesReviewed,
                waiting_time_minutes: parseInt(waitingTimeMinutes),
                waiting_time_amount: parseFloat(waitingTimeAmount),
                waiting_time_provider_cost: parseFloat(waitingTimeProviderCost),
                waiting_time_manual_fix: waitingTimeManualFix,
                final_price: parseFloat(finalPrice),
                driver_earnings: parseFloat(driverEarnings),
                provider_cost_base: parseFloat(providerCostBase),
                payment_method: paymentMethod,
                driver_id: selectedDriverId ? parseInt(selectedDriverId) : null,
                audit_override: auditOverride,
                audit_override_reason: auditOverrideReason,
                ...extraPayload
            };
            const updated = await updateTrip(trip.id, payload);
            onSave(updated);
        } catch (err) {
            const msg = err.response?.data?.detail || "Error al guardar la auditoría.";
            alert(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuickResolveDriverNote = async () => {
        setDriverNotesReviewed(true);
        await handleSave({ driver_notes_reviewed: true });
    };

    const handleQuickOverride = async () => {
        if (!auditOverrideReason.trim()) {
            alert("Debe escribir una justificación para omitir las discrepancias.");
            return;
        }
        setAuditOverride(true);
        await handleSave({ audit_override: true, audit_override_reason: auditOverrideReason });
    };

    const computeDriverEarningsWithWait = (baseCostVal, waitCostVal) => {
        const base = parseFloat(baseCostVal) || 0;
        const wait = parseFloat(waitCostVal) || 0;
        const extras = (trip.extra_services || [])
            .filter(s => s.id !== 'reimbursable_expense')
            .reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
        const totalCosto = base + wait + extras;
        if (trip.snapshot_driver_rules) {
            try {
                const rules = typeof trip.snapshot_driver_rules === 'string'
                    ? JSON.parse(trip.snapshot_driver_rules)
                    : trip.snapshot_driver_rules;
                const isHybridBooking = Boolean(trip.provider?.hybrid_settlement && trip.client_id === 1);
                if (!isHybridBooking && rules && rules.type === 'VARIABLE') {
                    const p = totalCosto < (rules.threshold || 0) ? (rules.p1 || 0) : (rules.p2 || 0);
                    return Math.round(totalCosto * (p / 100.0));
                }
            } catch (e) {
                // fallback
            }
        }
        return Math.round(totalCosto);
    };

    const handleWaitingProviderCostChange = (newWaitCostVal) => {
        setWaitingTimeProviderCost(newWaitCostVal);
        setWaitingTimeManualFix(true);
        setDriverEarnings(computeDriverEarningsWithWait(providerCostBase, newWaitCostVal));
    };

    const handleMarkWaitIncluded = () => {
        setWaitingTimeMinutes(0);
        setWaitingTimeAmount(0);
        setWaitingTimeProviderCost(0);
        setWaitingTimeManualFix(true);
        setDriverEarnings(computeDriverEarningsWithWait(providerCostBase, 0));
    };

    const reasons = trip.validation_details || [];
    const hasCCError = reasons.some(r => r.includes("Falta CC"));
    const hasAreaError = reasons.some(r => r.includes("Falta Área"));
    const hasDriverNoteError = reasons.some(r => r.includes("Obs. Conductor"));
    const hasMarginError = reasons.some(r => r.includes("Margen insuficiente"));
    const hasTCError = reasons.some(r => r.includes("TC fuera de rango") || r.includes("TC sospechoso"));
    const hasZeroError = reasons.some(r => r.includes("es 0"));
    const hasEfectivoError = reasons.some(r => r.includes("Efectivo"));
    const hasWaitError = reasons.some(r => r.includes("Tiempo de espera"));
    const hasRestrictedError = reasons.some(r => r.includes("Móvil restringido"));
    const hasAnyWaitingData = hasWaitError || (parseInt(waitingTimeMinutes) || 0) > 0 || (parseFloat(waitingTimeAmount) || 0) > 0 || (parseFloat(waitingTimeProviderCost) || 0) > 0;

    const ventaBase = parseFloat(finalPrice) || 0;
    const adicionalesExtras = (trip.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
    const esperaCliente = parseFloat(waitingTimeAmount) || 0;
    const ventaTotalReal = ventaBase + adicionalesExtras + esperaCliente;

    const costoChoferTotalInput = parseFloat(driverEarnings) || 0;
    const esperaChofer = parseFloat(waitingTimeProviderCost) || 0;
    const costoTotalChofer = costoChoferTotalInput > 0 
        ? costoChoferTotalInput 
        : (parseFloat(providerCostBase) || 0) + esperaChofer;
    const costoChoferBase = Math.max(0, costoTotalChofer - esperaChofer);

    const margenReal = ventaTotalReal > 0 ? ((ventaTotalReal - costoTotalChofer) / ventaTotalReal) * 100 : 0;

    return (
        <>
        <Card className="shadow border-0 h-100" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', borderLeft: '4px solid #f59e0b' }}>
            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                <div>
                    <h5 className="mb-0 fw-bold text-dark">📋 Auditoría Reserva #{trip.id}</h5>
                    <small className="text-muted">{trip.client?.name} • {trip.passenger_name}</small>
                </div>
                <Button variant="close" onClick={onClose} aria-label="Cerrar" />
            </Card.Header>
            <Card.Body className="p-3 d-flex flex-column gap-3">
                
                {/* LISTA DE DISCREPANCIAS */}
                <div className="d-flex flex-column gap-2">
                    <span className="fw-bold text-secondary small">Discrepancias Detectadas ({reasons.length}):</span>
                    {reasons.map((r, idx) => (
                        <Alert key={idx} variant="warning" className="py-2 px-3 mb-0 small d-flex align-items-center gap-2 border-0 bg-warning bg-opacity-10 text-warning-emphasis">
                            <AlertTriangle size={16} className="flex-shrink-0" />
                            <span>{r}</span>
                        </Alert>
                    ))}
                </div>

                <hr className="my-1 text-muted opacity-25" />

                {/* CONTROLES QUIRÚRGICOS DE RESOLUCIÓN */}
                <div className="d-flex flex-column gap-3">
                    
                    {/* 1. Centro de Costo / Área */}
                    {(hasCCError || hasAreaError) && (
                        <div className="p-3 bg-light rounded border">
                            <span className="fw-bold text-dark d-block mb-2 small">🏢 Asignar Centro de Costo / Área</span>
                            <Row className="g-2">
                                {hasCCError && (
                                    <Col xs={12}>
                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Form.Label className="small text-muted mb-0">Centro de Costo</Form.Label>
                                                <Button 
                                                    variant="link" 
                                                    size="sm" 
                                                    className="p-0 text-decoration-none fw-bold" 
                                                    style={{ fontSize: '11px' }}
                                                    onClick={() => setShowCCModal(true)}
                                                >
                                                    ➕ Nuevo CC
                                                </Button>
                                            </div>
                                            <SearchableSelect
                                                options={ccOptions}
                                                value={selectedCC}
                                                onChange={val => setSelectedCC(val)}
                                                placeholder="Buscar Centro de Costo..."
                                            />
                                        </Form.Group>
                                    </Col>
                                )}
                                {hasAreaError && (
                                    <Col xs={12}>
                                        <Form.Group>
                                            <Form.Label className="small text-muted mb-1">Área</Form.Label>
                                            <SearchableSelect
                                                options={areaOptions}
                                                value={selectedArea}
                                                onChange={val => setSelectedArea(val)}
                                                placeholder="Buscar Área..."
                                            />
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}

                    {/* 2. Observación del Conductor */}
                    {hasDriverNoteError && (
                        <div className="p-3 bg-light rounded border">
                            <span className="fw-bold text-dark d-block mb-1 small">💬 Observación del Conductor</span>
                            <div className="p-2 bg-white rounded border italic mb-3 small text-secondary">
                                "{trip.driver_notes}"
                            </div>
                            
                            <div className="d-flex flex-wrap gap-2">
                                <Button size="sm" variant="success" className="d-flex align-items-center gap-1" onClick={handleQuickResolveDriverNote} disabled={isSaving}>
                                    <CheckCircle size={14} /> Marcar como Revisado
                                </Button>
                                {!hasAnyWaitingData && (
                                    <Button size="sm" variant="outline-primary" onClick={() => setShowWaitingForm(!showWaitingForm)}>
                                        {showWaitingForm ? 'Ocultar Espera' : '➕ Registrar Espera'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 2.5 Revisión de Tiempo de Espera (Oculta al chofer hasta procesar el voucher) */}
                    {(hasAnyWaitingData || showWaitingForm) && (
                        <div className="p-3 bg-warning bg-opacity-10 rounded border border-warning">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-bold text-dark small">⏱️ Revisión de Tiempo de Espera</span>
                                <Badge bg="dark" className="fw-normal" style={{ fontSize: '10px' }}>
                                    Oculta al conductor hasta Procesar
                                </Badge>
                            </div>
                            <small className="text-muted d-block mb-2" style={{ fontSize: '11px' }}>
                                Revisa o ajusta la espera antes de procesar. Si la espera está incluida por convenio, déjala en $0.
                            </small>
                            <Row className="g-2 mb-2">
                                <Col xs={4}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Minutos</Form.Label>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={waitingTimeMinutes}
                                            onChange={e => {
                                                setWaitingTimeMinutes(e.target.value);
                                                setWaitingTimeManualFix(true);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Cobro Cliente ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={waitingTimeAmount}
                                            onChange={e => {
                                                setWaitingTimeAmount(e.target.value);
                                                setWaitingTimeManualFix(true);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Pago Chofer ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={waitingTimeProviderCost}
                                            onChange={e => handleWaitingProviderCostChange(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    className="fw-bold"
                                    style={{ fontSize: '11px' }}
                                    onClick={handleMarkWaitIncluded}
                                >
                                    🚫 Espera Incluida (Dejar en $0)
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    style={{ fontSize: '11px' }}
                                    onClick={() => handleWaitingProviderCostChange(0)}
                                >
                                    Pago Chofer Espera = $0
                                </Button>
                            </div>
                            <Form.Check 
                                type="checkbox" 
                                label="Espera revisada / autorizada por Operaciones" 
                                className="small text-muted"
                                checked={waitingTimeManualFix}
                                onChange={e => setWaitingTimeManualFix(e.target.checked)}
                            />
                        </div>
                    )}

                    {/* 3. Discrepancias Financieras (Margen, TC, Valores en Cero, Efectivo) */}
                    {(hasMarginError || hasTCError || hasZeroError || hasEfectivoError || hasWaitError || hasAnyWaitingData) && (
                        <div className="p-3 bg-light rounded border">
                            <span className="fw-bold text-dark d-block mb-2 small">💵 Corregir Valores / Métodos</span>
                            
                            <Row className="g-2 mb-3">
                                <Col xs={6}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Venta Base ($)</Form.Label>
                                        <Form.Control type="number" size="sm" value={finalPrice} onChange={e => setFinalPrice(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col xs={6}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Pago Conductor Total ($)</Form.Label>
                                        <Form.Control type="number" size="sm" value={driverEarnings} onChange={e => setDriverEarnings(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label className="small text-muted mb-1">Costo Proveedor Base ($)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={providerCostBase}
                                            onChange={e => {
                                                setProviderCostBase(e.target.value);
                                                setDriverEarnings(computeDriverEarningsWithWait(e.target.value, waitingTimeProviderCost));
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                {hasEfectivoError && (
                                    <Col xs={12}>
                                        <Form.Group>
                                            <Form.Label className="small text-muted mb-1">Método de Pago</Form.Label>
                                            <Form.Select size="sm" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                                <option value="VOUCHER">VOUCHER</option>
                                                <option value="EFECTIVO">EFECTIVO</option>
                                                <option value="CREDITO">CREDITO</option>
                                                <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>

                            {/* Desglose de Venta y Margen Real */}
                            <div className="p-2 rounded bg-dark text-white mb-3 d-flex flex-column gap-1 small">
                                <div className="d-flex justify-content-between">
                                    <span>Venta Base:</span>
                                    <span>${ventaBase.toLocaleString('es-CL')}</span>
                                </div>
                                {adicionalesExtras > 0 && (
                                    <div className="d-flex justify-content-between text-info">
                                        <span>Adicionales (Desvíos/Paradas):</span>
                                        <span>+ ${adicionalesExtras.toLocaleString('es-CL')}</span>
                                    </div>
                                )}
                                {esperaCliente > 0 && (
                                    <div className="d-flex justify-content-between text-info">
                                        <span>Espera Cliente:</span>
                                        <span>+ ${esperaCliente.toLocaleString('es-CL')}</span>
                                    </div>
                                )}
                                <div className="border-top border-secondary my-1"></div>
                                <div className="d-flex justify-content-between fw-bold text-warning fs-6">
                                    <span>Venta Total Real:</span>
                                    <span>${ventaTotalReal.toLocaleString('es-CL')}</span>
                                </div>
                                <div className="d-flex justify-content-between text-white-50">
                                    <span>Pago Chofer Base:</span>
                                    <span>${costoChoferBase.toLocaleString('es-CL')}</span>
                                </div>
                                {esperaChofer > 0 && (
                                    <div className="d-flex justify-content-between text-white-50">
                                        <span>Pago Espera Chofer:</span>
                                        <span>+ ${esperaChofer.toLocaleString('es-CL')}</span>
                                    </div>
                                )}
                                <div className="d-flex justify-content-between text-white-50">
                                    <span>Costo Chofer Total:</span>
                                    <span>${costoTotalChofer.toLocaleString('es-CL')}</span>
                                </div>
                                <div className="border-top border-secondary my-1"></div>
                                <div className="d-flex justify-content-between fw-bold text-success fs-6">
                                    <span>Margen Real:</span>
                                    <span>{margenReal.toFixed(1)}%</span>
                                </div>
                            </div>
                            
                            <div className="d-flex gap-2 mb-3">
                                <Button size="sm" variant="outline-warning" className="d-flex align-items-center gap-1 text-dark" onClick={() => setShowOverrideForm(!showOverrideForm)}>
                                    <HelpCircle size={14} /> Aprobar Discrepancia Financiera
                                </Button>
                            </div>

                            {showOverrideForm && (
                                <Card className="mt-3 border-0 bg-white shadow-sm p-3">
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small text-muted mb-1 fw-bold">Escribe la Justificación:</Form.Label>
                                        <Form.Control 
                                            as="textarea" 
                                            rows={2} 
                                            size="sm" 
                                            placeholder="Ej: Aprobado bajo margen por volumen de convenio / peajes manuales..."
                                            value={auditOverrideReason}
                                            onChange={e => setAuditOverrideReason(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Button size="sm" variant="warning" className="w-100 fw-bold text-dark" onClick={handleQuickOverride} disabled={isSaving || !auditOverrideReason.trim()}>
                                        Confirmar y Forzar Procesado
                                    </Button>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* 4. Móvil Restringido */}
                    {hasRestrictedError && (
                        <div className="p-3 bg-light rounded border">
                            <span className="fw-bold text-dark d-block mb-2 small">🚗 Reasignar Conductor / Vehículo</span>
                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">Conductor</Form.Label>
                                <Form.Select size="sm" value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)}>
                                    <option value="">-- Seleccionar --</option>
                                    {drivers.map(d => (
                                        <option key={d.id} value={d.id}>{d.internal_code || 'S/I'} - {d.full_name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    )}

                </div>

            </Card.Body>
            <Card.Footer className="bg-light p-3 d-flex justify-content-between gap-2 border-top">
                <Button variant="outline-secondary" size="sm" onClick={onClose} disabled={isSaving}>
                    Cerrar
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="success" size="sm" className="d-flex align-items-center gap-1 fw-bold" onClick={() => handleSave({ admin_status: 'processed', validation_details: [], waiting_time_manual_fix: true })} disabled={isSaving}>
                        <CheckCircle size={14} /> Aprobar y Procesar
                    </Button>
                    <Button variant="primary" size="sm" className="d-flex align-items-center gap-1" onClick={() => handleSave()} disabled={isSaving}>
                        {isSaving ? <Spinner size="sm" animation="border" /> : <Save size={14} />}
                        Guardar Cambios
                    </Button>
                </div>
            </Card.Footer>
        </Card>

        {/* Modal para Crear Centro de Costo */}
        <Modal show={showCCModal} onHide={() => setShowCCModal(false)} size="sm" centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 fw-bold">Nuevo Centro de Costo</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3">
                <Form.Group className="mb-2">
                    <Form.Label className="small text-muted mb-1">Código *</Form.Label>
                    <Form.Control
                        size="sm"
                        placeholder="Ej: CC-001"
                        value={newCCCode}
                        onChange={e => setNewCCCode(e.target.value)}
                        autoFocus
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="small text-muted mb-1">Nombre</Form.Label>
                    <Form.Control
                        size="sm"
                        placeholder="Ej: Gerencia de Finanzas"
                        value={newCCName}
                        onChange={e => setNewCCName(e.target.value)}
                    />
                </Form.Group>
                <Button 
                    variant="primary" 
                    size="sm" 
                    className="w-100 fw-bold" 
                    onClick={handleQuickCreateCC}
                    disabled={!newCCCode.trim() || isCreatingCC}
                >
                    {isCreatingCC ? 'Creando...' : 'Crear Centro de Costo'}
                </Button>
            </Modal.Body>
        </Modal>
        </>
    );
};

const TripsLogView = ({ isLoaded }) => {
    const [trips, setTrips] = useState([]);
    const { user: currentUser } = useAuthStore();
    const userRole = (currentUser?.role || '').toLowerCase();
    const isLocutor = userRole === 'locutor';

    // Obtener permisos efectivos
    const userPerms = useMemo(() => {
        if (['admin', 'gestor', 'operaciones'].includes(userRole)) return null; // Acceso total
        if (currentUser?.permissions) return currentUser.permissions;
        // Locutor sin permisos configurados: defaults conservadores
        if (isLocutor) return {
            can_view_dispatch: true, can_view_control_room: true, can_view_fleet: true,
            can_view_drivers: true, can_view_insights: false, can_view_billing: false,
            can_view_settlements: false, can_view_reports: false, can_view_clients: false,
            can_view_tariffs: false, can_view_admin_trips: false, can_view_booking_sim: false,
            can_process_vouchers: false, can_create_trips: true, can_assign_vehicles: false,
        };
        return {};
    }, [currentUser, userRole, isLocutor]);

    const can = useCallback((permKey) => {
        if (userPerms === null) return true; // Admin: acceso total
        return userPerms[permKey] === true;
    }, [userPerms]);

    const [clients, setClients] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [vehicles, setVehicles] = useState([]); 
    const [contracts, setContracts] = useState([]);
    const audioContextRef = useRef(null);
    const isSavingTripRef = useRef(false);

    const playAlertSound = useCallback(() => {
        try {
            if (!audioContextRef.current) audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const ctx = audioContextRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            const now = ctx.currentTime;
            const playTone = (freq, start, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(freq, start);
                gain.gain.setValueAtTime(0.3, start);
                gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
                osc.connect(gain); gain.connect(ctx.destination);
                osc.start(start); osc.stop(start + duration);
            };
            for (let i = 0; i < 6; i++) playTone(880, now + (i * 0.15), 0.1);
        } catch (e) { console.error(e); }
    }, []);
    
    const [panicAlerts, setPanicAlerts] = useState([]);
    const [filterMode, setFilterMode] = useState('all_active'); 
    const [customStart, setCustomStart] = useState(new Date().toISOString().split('T')[0]);
    const [customEnd, setCustomEnd] = useState(new Date().toISOString().split('T')[0]);
    const [viewMode, setViewMode] = useState('active'); 
    const [showMissingFlightsOnly, setShowMissingFlightsOnly] = useState(false); 
    
    // Notification Logs Modal States
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [notificationLogs, setNotificationLogs] = useState([]);
    const [logsSearchTerm, setLogsSearchTerm] = useState('');
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [isTriggering, setIsTriggering] = useState(false);
    const [isSyncingIncidents, setIsSyncingIncidents] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [isResending, setIsResending] = useState({}); // keys: `${driverId}_${dateStr}`
    const [logsTab, setLogsTab] = useState('tomorrow'); // 'tomorrow' or 'history'
    
    const [searchTerm, setSearchTerm] = useState('');
    const [searchFile, setSearchFile] = useState('');
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [selectedClient, setSelectedClient] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]); 
    const [filterAdminStatus, setFilterAdminStatus] = useState([]); 
    const [searchSettlementId, setSearchSettlementId] = useState('');
    const [searchAquivoyId, setSearchAquivoyId] = useState('');
    const [searchOsId, setSearchOsId] = useState('');
    const [searchPassengerName, setSearchPassengerName] = useState('');
    const [filterLiquidated, setFilterLiquidated] = useState(''); 
    const [filterBillingStatus, setFilterBillingStatus] = useState('');

    const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([]);
    const [selectedDriverOrVehicle, setSelectedDriverOrVehicle] = useState([]);
    const [areas, setAreas] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [selectedCostCenters, setSelectedCostCenters] = useState([]);
    const [requesters, setRequesters] = useState([]);
    const [selectedRequesters, setSelectedRequesters] = useState([]);
    
    // Filtros por País y Zona/Base
    const [bases, setBases] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedBases, setSelectedBases] = useState([]);

    // Filtros por Proveedor
    const [providers, setProviders] = useState([]);
    const [selectedProviders, setSelectedProviders] = useState([]);

    // Filtros de Incidencias Booking
    const [showIncidentsOnly, setShowIncidentsOnly] = useState(false);
    const [filterIncidentStatus, setFilterIncidentStatus] = useState('all');

    // Estados para dLocal Go Sandbox
    // Permitido para locutoras, gestora, operaciones, admin y despacho
    const canOperateDLocal = useMemo(() => {
        const email = String(currentUser?.email || '').toLowerCase();
        const role = String(currentUser?.role || '').toLowerCase();
        return ['admin', 'gestor', 'operaciones', 'locutor', 'dispatcher'].includes(role) ||
               email.includes('alejandro') ||
               Boolean(currentUser);
    }, [currentUser]);

    // Exclusivo para reseteo manual de pruebas a estado pendiente
    const isAlejandroUser = useMemo(() => {
        const email = String(currentUser?.email || '').toLowerCase();
        const role = String(currentUser?.role || '').toLowerCase();
        return email === 'alejandro.jerez@gmail.com' || role === 'admin';
    }, [currentUser]);

    const [showDLocalModal, setShowDLocalModal] = useState(false);
    const [dlocalTrip, setDlocalTrip] = useState(null);
    const [dlocalSelectedTrips, setDlocalSelectedTrips] = useState([]);
    const [dlocalCurrency, setDlocalCurrency] = useState('CLP');
    const [dlocalLoading, setDlocalLoading] = useState(false);
    const [dlocalLinkData, setDlocalLinkData] = useState(null);
    const [dlocalCustomAmount, setDlocalCustomAmount] = useState('');
    const [showCustomAmountInput, setShowCustomAmountInput] = useState(false);
    const [dlocalSendingWhatsApp, setDlocalSendingWhatsApp] = useState(false);
    const [dlocalPhone, setDlocalPhone] = useState('');
    const [dlocalUsdRate, setDlocalUsdRate] = useState(960);
    const [exchangeRatesList, setExchangeRatesList] = useState([]);

    const refreshDLocalExchangeRate = useCallback(async () => {
        try {
            const rates = await getExchangeRates();
            if (Array.isArray(rates)) {
                setExchangeRatesList(rates);
                const usdRow = rates.find(
                    r => (r.currency_from || '').toUpperCase().trim() === 'USD' && (r.currency_to || '').toUpperCase().trim() === 'CLP'
                );
                if (usdRow && Number(usdRow.rate) > 1) {
                    setDlocalUsdRate(Number(usdRow.rate));
                }
            }
        } catch (e) {
            // Mantiene el último valor o fallback
        }
    }, []);

    useEffect(() => {
        refreshDLocalExchangeRate();
    }, [refreshDLocalExchangeRate]);

    const getLiveConversionRateToCLP = useCallback((currencyFrom) => {
        const curr = (currencyFrom || 'CLP').toUpperCase().trim();
        if (curr === 'CLP') return 1.0;
        if (Array.isArray(exchangeRatesList) && exchangeRatesList.length > 0) {
            const direct = exchangeRatesList.find(
                r => (r.currency_from || '').toUpperCase().trim() === curr && (r.currency_to || '').toUpperCase().trim() === 'CLP'
            );
            if (direct && Number(direct.rate) > 0) return Number(direct.rate);

            const inv = exchangeRatesList.find(
                r => (r.currency_from || '').toUpperCase().trim() === 'CLP' && (r.currency_to || '').toUpperCase().trim() === curr
            );
            if (inv && Number(inv.rate) > 0) return 1.0 / Number(inv.rate);

            if (curr !== 'USD') {
                const usdToCurr = exchangeRatesList.find(
                    r => (r.currency_from || '').toUpperCase().trim() === 'USD' && (r.currency_to || '').toUpperCase().trim() === curr
                );
                const usdToClp = exchangeRatesList.find(
                    r => (r.currency_from || '').toUpperCase().trim() === 'USD' && (r.currency_to || '').toUpperCase().trim() === 'CLP'
                );
                if (usdToCurr && Number(usdToCurr.rate) > 0 && usdToClp && Number(usdToClp.rate) > 0) {
                    return (1.0 / Number(usdToCurr.rate)) * Number(usdToClp.rate);
                }
            }
        }
        if (curr === 'USD') return dlocalUsdRate > 1 ? dlocalUsdRate : 960.0;
        if (curr === 'PEN') return 290.0;
        if (curr === 'ARS') return 0.65;
        return 1.0;
    }, [exchangeRatesList, dlocalUsdRate]);

    const isTripDLocalEligible = useCallback((t) => {
        if (!t) return false;
        // Permitido para cualquier viaje si el usuario tiene permisos operativos de dLocal
        return canOperateDLocal;
    }, [canOperateDLocal]);

    const handleOpenDLocalModal = (t) => {
        refreshDLocalExchangeRate();
        setDlocalTrip(t);
        setDlocalSelectedTrips([t]);
        setDlocalCurrency('CLP');
        setDlocalLinkData(null);
        setDlocalCustomAmount('');
        setShowCustomAmountInput(false);
        setDlocalPhone(t.passenger_phone || '');
        setDlocalSendingWhatsApp(false);
        setShowDLocalModal(true);
        // Solo consultar silenciosamente si tiene una orden activa pendiente
        if (!t.is_paid && t.payment_auth_code && t.payment_auth_code.startsWith('ORDER:')) {
            handleCheckDLocalStatus(t.id, true);
        }
    };

    const handleOpenBulkDLocalModal = () => {
        if (!selectedTrips.length) return;
        const targetTrips = trips.filter(t => selectedTrips.includes(t.id));
        if (!targetTrips.length) return;

        // RESTRICCIÓN ESTRICTA DE CONVENIOS:
        // No se pueden agrupar reservas de diferentes clientes / convenios
        const uniqueClientIds = new Set(targetTrips.map(t => t.client_id ?? 'particular'));
        if (uniqueClientIds.size > 1) {
            toast.error("No es posible agrupar reservas de distintos convenios para crear un link de pago. Todos los servicios seleccionados deben pertenecer a la misma empresa.");
            return;
        }

        refreshDLocalExchangeRate();
        setDlocalTrip(targetTrips[0]);
        setDlocalSelectedTrips(targetTrips);
        setDlocalCurrency('CLP');
        setDlocalLinkData(null);
        setDlocalCustomAmount('');
        setShowCustomAmountInput(false);
        const phoneWithPax = targetTrips.find(t => t.passenger_phone)?.passenger_phone || '';
        setDlocalPhone(phoneWithPax);
        setDlocalSendingWhatsApp(false);
        setShowDLocalModal(true);
    };

    const handleSendDLocalWhatsApp = async () => {
        const isMulti = dlocalSelectedTrips.length > 1;
        if (!isMulti && !dlocalTrip?.id) return;
        if (!dlocalLinkData) return;
        const paymentUrl = dlocalLinkData.checkout_url || dlocalLinkData.redirect_url;
        if (!paymentUrl) {
            toast.error("No hay un link de pago disponible para enviar.");
            return;
        }
        const targetPhone = (dlocalPhone || dlocalTrip?.passenger_phone || '').trim();
        if (!targetPhone) {
            toast.error("Por favor ingresa un número de teléfono de destino.");
            return;
        }

        const amountStr = dlocalLinkData.currency === 'USD' 
            ? `US$ ${dlocalLinkData.amount}` 
            : `$${Number(dlocalLinkData.amount).toLocaleString('es-CL')} CLP`;

        setDlocalSendingWhatsApp(true);
        try {
            const payload = {
                payment_url: paymentUrl,
                amount_str: amountStr,
                target_phone: targetPhone
            };
            if (isMulti) {
                payload.trip_ids = dlocalSelectedTrips.map(t => t.id);
            } else {
                payload.trip_id = dlocalTrip.id;
            }
            const res = await sendDLocalPaymentLinkWhatsApp(payload);
            toast.success(res.message || "¡Link de pago enviado exitosamente por WhatsApp!");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al enviar link de pago por WhatsApp.");
        } finally {
            setDlocalSendingWhatsApp(false);
        }
    };

    const handleGenerateDLocalLinkFromTable = async (providerOverride = 'dlocal') => {
        const selectedProvider = typeof providerOverride === 'string' ? providerOverride : 'dlocal';
        const isWebpay = selectedProvider.startsWith('webpay');
        const isMulti = dlocalSelectedTrips.length > 1;
        if (!isMulti && !dlocalTrip?.id) return;
        setDlocalLoading(true);
        try {
            const effectiveRate = dlocalUsdRate > 1 ? dlocalUsdRate : 925;
            const totalCLP = isMulti
                ? dlocalSelectedTrips.reduce((acc, t) => acc + Number(t.final_price || t.estimated_price || 0), 0)
                : Number(dlocalTrip?.final_price || dlocalTrip?.estimated_price || 0);
            const targetCurrency = isWebpay ? 'CLP' : dlocalCurrency;
            const defaultAmount = targetCurrency === 'USD'
                ? Number((Math.round((totalCLP / effectiveRate) * 100) / 100).toFixed(2))
                : Math.round(totalCLP);
            const parsedCustom = dlocalCustomAmount ? parseFloat(dlocalCustomAmount) : null;
            const finalAmountToSend = (parsedCustom && parsedCustom > 0) ? parsedCustom : defaultAmount;

            let res;
            if (isMulti) {
                res = await createDLocalPaymentLink({
                    trip_ids: dlocalSelectedTrips.map(t => t.id),
                    currency: targetCurrency,
                    custom_amount: finalAmountToSend,
                    provider: selectedProvider
                });
            } else {
                res = await createDLocalPaymentLink({
                    trip_id: dlocalTrip.id,
                    currency: targetCurrency,
                    custom_amount: finalAmountToSend,
                    provider: selectedProvider
                });
            }
            setDlocalLinkData(res);
            const activeOrderCode = isWebpay && res.token_ws
                ? `ORDER:${res.order_id}|TBK:${res.token_ws}`
                : `ORDER:${res.order_id}`;
            const targetIds = isMulti ? dlocalSelectedTrips.map(t => t.id) : [dlocalTrip.id];

            setDlocalTrip(prev => ({
                ...prev,
                payment_auth_code: activeOrderCode
            }));
            setDlocalSelectedTrips(prev => prev.map(item => ({
                ...item,
                payment_auth_code: activeOrderCode
            })));
            setTrips(prev => prev.map(item => targetIds.includes(item.id) ? {
                ...item,
                payment_auth_code: activeOrderCode
            } : item));
            const labelProvider = isWebpay ? 'Transbank (Webpay Plus)' : 'dLocal';
            toast.success(`Link ${labelProvider} generado: ${res.currency} ${res.amount}${isMulti ? ` (${targetIds.length} reservas)` : ''}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al generar link de pago.");
        } finally {
            setDlocalLoading(false);
        }
    };

    const handleCheckDLocalStatus = useCallback(async (tripId, silent = false) => {
        if (!tripId) return;
        if (!silent) setDlocalLoading(true);
        try {
            const res = await getDLocalPaymentStatus(tripId, silent);
            if (res.is_paid) {
                const newAdminStatus = res.admin_status || 'settled';
                const targetIds = (res.trip_ids && res.trip_ids.length > 0) ? res.trip_ids : [tripId];
                setTrips(prev => prev.map(item => targetIds.includes(item.id) ? {
                    ...item,
                    status: (item.status || '').toLowerCase() === 'draft' ? 'pending' : item.status,
                    passenger_confirmed: true,
                    is_paid: true,
                    payment_method: res.payment_method || 'DLocal',
                    payment_auth_code: res.payment_auth_code,
                    admin_status: newAdminStatus
                } : item));
                setDlocalSelectedTrips(prev => prev.map(item => targetIds.includes(item.id) ? {
                    ...item,
                    status: (item.status || '').toLowerCase() === 'draft' ? 'pending' : item.status,
                    passenger_confirmed: true,
                    is_paid: true,
                    payment_method: res.payment_method || 'DLocal',
                    payment_auth_code: res.payment_auth_code,
                    admin_status: newAdminStatus
                } : item));
                setDlocalTrip(prev => (targetIds.includes(prev?.id) ? {
                    ...prev,
                    status: (prev.status || '').toLowerCase() === 'draft' ? 'pending' : prev.status,
                    passenger_confirmed: true,
                    is_paid: true,
                    payment_method: res.payment_method || 'DLocal',
                    payment_auth_code: res.payment_auth_code,
                    admin_status: newAdminStatus
                } : prev));
                toast.success(`✅ Pago acreditado y reserva confirmada con éxito (${targetIds.length} servicio${targetIds.length > 1 ? 's' : ''}).`);
            } else {
                if (!silent) {
                    toast.info("El pago aún figura como Pendiente.");
                }
            }
        } catch (err) {
            if (!silent) {
                toast.error(err.response?.data?.detail || "Error al verificar estado.");
            }
        } finally {
            setDlocalLoading(false);
        }
    }, []);

    const handleResetDLocalFromTable = async (tripId) => {
        if (!tripId) return;
        if (!window.confirm(`¿Seguro que deseas revertir el pago de prueba del viaje #${tripId} a PENDIENTE?`)) return;
        setDlocalLoading(true);
        try {
            const res = await resetDLocalPayment(tripId);
            const revertedStatus = res?.admin_status || 'pending_review';
            setTrips(prev => prev.map(item => item.id === tripId ? {
                ...item,
                is_paid: false,
                payment_method: 'VOUCHER',
                payment_auth_code: null,
                admin_status: revertedStatus
            } : item));
            if (dlocalTrip?.id === tripId) {
                setDlocalTrip(prev => ({ ...prev, is_paid: false, payment_method: 'VOUCHER', payment_auth_code: null, admin_status: revertedStatus }));
                setDlocalLinkData(null);
            }
            toast.info("Pago revertido a PENDIENTE.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al resetear pago.");
        } finally {
            setDlocalLoading(false);
        }
    };

    // Auto-polling mientras el modal de dLocal esté abierto con una orden activa pendiente
    useEffect(() => {
        if (!showDLocalModal || !dlocalTrip?.id || dlocalTrip.is_paid) return;
        if (!dlocalTrip.payment_auth_code || !dlocalTrip.payment_auth_code.startsWith('ORDER:')) return;

        const pollInterval = setInterval(() => {
            handleCheckDLocalStatus(dlocalTrip.id, true);
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [showDLocalModal, dlocalTrip?.id, dlocalTrip?.is_paid, dlocalTrip?.payment_auth_code, handleCheckDLocalStatus]);

    const countryOptions = useMemo(() => {
        const codes = [...new Set(bases.map(b => b.country_code).filter(Boolean).map(c => c.trim().toUpperCase()))];
        return codes.map(c => ({
            id: c,
            name: COUNTRY_NAMES[c] ? `${COUNTRY_NAMES[c]} (${c})` : `País (${c})`
        }));
    }, [bases]);

    const baseOptions = useMemo(() => {
        const filtered = selectedCountries.length > 0 
            ? bases.filter(b => b.country_code && selectedCountries.includes(b.country_code.trim().toUpperCase())) 
            : bases;
        return filtered.map(b => ({
            id: b.id.toString(),
            name: b.name
        }));
    }, [bases, selectedCountries]);

    const providerOptions = useMemo(() => {
        return (providers || []).map(p => ({
            id: p.id.toString(),
            name: p.name
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [providers]);

    const driverOrVehicleOptions = useMemo(() => {
        const list = [];
        vehicles.forEach(v => {
            list.push({
                id: `vehicle_${v.internal_code}`,
                name: `Móvil ${v.internal_code}`
            });
        });
        drivers.forEach(d => {
            list.push({
                id: `driver_${d.id}`,
                name: d.full_name
            });
        });
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [drivers, vehicles]);

    const areaOptions = useMemo(() => {
        const filteredAreas = selectedClient.length > 0 
            ? areas.filter(a => selectedClient.includes(a.client_id)) 
            : areas;
        return filteredAreas
            .filter(a => a.is_active !== false)
            .map(a => ({
                id: a.id,
                name: a.name
            })).sort((a, b) => a.name.localeCompare(b.name));
    }, [areas, selectedClient]);

    const costCenterOptions = useMemo(() => {
        const filteredCCs = selectedClient.length > 0 
            ? costCenters.filter(cc => selectedClient.includes(cc.client_id)) 
            : costCenters;
        return filteredCCs
            .filter(cc => cc.is_active !== false)
            .map(cc => ({
                id: cc.id,
                name: cc.code + (cc.name ? ` - ${cc.name}` : '')
            })).sort((a, b) => a.name.localeCompare(b.name));
    }, [costCenters, selectedClient]);

    const requesterOptions = useMemo(() => {
        const filteredRequesters = selectedClient.length > 0 
            ? requesters.filter(r => selectedClient.includes(r.client_id)) 
            : requesters;
        return filteredRequesters.map(r => ({
            id: r.id,
            name: r.full_name + (r.email ? ` (${r.email})` : '')
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [requesters, selectedClient]);

    // Limpieza automática de selecciones inconsistentes al cambiar clientes
    useEffect(() => {
        if (selectedClient.length > 0) {
            setSelectedCostCenters(prev => {
                const activeCCs = costCenters.filter(cc => selectedClient.includes(cc.client_id));
                const activeCCIds = activeCCs.map(cc => cc.id);
                const nextSelected = prev.filter(ccId => activeCCIds.includes(ccId));
                if (nextSelected.length !== prev.length) {
                    return nextSelected;
                }
                return prev;
            });

            setSelectedAreas(prev => {
                const activeAreas = areas.filter(a => selectedClient.includes(a.client_id));
                const activeAreaIds = activeAreas.map(a => a.id);
                const nextSelected = prev.filter(areaId => activeAreaIds.includes(areaId));
                if (nextSelected.length !== prev.length) {
                    return nextSelected;
                }
                return prev;
            });

            setSelectedRequesters(prev => {
                const activeReqs = requesters.filter(r => selectedClient.includes(r.client_id));
                const activeReqIds = activeReqs.map(r => r.id);
                const nextSelected = prev.filter(reqId => activeReqIds.includes(reqId));
                if (nextSelected.length !== prev.length) {
                    return nextSelected;
                }
                return prev;
            });
        }
    }, [selectedClient, costCenters, areas, requesters]);

    const [importing, setImporting] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFilter, setPreviewFilter] = useState('all');
    const fileInputRef = useRef(null);



    const [appliedFilters, setAppliedFilters] = useState({
        client: [], provider: [], driver: [], status: [], adminStatus: [], settlementId: '', liquidated: '', term: '', external_reference: '', aquivoy_id: '', vehicle_code: [], area: [], costCenter: [], file: '', requester: [], passenger_name: '', origin_base_id: '', country_code: '', vehicle_type: []
    });

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25); 
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showBulkVoucherModal, setShowBulkVoucherModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentTrip, setCurrentTrip] = useState(null);
    const [selectedTrips, setSelectedTrips] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedTripLogs, setSelectedTripLogs] = useState([]);
    const [historyTripId, setHistoryTripId] = useState(null);
    const [showDriverModal, setShowDriverModal] = useState(false);
    const [assigningTrip, setAssigningTrip] = useState(null);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completingTrip, setCompletingTrip] = useState(null);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditTrip, setAuditTrip] = useState(null);
    const [showEvidenceModal, setShowEvidenceModal] = useState(false);
    const [evidenceTrip, setEvidenceTrip] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [discrepancies] = useState({});
    const [auditSidebarTrip, setAuditSidebarTrip] = useState(null);

    // ESTADO PARA PROCESAMIENTO ASÍNCRONO
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProcessMessage, setBulkProcessMessage] = useState("");

    // ESTADOS PARA ANULACIÓN DE SERVICIOS
    const [showVoidModal, setShowVoidModal] = useState(false);
    const [voidTripId, setVoidTripId] = useState(null);
    const [voidReason, setVoidReason] = useState("");
    const [isVoiding, setIsVoiding] = useState(false);

    // ESTADOS PARA ITINERARIOS DE CONDUCTORES
    const [showItineraryModal, setShowItineraryModal] = useState(false);
    const [itineraryDate, setItineraryDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    });
    const [itineraryTrips, setItineraryTrips] = useState([]);
    const [itineraryLoading, setItineraryLoading] = useState(false);

    // ESTADOS PARA WHATSAPP
    const [isSendingConfirmations, setIsSendingConfirmations] = useState(false);
    const [showWhatsAppPreview, setShowWhatsAppPreview] = useState(false);
    const [selectedRecipientKeys, setSelectedRecipientKeys] = useState([]);
    const [isSendingItinerary, setIsSendingItinerary] = useState({});

    // ESTADOS PARA ALERTAS DE FREE CANCELLATION < 24H (RECLAMOS BOOKING)
    const [unfairFreeCancellations, setUnfairFreeCancellations] = useState([]);
    const [showUnfairFreeDetails, setShowUnfairFreeDetails] = useState(true);

    const loadUnfairFreeCancellations = useCallback(async () => {
        try {
            const res = await apiClient.get('/booking/unfair-free-cancellations');
            if (Array.isArray(res.data)) {
                setUnfairFreeCancellations(res.data);
            }
        } catch (e) {
            console.debug("Alertas de Free Cancellation < 24h no disponibles temporalmente");
        }
    }, []);

    const handleResolveUnfairFreeCancellation = async (tripId, osRef) => {
        if (!window.confirm(`¿Marcar la reserva OS #${osRef} como ya reclamada/revisada en Booking.com?`)) {
            return;
        }
        try {
            try {
                await apiClient.post(`/booking/unfair-free-cancellations/${tripId}/resolve`);
            } catch (primaryErr) {
                await updateTrip(tripId, { admin_status: 'processed', validation_details: [] });
            }
            toast.success(`✅ Reserva OS #${osRef} marcada como reclamada/revisada.`);
            setUnfairFreeCancellations(prev => prev.filter(item => item.id !== tripId));
            loadTrips(false);
        } catch (e) {
            console.error("Error al resolver alerta de Free Cancellation:", e);
            toast.error("No se pudo marcar como resuelta la alerta.");
        }
    };

    const loadPanicAlerts = useCallback(async () => {
        loadUnfairFreeCancellations();
        if (viewMode !== 'active') return; 
        try {
            const res = await apiClient.get('/transport/panic-alerts/');
            if (res.data) setPanicAlerts(res.data);
        } catch (e) { 
            console.debug("Pánicos temporalmente no disponibles"); 
        }
    }, [viewMode, loadUnfairFreeCancellations]);

    // LÓGICA DE ITINERARIOS DE CONDUCTORES
    const loadItineraryTrips = useCallback(async (date) => {
        setItineraryLoading(true);
        try {
            const startStr = date + 'T00:00:00';
            const endStr = date + 'T23:59:59';
            const data = await getTrips({
                start_date: startStr,
                end_date: endStr,
                limit: 1000
            });
            setItineraryTrips(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Error al cargar itinerarios:", e);
            toast.error("Error al cargar viajes para los itinerarios.");
        } finally {
            setItineraryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (showItineraryModal) {
            loadItineraryTrips(itineraryDate);
        }
    }, [showItineraryModal, itineraryDate, loadItineraryTrips]);

    const groupedItineraries = useMemo(() => {
        const validTrips = itineraryTrips.filter(t => 
            t.driver_id && 
            !['cancelled', 'no_show', 'anulado'].includes(t.status?.toLowerCase())
        );

        const groups = {};
        validTrips.forEach(t => {
            const dId = t.driver_id;
            if (!groups[dId]) {
                groups[dId] = {
                    driver: t.driver || drivers.find(d => d.id === dId) || { id: dId, full_name: `Chofer #${dId}`, phone: '' },
                    vehicle: t.vehicle || vehicles.find(v => v.current_driver_id === dId) || { internal_code: t.driver?.vehicle_plate || '---' },
                    trips: []
                };
            }
            groups[dId].trips.push(t);
        });

        Object.keys(groups).forEach(dId => {
            groups[dId].trips.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        });

        return Object.values(groups).sort((a, b) => a.driver.full_name.localeCompare(b.driver.full_name));
    }, [itineraryTrips, drivers, vehicles]);

    const formatItineraryMessage = (group, date) => {
        const formattedDate = date.split('-').reverse().join('/');
        const driverName = group.driver.full_name;
        const vehicleCode = group.vehicle.internal_code || group.driver.vehicle_plate || '---';

        let msg = `*EMIGO - ITINERARIO DE VIAJES*\n`;
        msg += `📅 *Fecha:* ${formattedDate}\n`;
        msg += `👤 *Conductor:* ${driverName} (Móvil: ${vehicleCode})\n\n`;

        group.trips.forEach((t, index) => {
            let tz = t.origin_base?.timezone || t.timezone;
            if (!tz) {
                if (t.local_currency_code === 'PEN') tz = 'America/Lima';
                else if (t.local_currency_code === 'ARS') tz = 'America/Argentina/Mendoza';
                else if (t.local_currency_code === 'COP') tz = 'America/Bogota';
                else if (t.local_currency_code === 'BRL') tz = 'America/Sao_Paulo';
                else tz = 'America/Santiago';
            }
            
            let timeStr = '';
            try {
                timeStr = new Date(t.scheduled_time).toLocaleTimeString('es-CL', {
                    timeZone: tz,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            } catch (err) {
                timeStr = format(new Date(t.scheduled_time), 'HH:mm');
            }
            msg += `---\n`;
            msg += `*SERVICIO ${index + 1} - ${timeStr} hrs*\n`;
            msg += `🏢 *Cliente:* ${t.client?.name || '---'}\n`;
            msg += `📍 *Origen:* ${t.pickup_address}\n`;
            msg += `🏁 *Destino:* ${t.dropoff_address}\n`;

            const totalPax = 1 + (t.additional_passengers?.length || 0);
            msg += `👥 *Pasajeros (${totalPax}):*\n`;
            msg += `- ${t.passenger_name} (${t.passenger_phone || 'Sin tel.'})\n`;
            if (t.additional_passengers && t.additional_passengers.length > 0) {
                t.additional_passengers.forEach(ap => {
                    msg += `- ${ap.full_name} (${ap.phone || 'Sin tel.'})\n`;
                });
            }

            if (t.flight_number) {
                msg += `✈️ *Vuelo:* ${t.flight_number}\n`;
            }
            if (t.notes) {
                msg += `📝 *Notas:* ${t.notes}\n`;
            }
            msg += `\n`;
        });

        return msg;
    };

    const handleSendItinerary = async (group) => {
        const phoneClean = group.driver.phone ? group.driver.phone.replace(/[^0-9]/g, '') : '';
        if (!phoneClean) {
            toast.warning(`El conductor ${group.driver.full_name} no tiene un teléfono válido.`);
            return;
        }

        const nowIso = new Date().toISOString();
        const tripIds = group.trips.map(t => t.id);

        try {
            await Promise.all(tripIds.map(id => 
                updateTrip(id, { itinerary_sent_at: nowIso })
            ));
            
            setItineraryTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) {
                    return { ...t, itinerary_sent_at: nowIso };
                }
                return t;
            }));

            setTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) {
                    return { ...t, itinerary_sent_at: nowIso };
                }
                return t;
            }));

            toast.success(`Itinerario marcado como enviado para ${group.driver.full_name}`);
        } catch (e) {
            console.error("Error marcando itinerario enviado:", e);
            toast.error("Error al registrar envío en el servidor.");
        }

        const text = formatItineraryMessage(group, itineraryDate);
        const url = `https://wa.me/${phoneClean}?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    const handleSendItineraryOfficial = async (group) => {
        if (!group.driver.id) return;
        const driverId = group.driver.id;

        setIsSendingItinerary(prev => ({ ...prev, [driverId]: true }));
        try {
            await sendDriverDailySchedule(driverId, itineraryDate);
            toast.success(`Itinerario enviado oficialmente por WhatsApp a ${group.driver.full_name}`);
            
            // Registrar envío local
            const nowIso = new Date().toISOString();
            const tripIds = group.trips.map(t => t.id);
            setItineraryTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));
            setTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));
        } catch (error) {
            console.error("Error al enviar itinerario por WhatsApp API:", error);
            if (window.confirm("Fallo en la API oficial de WhatsApp. ¿Deseas abrir la ventana para enviarlo manualmente?")) {
                handleSendItinerary(group);
            }
        } finally {
            setIsSendingItinerary(prev => ({ ...prev, [driverId]: false }));
        }
    };

    const fetchPendingCount = useCallback(async () => {
        try {
            const res = await getPendingConfirmationsCount();
            if (res && typeof res.pending_count === 'number') {
                setPendingCount(res.pending_count);
            }
        } catch (e) {
            console.error("Error loading pending confirmations count", e);
        }
    }, []);

    useEffect(() => {
        fetchPendingCount();
        loadUnfairFreeCancellations();
    }, [fetchPendingCount, loadUnfairFreeCancellations]);

    const handleOpenNotificationLogs = async () => {
        setShowLogsModal(true);
        setLogsTab('tomorrow');
        setIsLoadingLogs(true);
        try {
            const data = await getDriverNotificationLogs({ limit: 1000 });
            setNotificationLogs(Array.isArray(data) ? data : []);
            fetchPendingCount();
        } catch (e) {
            console.error("Error loading notification logs", e);
        } finally {
            setIsLoadingLogs(false);
        }
    };

    const handleTriggerNotifications = async () => {
        if (!window.confirm("¿Estás seguro de que deseas enviar las notificaciones push con la agenda de mañana a todos los conductores asignados ahora?")) {
            return;
        }
        setIsTriggering(true);
        try {
            await triggerTomorrowAgendaNotifications();
            toast.success("🚀 Agenda de mañana despachada con éxito.");
            // Refrescar de inmediato la bitácora
            setIsLoadingLogs(true);
            const data = await getDriverNotificationLogs({ limit: 1000 });
            setNotificationLogs(Array.isArray(data) ? data : []);
            fetchPendingCount();
        } catch (e) {
            console.error("Error triggering notifications", e);
            toast.error("Error al despachar las notificaciones de agenda.");
        } finally {
            setIsTriggering(false);
            setIsLoadingLogs(false);
        }
    };

    const handleResendNotification = async (driverId, scheduledForDate, driverName) => {
        const key = `${driverId}_${scheduledForDate}`;
        setIsResending(prev => ({ ...prev, [key]: true }));
        try {
            await resendDriverNotification(driverId, scheduledForDate);
            toast.success(`🚀 Notificación push de agenda re-enviada con éxito a ${driverName}.`);
            // Refrescar bitácora y conteo
            const data = await getDriverNotificationLogs({ limit: 1000 });
            setNotificationLogs(Array.isArray(data) ? data : []);
            fetchPendingCount();
        } catch (e) {
            console.error("Error resending driver notification", e);
            toast.error(`Error al re-enviar la notificación a ${driverName}.`);
        } finally {
            setIsResending(prev => ({ ...prev, [key]: false }));
        }
    };

    const getFriendlyErrorMessage = (errMsg) => {
        if (!errMsg) return 'Enviada con éxito';
        const cleanMsg = errMsg.toLowerCase();
        if (cleanMsg.includes("fcm") || cleanMsg.includes("token")) {
            return "Notificación no enviada: Conductor sin sesión iniciada";
        }
        if (cleanMsg.includes("firebase") || cleanMsg.includes("despachar") || cleanMsg.includes("api")) {
            return "Notificación no enviada: Conductor no tiene aprobadas las notificaciones (permisos)";
        }
        return errMsg;
    };

    const isValidPhoneNumber = (phone) => {
        if (!phone) return false;
        let target = phone;
        for (const sep of ['/', ',', ';', ' y ', ' o ']) {
            if (target.includes(sep)) {
                target = target.split(sep)[0];
            }
        }
        const clean = target.replace(/[^\d]/g, "");
        if (clean.length < 8) return false;
        // Detectar si todos los números son iguales (números ficticios como 99999999)
        if (/^(\d)\1+$/.test(clean)) return false;
        return true;
    };

    const whatsappRecipients = useMemo(() => {
        const list = [];
        selectedTrips.forEach(id => {
            const trip = trips.find(t => t.id === id);
            if (!trip) return;
            
            // 1. Pasajero Principal
            list.push({
                tripId: trip.id,
                name: trip.passenger_name || "Pasajero Principal",
                phone: trip.passenger_phone || "",
                role: "Principal",
                isValid: isValidPhoneNumber(trip.passenger_phone),
                alreadySent: !!trip.itinerary_sent_at
            });
            
            // 2. Acompañantes
            if (trip.additional_passengers && trip.additional_passengers.length > 0) {
                trip.additional_passengers.forEach(comp => {
                    list.push({
                        tripId: trip.id,
                        name: comp.full_name || "Acompañante",
                        phone: comp.phone || "",
                        role: `Acompañante de ${trip.passenger_name || 'Principal'}`,
                        isValid: isValidPhoneNumber(comp.phone),
                        alreadySent: !!trip.itinerary_sent_at
                    });
                });
            }

            // 3. Pasajeros de paradas intermedias
            if (trip.stops && trip.stops.length > 0) {
                trip.stops.forEach((stop, sIdx) => {
                    if (stop.passenger_name && stop.passenger_name.trim() !== '') {
                        list.push({
                            tripId: trip.id,
                            name: stop.passenger_name,
                            phone: stop.passenger_phone || "",
                            role: `Pasajero Parada P${sIdx + 1}`,
                            isValid: isValidPhoneNumber(stop.passenger_phone),
                            alreadySent: !!trip.itinerary_sent_at
                        });
                    }
                });
            }
        });
        return list;
    }, [selectedTrips, trips]);

    const handleBulkSendPassengerConfirmations = () => {
        if (selectedTrips.length === 0) return;
        const defaultSelected = [];
        selectedTrips.forEach(id => {
            const trip = trips.find(t => t.id === id);
            if (!trip) return;
            // Pasajero Principal
            if (trip.passenger_phone && isValidPhoneNumber(trip.passenger_phone) && !trip.itinerary_sent_at) {
                defaultSelected.push(`${trip.id}-${trip.passenger_phone}`);
            }
            // Acompañantes
            if (trip.additional_passengers) {
                trip.additional_passengers.forEach(comp => {
                    if (comp.phone && isValidPhoneNumber(comp.phone) && !trip.itinerary_sent_at) {
                        defaultSelected.push(`${trip.id}-${comp.phone}`);
                    }
                });
            }
            // Pasajeros de paradas
            if (trip.stops) {
                trip.stops.forEach(stop => {
                    if (stop.passenger_phone && isValidPhoneNumber(stop.passenger_phone) && !trip.itinerary_sent_at) {
                        defaultSelected.push(`${trip.id}-${stop.passenger_phone}`);
                    }
                });
            }
        });
        setSelectedRecipientKeys(defaultSelected);
        setShowWhatsAppPreview(true);
    };

    const triggerSendWhatsApp = async () => {
        if (selectedRecipientKeys.length === 0) {
            toast.error("Debe seleccionar al menos un destinatario válido para realizar el envío.");
            return;
        }
        setIsSendingConfirmations(true);
        try {
            const res = await bulkSendPassengerConfirmations(selectedTrips, selectedRecipientKeys);
            toast.info(`WhatsApp Twilio: ${res.successfully_sent} confirmaciones enviadas. ${res.errors?.length || 0} advertencias/errores.`);
            if (res.errors && res.errors.length > 0) {
                console.warn("Errores de envío masivo:", res.errors);
            }
            setSelectedTrips([]);
            setSelectedRecipientKeys([]);
            setShowWhatsAppPreview(false);
            loadTrips(true);
        } catch (err) {
            console.error("Error en envío masivo de confirmaciones:", err);
            toast.error("Ocurrió un error al despachar los mensajes por WhatsApp.");
        } finally {
            setIsSendingConfirmations(false);
        }
    };

    const handleToggleRecipient = (key) => {
        setSelectedRecipientKeys(prev => {
            if (prev.includes(key)) {
                return prev.filter(k => k !== key);
            } else {
                return [...prev, key];
            }
        });
    };

    const getItineraryStatusBadge = (group) => {
        const sentTrips = group.trips.filter(t => t.itinerary_sent_at);
        if (sentTrips.length === 0) {
            return <Badge bg="secondary">⏳ No Enviado</Badge>;
        }
        if (sentTrips.length === group.trips.length) {
            const latestSent = sentTrips.reduce((max, t) => {
                const d = new Date(t.itinerary_sent_at);
                return d > max ? d : max;
            }, new Date(0));
            return (
                <Badge bg="success">
                    ✅ Enviado ({format(latestSent, 'HH:mm')})
                </Badge>
            );
        }
        return <Badge bg="warning" text="dark">⚠️ Modificado (Re-enviar)</Badge>;
    };

    const handleSyncBookingIncidents = async () => {
        try {
            setIsSyncingIncidents(true);
            const res = await apiClient.post('/booking/sync-incidents');
            toast.success(res.data?.message || "Sincronización de incidencias completada");
            loadTrips(true);
        } catch (error) {
            console.error("Error al sincronizar incidencias:", error);
            toast.error("Error al sincronizar incidencias: " + (error.response?.data?.detail || error.message));
        } finally {
            setIsSyncingIncidents(false);
        }
    };

    const handleResolvePassengerAlert = async (tripId, passengerName) => {
        if (!window.confirm(`¿Marcar la alerta de respuesta del pasajero ${passengerName || ''} como resuelta?`)) {
            return;
        }
        try {
            await apiClient.put(`/transport/trips/${tripId}/resolve-passenger-alert`);
            toast.success("Alerta de pasajero resuelta correctamente.");
            loadTrips(false);
        } catch (error) {
            console.error("Error al resolver alerta del pasajero:", error);
            toast.error("Error al resolver la alerta: " + (error.response?.data?.detail || error.message));
        }
    };

    const loadTrips = useCallback(async (showLoading = false) => { 
        if (showLoading) setIsLoading(true);
        try { 
            let startDate = null, endDate = null;
            const now = new Date();

            if (viewMode === 'history') {
                // Historial: No mostrar nada por defecto para evitar sobrecarga
                const hasDateFilter = filterMode === 'today' || filterMode === 'this_month' || (filterMode === 'custom' && customStart);
                const hasOtherFilter = appliedFilters.term || appliedFilters.file || (appliedFilters.client && appliedFilters.client.length > 0) || (appliedFilters.provider && appliedFilters.provider.length > 0) || (appliedFilters.driver && appliedFilters.driver.length > 0) || (appliedFilters.status && appliedFilters.status.length > 0) || (appliedFilters.adminStatus && appliedFilters.adminStatus.length > 0) || (appliedFilters.requester && appliedFilters.requester.length > 0) || appliedFilters.settlementId || appliedFilters.liquidated || appliedFilters.passenger_name;
                
                if (!hasDateFilter && !hasOtherFilter) {
                    setTrips([]);
                    setIsLoading(false);
                    return;
                }
            }

            const hasDateFilter = filterMode === 'today' || filterMode === 'this_month' || (filterMode === 'custom' && customStart);

            if (viewMode === 'smart') {
                startDate = null;
                endDate = null;
            } else {
                if (hasDateFilter) {
                    if (filterMode === 'today') {
                        startDate = new Date(now.setHours(0,0,0,0)).toISOString();
                        endDate = new Date(now.setHours(23,59,59,999)).toISOString();
                    } else if (filterMode === 'this_month') {
                        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();
                        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
                    } else if (filterMode === 'custom') {
                        startDate = new Date(customStart + 'T00:00:00').toISOString();
                        endDate = new Date(customEnd + 'T23:59:59').toISOString();
                    }
                } else if (appliedFilters.passenger_name && appliedFilters.passenger_name.trim() !== '') {
                    // Si se filtra por pasajero sin rango de fecha, buscar últimos 3 meses (90 días) hacia adelante
                    startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
                    endDate = null;
                } else if (viewMode === 'active') {
                    // Despacho Diario: Por defecto viajes en curso / atrasados actuales y hasta 14 días hacia adelante
                    if (filterMode === 'all_active') {
                        startDate = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString();
                        endDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
                    }
                }
            }
            const hasSpecificIdSearch = !!(appliedFilters.external_reference || appliedFilters.aquivoy_id || appliedFilters.term);
            const tripQueryParams = {
                status: hasSpecificIdSearch
                    ? null
                    : ((appliedFilters.status && appliedFilters.status.length > 0) 
                        ? appliedFilters.status.join(',') 
                        : (viewMode === 'active' 
                            ? ACTIVE_STATUSES.join(',') 
                            : (viewMode === 'smart' 
                                ? 'pending' 
                                : null))),
                start_date: hasSpecificIdSearch ? null : startDate,
                end_date: hasSpecificIdSearch ? null : endDate,
                client_id: hasSpecificIdSearch ? null : ((appliedFilters.client && appliedFilters.client.length > 0) ? appliedFilters.client.join(',') : null),
                driver_id: hasSpecificIdSearch ? null : ((appliedFilters.driver && appliedFilters.driver.length > 0) ? appliedFilters.driver.join(',') : null),
                skip: 0,
                limit: 2000,
                settlement_id: hasSpecificIdSearch ? null : (appliedFilters.settlementId || null),
                liquidated_status: hasSpecificIdSearch ? null : (appliedFilters.liquidated || null),
                billing_status: hasSpecificIdSearch ? null : (appliedFilters.billing_status || null),
                admin_status: hasSpecificIdSearch ? null : ((appliedFilters.adminStatus && appliedFilters.adminStatus.length > 0) ? appliedFilters.adminStatus.join(',') : null),
                search_term: appliedFilters.term || null,
                external_reference: appliedFilters.external_reference || null,
                aquivoy_id: appliedFilters.aquivoy_id || null,
                vehicle_code: hasSpecificIdSearch ? null : ((appliedFilters.vehicle_code && appliedFilters.vehicle_code.length > 0) ? appliedFilters.vehicle_code.join(',') : null),
                area_id: hasSpecificIdSearch ? null : ((appliedFilters.area && appliedFilters.area.length > 0) ? appliedFilters.area.join(',') : null),
                cost_center_id: hasSpecificIdSearch ? null : ((appliedFilters.costCenter && appliedFilters.costCenter.length > 0) ? appliedFilters.costCenter.join(',') : null),
                file: hasSpecificIdSearch ? null : (appliedFilters.file || null),
                requester_id: hasSpecificIdSearch ? null : ((appliedFilters.requester && appliedFilters.requester.length > 0) ? appliedFilters.requester.join(',') : null),
                passenger_name: hasSpecificIdSearch ? null : (appliedFilters.passenger_name || null),
                origin_base_id: hasSpecificIdSearch ? null : (appliedFilters.origin_base_id || null),
                country_code: hasSpecificIdSearch ? null : (appliedFilters.country_code || null),
                requested_vehicle_type_id: hasSpecificIdSearch ? null : ((appliedFilters.vehicle_type && appliedFilters.vehicle_type.length > 0) ? appliedFilters.vehicle_type.join(',') : null),
                provider_id: hasSpecificIdSearch ? null : ((appliedFilters.provider && appliedFilters.provider.length > 0) ? appliedFilters.provider.join(',') : null)
            };
            let data;
            try {
                data = await getTrips(tripQueryParams);
            } catch (firstErr) {
                if (showLoading) {
                    await new Promise(res => setTimeout(res, 800));
                    data = await getTrips(tripQueryParams);
                } else {
                    throw firstErr;
                }
            }
            setTrips(Array.isArray(data) ? data : []);
            setLastUpdated(new Date());
            if (!showLoading) loadPanicAlerts();
        } catch (err) { 
            console.error("Error cargando viajes:", err); 
            // No vaciar la tabla si es un refresco silencioso o si ya habían viajes en pantalla
            setTrips(prev => (Array.isArray(prev) && prev.length > 0) ? prev : []); 
        }
        if (showLoading) setIsLoading(false);
    }, [filterMode, customStart, customEnd, appliedFilters, loadPanicAlerts, viewMode]);

    const handleBulkImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (fileInputRef.current) fileInputRef.current.value = "";

        setAnalyzing(true);
        const toastId = toast.loading(`🔍 Analizando el archivo Excel "${file.name}"...`);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const stats = await bulkImportPreview(formData);
            toast.dismiss(toastId);
            setPreviewData(stats);
            setPreviewFile(file);
            setShowPreviewModal(true);
        } catch (e) {
            toast.dismiss(toastId);
            toast.error("Error al analizar el archivo. Revisa el formato del Excel.");
        } finally {
            setAnalyzing(false);
        }
    };

    const handleConfirmImport = async () => {
        if (!previewFile) return;
        setImporting(true);
        const formData = new FormData();
        formData.append('file', previewFile);
        try {
            const res = await bulkImportTrips(formData);
            const parts = [];
            if (res.imported > 0) parts.push(`${res.imported} creadas`);
            if (res.updated > 0) parts.push(`${res.updated} con Área/CC actualizado`);
            if (res.skipped > 0) parts.push(`${res.skipped} sin cambios`);
            toast.success(`✅ Importación completada: ${parts.join(' · ')}`);
            setShowPreviewModal(false);
            setPreviewData(null);
            setPreviewFile(null);
            loadTrips(true);
        } catch (e) {
            toast.error("Error al importar. Revisa el formato del Excel.");
        } finally {
            setImporting(false);
        }
    };





    useEffect(() => {
        const loadMasters = async () => {
            try {
                // Ola 1: Catálogos principales de despacho (independientes con allSettled)
                const [cRes, dRes, vtRes, ctRes, vRes, bsRes, provsRes] = await Promise.allSettled([
                    getClients(), 
                    getDrivers({ limit: 1000 }), 
                    getVehicleTypes(), 
                    getContracts(), 
                    getVehicles({ limit: 1000 }), 
                    getBases(),
                    getProviders()
                ]);
                if (cRes.status === 'fulfilled') setClients(cRes.value || []);
                if (dRes.status === 'fulfilled') setDrivers(dRes.value || []);
                if (vtRes.status === 'fulfilled') setVehicleTypes(vtRes.value || []);
                if (ctRes.status === 'fulfilled') setContracts(ctRes.value || []);
                if (vRes.status === 'fulfilled') setVehicles(vRes.value || []);
                if (bsRes.status === 'fulfilled') setBases(bsRes.value || []);
                if (provsRes.status === 'fulfilled') setProviders(provsRes.value || []);

                // Ola 2: Catálogos secundarios de áreas, centros de costo y solicitantes
                const [aRes, ccRes, reqsRes] = await Promise.allSettled([
                    getAreas(), 
                    getCostCenters(),
                    getRequesters({ limit: 2000 })
                ]);
                if (aRes.status === 'fulfilled') setAreas(aRes.value || []);
                if (ccRes.status === 'fulfilled') setCostCenters(ccRes.value || []);
                if (reqsRes.status === 'fulfilled') setRequesters(reqsRes.value || []);
            } catch (err) { console.error(err); }
        };
        loadMasters();
    }, []);

    const handleShowPendingReviewOnly = () => {
        // Reset local visual inputs
        setSelectedDriverOrVehicle([]);
        setFilterStatus([]);
        setSelectedClient([]);
        setSelectedProviders([]);
        setSelectedAreas([]);
        setSelectedCostCenters([]);
        setSelectedRequesters([]);
        setSelectedCountries([]);
        setSelectedBases([]);
        setSearchSettlementId('');
        setSearchOsId('');
        setSearchAquivoyId('');
        setSearchTerm('');
        setSearchFile('');
        setFilterLiquidated('');
        setFilterBillingStatus('');

        // Set pending_review locally
        setFilterAdminStatus(['pending_review']);

        // Apply filters immediately
        setSearchPassengerName('');
        setPage(1);
        setAppliedFilters({
            client: [],
            provider: [],
            driver: [],
            status: [],
            adminStatus: ['pending_review'],
            settlementId: '',
            liquidated: '',
            billing_status: '',
            term: '',
            external_reference: '',
            aquivoy_id: '',
            vehicle_code: [],
            area: [],
            costCenter: [],
            file: '',
            requester: [],
            passenger_name: '',
            origin_base_id: '',
            country_code: '',
            vehicle_type: []
        });
    };

    useEffect(() => { loadTrips(true); }, [loadTrips, viewMode]);

    useEffect(() => {
        setPage(1);
    }, [appliedFilters, filterMode, customStart, customEnd]);

    useEffect(() => {
        if (viewMode === 'active') {
            const interval = setInterval(() => loadTrips(false), 30000); 
            const handleIssueCleared = () => loadTrips(false);
            window.addEventListener('whatsapp_passenger_issue_cleared', handleIssueCleared);
            return () => {
                clearInterval(interval);
                window.removeEventListener('whatsapp_passenger_issue_cleared', handleIssueCleared);
            };
        }
    }, [viewMode, loadTrips]);

    // Detectar retorno desde checkout dLocal (?dlocal_success=12345)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const successTripId = params.get('dlocal_success');
        if (successTripId) {
            handleCheckDLocalStatus(parseInt(successTripId));
            // Limpiar el parámetro de la URL sin recargar la página
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [handleCheckDLocalStatus]);

    const handleSearchClick = () => {
        setPage(1);
        
        const driverIds = [];
        const vehicleCodes = [];
        if (Array.isArray(selectedDriverOrVehicle)) {
            selectedDriverOrVehicle.forEach(val => {
                if (val.startsWith('driver_')) {
                    driverIds.push(val.replace('driver_', ''));
                } else if (val.startsWith('vehicle_')) {
                    vehicleCodes.push(val.replace('vehicle_', ''));
                }
            });
        }

        setAppliedFilters({
            client: selectedClient, 
            provider: selectedProviders,
            driver: driverIds, 
            status: filterStatus,
            adminStatus: filterAdminStatus, 
            settlementId: searchSettlementId,
            liquidated: filterLiquidated, 
            billing_status: filterBillingStatus,
            term: searchTerm,
            external_reference: searchOsId,
            aquivoy_id: searchAquivoyId,
            vehicle_code: vehicleCodes,
            area: selectedAreas,
            costCenter: selectedCostCenters,
            file: searchFile,
            requester: selectedRequesters,
            passenger_name: searchPassengerName,
            origin_base_id: selectedBases.join(','),
            country_code: selectedCountries.join(','),
            vehicle_type: selectedVehicleTypes
        });
    };

    // EFECTO RADICAL: LIMPIAR TODO AL CAMBIAR DE PESTAÑA
    useEffect(() => {
        // Resetear inputs visuales
        setSelectedVehicleTypes([]);
        setSelectedDriverOrVehicle([]);
        setFilterStatus([]);
        setSelectedClient([]);
        setSelectedProviders([]);
        setSelectedAreas([]);
        setSelectedCostCenters([]);
        setSelectedRequesters([]);
        setSelectedCountries([]);
        setSelectedBases([]);
        setSearchSettlementId('');
        setSearchOsId('');
        setSearchAquivoyId('');
        setSearchTerm('');
        setSearchFile('');
        setSearchPassengerName('');
        setFilterAdminStatus([]);
        setFilterLiquidated('');
        setFilterBillingStatus('');

        // Resetear filtros aplicados al servidor
        setAppliedFilters({
            client: [], provider: [], driver: [], status: [], adminStatus: [], settlementId: '', 
            liquidated: '', billing_status: '', term: '', external_reference: '', aquivoy_id: '', vehicle_code: [],
            area: [], costCenter: [], file: '', requester: [], passenger_name: '',
            origin_base_id: '', country_code: '', vehicle_type: []
        });
        
        setPage(1);
    }, [viewMode]);

    const handleExportExcel = () => {
        if (!filteredTrips || filteredTrips.length === 0) {
            toast.warning("No hay datos para exportar.");
            return;
        }

        const dataRows = filteredTrips.flatMap(t => {
            let tripTimezone = t.origin_base?.timezone || t.timezone;
            if (!tripTimezone) {
                if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                else tripTimezone = 'America/Santiago';
            }

            const localTimeStr = new Intl.DateTimeFormat('es-CL', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: tripTimezone,
                hour12: false
            }).format(new Date(t.scheduled_time));

            const chileTimeStr = new Intl.DateTimeFormat('es-CL', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: 'America/Santiago',
                hour12: false
            }).format(new Date(t.scheduled_time));

            const createdChileTimeStr = t.created_at ? new Intl.DateTimeFormat('es-CL', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: 'America/Santiago',
                hour12: false
            }).format(new Date(t.created_at)) : '';

            const basePrice = Number(t.final_price || t.estimated_price || 0);
            const waitAmount = Number(t.waiting_time_amount || 0);
            const totalAmount = basePrice + waitAmount;

            const driverEarningsLocal = Number(t.driver_earnings || 0);
            const currencyCode = t.provider?.currency_code || t.driver?.provider?.currency_code || t.local_currency_code || 'CLP';

            const storedCostCLP = (t.provider_cost_clp && Number(t.provider_cost_clp) > 0)
                ? Number(t.provider_cost_clp)
                : ((t.provider_cost_base && Number(t.provider_cost_base) > 0) ? Number(t.provider_cost_base) : 0);
            const storedCostLocal = Number(t.provider_cost_local || 0);

            let rate = Number(t.exchange_rate_snapshot || 0);
            if (rate <= 1 && storedCostCLP > 0 && storedCostLocal > 0) {
                rate = storedCostCLP / storedCostLocal;
            }
            if (rate <= 1 && currencyCode !== 'CLP') {
                rate = getLiveConversionRateToCLP(currencyCode);
            }

            const driverEarningsCLP = currencyCode === 'CLP'
                ? driverEarningsLocal
                : (storedCostCLP > 0 && (storedCostLocal <= 0 || Math.abs(driverEarningsLocal - storedCostLocal) < 0.01)
                    ? Math.round(storedCostCLP)
                    : Math.round(driverEarningsLocal * rate));

            const baseRowData = {
                'ID Reserva': t.id,
                'ID de Aquivoy': t.aquivoy_id || '',
                'OS': t.external_reference || '',
                'Fecha/Hora Local': localTimeStr,
                'Fecha/Hora Chile': chileTimeStr,
                'Fecha Creación (Chile)': createdChileTimeStr,
                'Zona Horaria': tripTimezone,
                'Cliente': t.client?.name || '',
                'Pasajero Principal': t.passenger_name || '',
                'Solicitante': t.requester?.full_name || t.external_solicitante || '',
                'Total Pasajeros': 1 + (t.additional_passengers?.length || 0),
                'Pasajeros Detalle': t.passenger_name + (t.additional_passengers && t.additional_passengers.length > 0 ? ' + ' + t.additional_passengers.map(p => p.full_name).join(', ') : ''),
                'Móvil': t.vehicle?.internal_code || '',
                'Conductor': t.driver?.full_name || '',
                'Patente Conductor/Móvil': t.vehicle?.plate || t.driver?.vehicle_plate || '',
                'Teléfono Conductor': t.driver?.phone || '',
                'Tipo Vehículo': t.requested_vehicle_type?.name || '',
                'Centro de Costo Código': t.cost_center?.code || t.cost_center_label || '',
                'Centro de Costo Nombre': t.cost_center?.name || '',
                'Área': t.area?.name || t.area_label || '',
                'File / Expediente': t.file || t.client_file_number || '',
                'Nº Vuelo': t.flight_number || '',
                'Estado': STATUS_LABELS[t.status] || t.status || '',
                'Revisión': t.admin_status === 'processed' ? 'Procesado' : t.admin_status === 'rejected' ? 'Rechazado' : t.admin_status === 'settled' ? 'Pagado' : t.admin_status === 'pending_conciliation' ? 'Por Conciliar' : 'Por Revisar',
                'Moneda': currencyCode,
                'Nº Preliquidación / Liquidación': t.settlement_id || t.historical_settlement_number || '',
                'Nº Prefactura': t.client_invoice?.internal_number || t.historical_preinvoice_number || '',
                'Facturas / Documentos Tributarios': (t.client_invoice?.documents || []).map(d => `${d.type || 'FACTURA'} ${d.folio}`).join(', ') || t.historical_invoice_number || '',
                'Observaciones Operador': t.notes || '',
                'Observaciones Conductor': t.driver_notes || ''
            };

            const sortedStops = t.stops && t.stops.length > 0
                ? [...t.stops].sort((a, b) => (a.order || 0) - (b.order || 0))
                : [];

            const passengerList = [];

            // 1. Agregar las paradas intermedias de recogida
            sortedStops.forEach((stop) => {
                if (stop.address || stop.passenger_name) {
                    passengerList.push({
                        paxName: stop.passenger_name || '',
                        paxPhone: stop.passenger_phone || '',
                        pickupAddress: stop.address || t.pickup_address || '',
                        dropoffAddress: t.dropoff_address || ''
                    });
                }
            });

            // 2. Verificar si el pasajero/recogida principal está incluido o se agrega al inicio
            const isMainInStops = passengerList.some(p => 
                (p.paxName && t.passenger_name && p.paxName.trim().toLowerCase() === t.passenger_name.trim().toLowerCase()) ||
                (p.pickupAddress && t.pickup_address && p.pickupAddress.trim().toLowerCase() === t.pickup_address.trim().toLowerCase())
            );

            if (!isMainInStops && (t.passenger_name || t.pickup_address)) {
                passengerList.unshift({
                    paxName: t.passenger_name || '',
                    paxPhone: t.passenger_phone || '',
                    pickupAddress: t.pickup_address || '',
                    dropoffAddress: t.dropoff_address || ''
                });
            }

            if (passengerList.length === 0) {
                passengerList.push({
                    paxName: t.passenger_name || '',
                    paxPhone: t.passenger_phone || '',
                    pickupAddress: t.pickup_address || '',
                    dropoffAddress: t.dropoff_address || ''
                });
            }

            if (passengerList.length === 1) {
                return [{
                    ...baseRowData,
                    'Nº Parada / Tramo': 'Directo',
                    'Pasajero Tramo / Parada': passengerList[0].paxName,
                    'Teléfono Pasajero Tramo': passengerList[0].paxPhone,
                    'Origen': passengerList[0].pickupAddress,
                    'Destino': passengerList[0].dropoffAddress,
                    'Valor Servicio': basePrice,
                    'Espera (Minutos)': t.waiting_time_minutes || 0,
                    'Valor Espera': waitAmount,
                    'Monto Total': totalAmount,
                    'Pago Chofer (Moneda Local)': driverEarningsLocal,
                    'Pago Chofer (CLP)': driverEarningsCLP
                }];
            } else {
                return passengerList.map((pax, idx) => ({
                    ...baseRowData,
                    'Nº Parada / Tramo': `Pasajero ${idx + 1} (Recogida ${idx + 1})`,
                    'Pasajero Tramo / Parada': pax.paxName,
                    'Teléfono Pasajero Tramo': pax.paxPhone,
                    'Origen': pax.pickupAddress,
                    'Destino': pax.dropoffAddress,
                    'Valor Servicio': idx === 0 ? basePrice : 0,
                    'Espera (Minutos)': idx === 0 ? (t.waiting_time_minutes || 0) : 0,
                    'Valor Espera': idx === 0 ? waitAmount : 0,
                    'Monto Total': idx === 0 ? totalAmount : 0,
                    'Pago Chofer (Moneda Local)': idx === 0 ? driverEarningsLocal : 0,
                    'Pago Chofer (CLP)': idx === 0 ? driverEarningsCLP : 0
                }));
            }
        });

        const worksheet = XLSX.utils.json_to_sheet(dataRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Viajes");

        // Ajustar anchos de columnas automáticamente
        const maxLen = {};
        dataRows.forEach(row => {
            Object.keys(row).forEach(key => {
                const val = String(row[key] || '');
                maxLen[key] = Math.max(maxLen[key] || 10, val.length);
            });
        });
        worksheet['!cols'] = Object.keys(maxLen).map(key => ({ wch: Math.min(maxLen[key] + 2, 50) }));

        const dateStr = format(new Date(), 'yyyyMMdd_HHmmss');
        XLSX.writeFile(workbook, `viajes_exportados_${dateStr}.xlsx`);
        toast.success("Excel exportado correctamente.");
    };

    const handleResolvePanic = async (alertId) => {
        if (!window.confirm("¿Confirmas que la situación ha sido controlada? Se enviará una notificación de tranquilidad al conductor.")) return;
        try {
            await apiClient.put(`/transport/panic-alert/${alertId}/resolve`);
            setPanicAlerts(prev => prev.filter(a => a.id !== alertId));
            toast.success("Situación controlada. Conductor notificado.");
        } catch (e) { alert("Error al resolver."); }
    };

    const handleOpenCreate = () => { setCurrentTrip({ client_id: '', requester_id: '', passenger_name: '', passenger_count: 1, stops: [], additional_passenger_ids: [], scheduled_time: '', internal_notes: '' }); setIsEditing(false); setShowModal(true); };
    const handleEditTrip = (trip) => { setCurrentTrip(trip); setIsEditing(true); setShowModal(true); };
    
    const handleSaveAudit = (updatedTrip) => {
        // Actualizar la lista local
        setTrips(prev => prev.map(t => t.id === updatedTrip.id ? updatedTrip : t));
        
        // Seleccionar automáticamente el siguiente viaje pendiente de la lista paginada
        const currentIndex = paginatedTrips.findIndex(t => t.id === updatedTrip.id);
        if (currentIndex !== -1 && currentIndex + 1 < paginatedTrips.length) {
            setAuditSidebarTrip(paginatedTrips[currentIndex + 1]);
        } else if (currentIndex !== -1 && currentIndex - 1 >= 0) {
            setAuditSidebarTrip(paginatedTrips[currentIndex - 1]);
        } else {
            setAuditSidebarTrip(null);
        }
    };
    
    const handleSaveTrip = async (payload, keepOpen = false) => { 
        if (isSavingTripRef.current || isLoading) return;

        isSavingTripRef.current = true;
        const isActualEdit = isEditing && payload.id;
        
        if (!isActualEdit) {
            const duplicate = trips.find(t => 
                t.passenger_name?.toLowerCase() === payload.passenger_name?.toLowerCase() &&
                format(new Date(t.scheduled_time), 'yyyy-MM-dd') === format(new Date(payload.scheduled_time), 'yyyy-MM-dd') &&
                t.pickup_address?.toLowerCase() === payload.pickup_address?.toLowerCase()
            );
            if (duplicate) {
                if (!window.confirm(`⚠️ Esta solicitud ya fue creada bajo el número #${duplicate.id}. ¿Estás seguro de querer crearla?`)) {
                    isSavingTripRef.current = false;
                    return;
                }
            }
        }

        setIsLoading(true);
        try { 
            let baseRes;
            const recurrenceDates = payload.recurrenceDates;
            const cleanPayload = { ...payload };
            delete cleanPayload.recurrenceDates;

            if (isActualEdit) {
                baseRes = await updateTrip(payload.id, cleanPayload);
                setTrips(prev => prev.map(t => t.id === baseRes.id ? baseRes : t));
                toast.success("Servicio actualizado correctamente.");
            } else {
                baseRes = await createTrip(cleanPayload);
                setTrips(prev => [baseRes, ...prev]);

                if (recurrenceDates && recurrenceDates.length > 0) {
                    toast.info(`Creando ${recurrenceDates.length} servicios adicionales recurrentes...`);
                    const createdTrips = [];
                    for (const date of recurrenceDates) {
                        const recPayload = {
                            ...cleanPayload,
                            scheduled_time: date,
                        };
                        try {
                            const recRes = await createTrip(recPayload);
                            createdTrips.push(recRes);
                        } catch (err) {
                            console.error(`Error al crear viaje recurrente para la fecha ${date}:`, err);
                            toast.error(`Error al crear viaje para la fecha: ${date}`);
                        }
                    }
                    if (createdTrips.length > 0) {
                        setTrips(prev => [...createdTrips, ...prev]);
                        toast.success(`✅ Se crearon ${createdTrips.length + 1} servicios con éxito.`);
                    }
                } else {
                    toast.success(`✅ Reserva #${baseRes.id} creada con éxito.`);
                }
            }

            if (!keepOpen) {
                setShowModal(false); 
                setIsEditing(false);
                setCurrentTrip(null);
            }
        } catch (e) { 
            console.error("Error al guardar viaje:", e);
            const errorMsg = e.response?.data?.detail || e.message || "Error al guardar";
            toast.error(`❌ Error al guardar: ${errorMsg}`);
        } finally {
            isSavingTripRef.current = false;
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (trip, newStatus) => { try { const updated = await updateTrip(trip.id, { status: newStatus }); setTrips(prev => prev.map(t => t.id === updated.id ? updated : t)); } catch (err) { alert("Error al actualizar estado"); } };
    const handleAdminStatusChange = async (trip, newAdminStatus) => { 
        try { 
            const payload = newAdminStatus === 'processed' 
                ? { admin_status: 'processed', validation_details: [] } 
                : { admin_status: newAdminStatus };
            const updated = await updateTrip(trip.id, payload); 
            setTrips(prev => prev.map(t => t.id === updated.id ? updated : t)); 
            toast.success("Estado administrativo actualizado.");
        } catch (err) { 
            const msg = err.response?.data?.detail || "Error al cambiar estado administrativo.";
            alert(msg); 
        } 
    };

    const handleVoidClick = (trip) => {
        if (window.confirm('¿Está seguro de anular este servicio? Esta acción no se puede deshacer y el servicio no se cobrará ni se pagará.')) {
            setVoidTripId(trip.id);
            setVoidReason("");
            setShowVoidModal(true);
        }
    };

    const handleConfirmVoid = async () => {
        if (!voidReason.trim()) {
            toast.warning("Debe ingresar un motivo para la anulación.");
            return;
        }
        setIsVoiding(true);
        try {
            await voidTrip(voidTripId, voidReason);
            toast.success(`Servicio #${voidTripId} anulado exitosamente.`);
            setShowVoidModal(false);
            loadTrips();
        } catch (error) {
            console.error("Error al anular servicio:", error);
            const msg = error.response?.data?.detail || "Error al anular el servicio.";
            toast.error(msg);
        } finally {
            setIsVoiding(false);
        }
    };

    const handleUnvoidClick = async (trip) => {
        if (window.confirm(`¿Está seguro de revertir la anulación del servicio #${trip.id} y restaurarlo al estado anterior (Finalizado)?`)) {
            try {
                const updated = await unvoidTrip(trip.id);
                setTrips(prev => prev.map(t => t.id === updated.id ? updated : t));
                toast.success(`Servicio #${trip.id} restaurado con éxito.`);
            } catch (err) {
                console.error("Error al restaurar servicio:", err);
                const msg = err.response?.data?.detail || "Error al restaurar el servicio.";
                toast.error(msg);
            }
        }
    };
    const handleUnassign = async (trip) => { if (!window.confirm('¿Liberar?')) return; const updated = await updateTrip(trip.id, { driver_id: null, status: 'pending' }); setTrips(prev => prev.map(t => t.id === updated.id ? updated : t)); };
    const handleShowHistory = async (tripId) => { setHistoryTripId(tripId); setShowHistoryModal(true); try { const logs = await getTripLogs(tripId); setSelectedTripLogs(logs); } catch (e) { console.error(e); } };
    const handleOpenAudit = (trip) => { setAuditTrip(trip); setShowAuditModal(true); };
    const handleOpenAssignDriver = (trip) => { setAssigningTrip(trip); setShowDriverModal(true); };
    
    const handleCloneTrip = (trip) => {
        // Clonar paradas reseteando completamente su estado de ejecución
        const cleanedStops = (trip.stops || []).map(stop => ({
            ...stop,
            id: undefined,         // El backend asignará nuevos IDs
            status: 'pending',     // Resetear: no 'done' del viaje anterior
            arrived_at: null,      // Limpiar: no copiar hora de llegada antigua
            departed_at: null,     // CLAVE: evita el bug "Continúo Viaje 18:41:44"
        }));

        const cloned = { 
            ...trip, 
            id: undefined, 
            status: 'pending', 
            admin_status: 'pending_review', 
            start_time: null, 
            end_time: null, 
            external_reference: null, 
            settlement_id: null, 
            client_invoice_id: null,
            is_paid: false,
            payment_auth_code: null,
            historical_settlement_number: null,
            historical_preinvoice_number: null,
            historical_invoice_number: null,
            validation_folio: null,
            validation_details: null,
            driver_notes_reviewed: false,
            audit_override: false,
            audit_override_reason: null,
            passenger_confirmed: false,
            confirmation_received_at: null,
            passenger_issue: false,
            first_movement_at: null,
            itinerary_sent_at: null,
            last_board_alert_sent_at: null,
            aquivoy_id: null,
            driver_id: null, 
            vehicle_id: null, 
            driver_notes: '',
            // Limpiar coordenadas e hitos GPS del viaje anterior
            gps_en_route_lat: null,
            gps_en_route_lng: null,
            gps_en_route_time: null,
            gps_arrived_lat: null,
            gps_arrived_lng: null,
            gps_arrived_time: null,
            gps_on_board_lat: null,
            gps_on_board_lng: null,
            gps_completed_lat: null,
            gps_completed_lng: null,
            // Limpiar tiempos de espera y ganancias asociadas
            waiting_time_minutes: 0,
            waiting_time_amount: 0.0,
            waiting_time_provider_cost: 0.0,
            waiting_time_manual_fix: false,
            driver_earnings: null,
            final_price: null,
            positions: [],
            panic_alerts: [],
            evidence: [],
            last_notified_at: null,
            local_currency_code: null,
            provider_cost_local: null,
            provider_cost_usd: null,
            provider_cost_clp: null,
            exchange_rate_snapshot: null,
            // Paradas limpias (status=pending, sin tiempos de ejecución)
            stops: cleanedStops,
        };
        setCurrentTrip(cloned); setIsEditing(false); setShowModal(true);
    };

    const handleDriverSelected = async (driverId, auditOverride = false) => {
        if (!assigningTrip) return;
        try {
            const payload = { driver_id: driverId, status: 'assigned' };
            if (auditOverride) {
                payload.audit_override = true;
                payload.force_driver_assignment = true;
            }
            const updated = await updateTrip(assigningTrip.id, payload);
            setTrips(prev => prev.map(t => t.id === updated.id ? updated : t));
            setShowDriverModal(false);
            setAssigningTrip(null);
        } catch (e) {
            const errorDetail = e.response?.data?.detail;
            if (errorDetail && typeof errorDetail === 'string' && errorDetail.startsWith('MARGIN_WARNING')) {
                const [, provName, cost, sale, sugProv] = errorDetail.split('|');
                const formatNum = (val) => Number(val || 0).toLocaleString('es-CL');
                const msg = `⚠️ Pérdida Detectada:\nEl costo de ${provName} ($${formatNum(cost)}) supera el valor de venta ($${formatNum(sale)}).\n\nProveedor recomendado con tarifa óptima: ${sugProv || 'Ninguno'}\n\n¿Deseas asignar a este conductor de todas formas?`;
                if (window.confirm(msg)) {
                    handleDriverSelected(driverId, true);
                }
            } else {
                const msg = e.response?.data?.detail || "Error al asignar conductor";
                alert(msg);
            }
        }
    };
    const handleOpenComplete = (trip) => { setCompletingTrip(trip); setShowCompleteModal(true); };
    const handleCompleteTrip = async (tripId, notes) => { try { const updated = await updateTrip(tripId, { status: 'completed', driver_notes: notes, end_time: new Date().toISOString() }); setTrips(prev => prev.map(t => t.id === updated.id ? updated : t)); setShowCompleteModal(false); setCompletingTrip(null); } catch (e) { alert("Error finalizar"); } };
    const handleSelectAll = (e) => setSelectedTrips(e.target.checked ? trips.map(t => t.id) : []);
    const handleSelectTrip = (id) => setSelectedTrips(prev => prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]);

    // --- NUEVO PROCESAMIENTO MASIVO ASÍNCRONO ---
    const handleBulkProcess = async (newAdminStatus = 'processed') => { 
        if (newAdminStatus !== 'processed') {
            // Reversión simple (sincrona)
            if (!window.confirm(`¿Revertir estado de ${selectedTrips.length} servicios?`)) return;
            try { 
                await Promise.all(selectedTrips.map(id => updateTrip(id, { admin_status: 'pending_review' }))); 
                toast.success("Servicios revertidos.");
                loadTrips(true); setSelectedTrips([]); 
            } catch (err) { 
                const msg = err.response?.data?.detail || "Error al revertir";
                alert(msg); 
            }
            return;
        }

        if (!window.confirm(`¿Procesar ${selectedTrips.length} servicios?`)) return; 
        
        setIsBulkProcessing(true);
        setBulkProcessMessage(`Procesando ${selectedTrips.length} servicios...`);

        try { 
            // Forzamos el estado a 'processed' directamente y limpiamos validation_details
            await Promise.all(selectedTrips.map(id => updateTrip(id, { admin_status: 'processed', validation_details: [] })));
            
            setIsBulkProcessing(false);
            loadTrips(true); 
            setSelectedTrips([]); 
            toast.success("Servicios procesados correctamente.");

        } catch (err) { 
            setIsBulkProcessing(false);
            const msg = err.response?.data?.detail || "Error al procesar.";
            alert(msg);
        }
    };

    const getClientName = (id) => clients.find(cl => cl.id === id)?.name || 'Desc.';
    const getDriverName = (id) => drivers.find(dr => dr.id === id)?.full_name || '-';
    
    const getVehicleCode = (trip) => { 
        if (trip.vehicle?.internal_code) return trip.vehicle.internal_code; 
        if (trip.driver_id) {
            const v = vehicles.find(veh => veh.current_driver_id === trip.driver_id && veh.is_active);
            if (v) return v.internal_code;
        }
        return trip.driver?.vehicle_plate || '---'; 
    };

    const nearbyDriverConflictsByTripId = useMemo(() => {
        const conflictMap = new Map();
        if (!Array.isArray(trips) || trips.length < 2) return conflictMap;

        const haversineKm = (lat1, lon1, lat2, lon2) => {
            if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
            const R = 6371.0;
            const toRad = (v) => (v * Math.PI) / 180;
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };

        const calcEffectiveDuration = (tr) => {
            const rawDur = Number(tr.estimated_duration_minutes) || 0;
            const distKm = Number(tr.distance_km) || 0;
            const distFloor = distKm > 0 ? Math.round(distKm * 1.5) : 0;
            let baseDur = Math.max(30, rawDur, distFloor);
            const addr = (tr.pickup_address || '').toLowerCase();
            if (tr.flight_number || addr.includes('aeropuerto') || addr.includes('airport') || addr.includes('terminal aereo')) {
                baseDur += 15;
            }
            return baseDur;
        };

        const calcTransitMins = (lat1, lng1, lat2, lng2) => {
            const straightKm = haversineKm(lat1, lng1, lat2, lng2);
            if (straightKm === null) return 20;
            const streetKm = straightKm * 1.35;
            return Math.max(10, Math.round(streetKm * 1.5));
        };

        const formatTripTime = (tr) => {
            try {
                let tz = tr.origin_base?.timezone || tr.timezone;
                if (!tz) {
                    if (tr.local_currency_code === 'PEN') tz = 'America/Lima';
                    else if (tr.local_currency_code === 'ARS') tz = 'America/Argentina/Mendoza';
                    else if (tr.local_currency_code === 'EUR') tz = 'Europe/Madrid';
                    else if (tr.local_currency_code === 'UYU') tz = 'America/Montevideo';
                    else if (tr.pickup_lat < 0 && tr.pickup_lat > -18 && tr.pickup_lng < -68 && tr.pickup_lng > -82) tz = 'America/Lima';
                    else if (tr.pickup_lat >= -35.5 && tr.pickup_lat <= -30.0 && tr.pickup_lng >= -58.5 && tr.pickup_lng <= -53.0) tz = 'America/Montevideo';
                    else if (tr.pickup_lat >= 36.0 && tr.pickup_lat <= 44.0 && tr.pickup_lng >= -9.5 && tr.pickup_lng <= 4.5) tz = 'Europe/Madrid';
                    else tz = 'America/Santiago';
                }
                return new Intl.DateTimeFormat('es-CL', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: tz,
                    hour12: false
                }).format(new Date(tr.scheduled_time));
            } catch (e) {
                return format(new Date(tr.scheduled_time), 'HH:mm');
            }
        };

        const ignoredStatuses = new Set(['cancelled', 'anulado', 'no_show', 'cancelled_paid', 'draft']);
        const byDriver = new Map();

        for (const t of trips) {
            if (!t.driver_id || !t.scheduled_time) continue;
            const st = (t.status || '').toLowerCase();
            if (ignoredStatuses.has(st)) continue;
            const ts = new Date(t.scheduled_time).getTime();
            if (Number.isNaN(ts)) continue;
            if (!byDriver.has(t.driver_id)) byDriver.set(t.driver_id, []);
            byDriver.get(t.driver_id).push({ trip: t, ts, statusLower: st });
        }

        const SAFETY_BUFFER_MINS = 20;

        for (const list of byDriver.values()) {
            if (list.length < 2) continue;
            list.sort((a, b) => a.ts - b.ts);
            for (let i = 0; i < list.length; i++) {
                for (let j = i + 1; j < list.length; j++) {
                    const first = list[i];
                    const second = list[j];
                    const diffMins = Math.round((second.ts - first.ts) / 60000);
                    if (diffMins > 360) break;

                    const firstDur = calcEffectiveDuration(first.trip);
                    const transit = calcTransitMins(
                        first.trip.dropoff_lat ?? first.trip.destination_lat,
                        first.trip.dropoff_lng ?? first.trip.destination_lng,
                        second.trip.pickup_lat,
                        second.trip.pickup_lng
                    );
                    const requiredGap = firstDur + transit + SAFETY_BUFFER_MINS;

                    if (diffMins < requiredGap) {
                        const addConflict = (targetId, otherItem) => {
                            if (!conflictMap.has(targetId)) conflictMap.set(targetId, []);
                            conflictMap.get(targetId).push({
                                id: otherItem.trip.id,
                                timeStr: formatTripTime(otherItem.trip),
                                passenger: otherItem.trip.passenger_name || 'Pasajero',
                                diffMins,
                                requiredGap
                            });
                        };
                        addConflict(first.trip.id, second);
                        addConflict(second.trip.id, first);
                    }
                }
            }
        }

        return conflictMap;
    }, [trips]);
    
    const getAdminStatusBadge = (s, isPaid = false) => {
        if (s === 'processed') {
            return (
                <span className="d-inline-flex align-items-center gap-1">
                    <Badge bg="success">Procesado</Badge>
                    {isPaid && <Badge bg="info" className="text-dark bg-opacity-25 border border-info">Pagado</Badge>}
                </span>
            );
        }
        if (s === 'rejected') return <Badge bg="danger">Rechazado</Badge>;
        if (s === 'settled') return <Badge bg="info" className="text-dark bg-opacity-25 border border-info">Pagado</Badge>;
        if (s === 'pending_conciliation') return <Badge bg="secondary" className="bg-opacity-25 border border-secondary text-secondary">Por Conciliar</Badge>;
        if (isPaid) {
            return (
                <span className="d-inline-flex align-items-center gap-1">
                    <Badge bg="warning" text="dark">Por Revisar</Badge>
                    <Badge bg="info" className="text-dark bg-opacity-25 border border-info">Pagado</Badge>
                </span>
            );
        }
        return <Badge bg="warning" text="dark">Por Revisar</Badge>;
    };

const isAirport = (address) => {
    if (!address || typeof address !== 'string') return false;
    const lower = address.toLowerCase();
    if (lower.includes('aeropuerto') || lower.includes('airport') || lower.includes('carriel sur') || lower.includes('el tepual') || lower.includes('chacalluta') || lower.includes('el plumerillo') || lower.includes('ezeiza') || lower.includes('pudahuel')) {
        return true;
    }
    const iataCodes = ['scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'pmc', 'zco', 'ari', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim', 'bog', 'cpo', 'araucania'];
    const iataRegex = new RegExp(`\\b(${iataCodes.join('|')})\\b`, 'i');
    return iataRegex.test(address);
};

const isFlightNumberMissing = (flightNum) => {
    if (!flightNum) return true;
    const cleaned = flightNum.toString().trim().toLowerCase();
    return cleaned === "" || cleaned === "0" || cleaned === "00" || cleaned.includes("pendiente");
};

    const criticalAirportTripsWithoutFlight = useMemo(() => {
        const now = new Date();
        return trips.filter(t => {
            const statusLower = t.status?.toLowerCase();
            const isPreService = ['pending', 'assigned', 'confirmed'].includes(statusLower);
            if (!isPreService) return false;

            const scheduledTime = new Date(t.scheduled_time);
            const minutesLeft = (scheduledTime - now) / 60000;
            const isWithin24Hours = minutesLeft <= 1440; 
            if (!isWithin24Hours) return false;

            if (!isAirport(t.pickup_address)) return false;

            return isFlightNumberMissing(t.flight_number);
        });
    }, [trips]);

const filteredTrips = useMemo(() => {
        const now = new Date();
        const processed = trips.filter(t => {
            const statusLower = t.status?.toLowerCase();
            const isActiveTab = viewMode === 'active';
            if (isActiveTab && !ACTIVE_STATUSES.includes(statusLower)) return false;
            if (!isActiveTab && !HISTORY_STATUSES.includes(statusLower)) return false;

            // Filtro de vuelos faltantes para la pestaña activa
            if (showMissingFlightsOnly && isActiveTab) {
                const isPreService = ['pending', 'assigned', 'confirmed'].includes(statusLower);
                if (!isPreService) return false;

                const scheduledTime = new Date(t.scheduled_time);
                const minutesLeft = (scheduledTime - now) / 60000;
                const isWithin24Hours = minutesLeft <= 1440;
                if (!isWithin24Hours) return false;

                if (!isAirport(t.pickup_address)) return false;

                if (!isFlightNumberMissing(t.flight_number)) return false;
            }

            // Filtro de solo incidencias Booking
            if (showIncidentsOnly) {
                if (!t.booking_incident_status) return false;
            }

            // Filtro por estado específico de incidencia
            if (filterIncidentStatus && filterIncidentStatus !== 'all') {
                if (t.booking_incident_status !== filterIncidentStatus) return false;
            }

            // Si se filtró por proveedor, evitar mostrar viajes ya asignados a móviles/choferes que NO pertenecen al proveedor seleccionado
            if (appliedFilters.provider && appliedFilters.provider.length > 0) {
                const selectedProvIds = appliedFilters.provider.map(p => Number(p));
                const tripProvId = t.provider_id ? Number(t.provider_id) : null;
                const vehProvId = t.vehicle?.provider_id ? Number(t.vehicle.provider_id) : null;
                const drvProvId = t.driver?.provider_id ? Number(t.driver.provider_id) : null;
                const suggProvId = t.suggested_provider_id ? Number(t.suggested_provider_id) : null;

                const hasAssignedUnit = Boolean(t.vehicle_id || t.driver_id);
                if (hasAssignedUnit) {
                    const effectiveProvIds = [tripProvId, vehProvId, drvProvId].filter(Boolean);
                    const matchesAssignedProv = effectiveProvIds.some(pid => selectedProvIds.includes(pid));
                    if (!matchesAssignedProv) return false;
                } else {
                    const matchesUnassignedProv = (tripProvId && selectedProvIds.includes(tripProvId)) || (suggProvId && selectedProvIds.includes(suggProvId));
                    if (!matchesUnassignedProv) return false;
                }
            }

            return true;
        }).map(t => {
            const scheduledTime = new Date(t.scheduled_time);
            const minutesLeft = (scheduledTime - now) / 60000;
            const threshold = t.client?.lead_time_alert || 60;
            const statusLower = t.status?.toLowerCase();

            const isWithin48Hours = minutesLeft <= 2880; // 48 horas en minutos

            const isDelayed = ['pending', 'assigned', 'confirmed'].includes(statusLower) && scheduledTime < now;
            const isProactiveAlert = ['pending', 'assigned', 'confirmed'].includes(statusLower) && minutesLeft <= threshold;

            // Solo crítico o incompleto si está dentro de las próximas 48 horas (o pasado)
            const isCritical = isWithin48Hours && (isDelayed || isProactiveAlert);
            const isIncomplete = isWithin48Hours && (!t.driver_id || ['pending', 'assigned'].includes(statusLower));
            
            return { ...t, isDelayed, isCritical, isIncomplete };
        });

        if (viewMode === 'history') {
            return processed.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        }

        return processed.sort((a, b) => {
            if (a.isCritical && !b.isCritical) return -1;
            if (!a.isCritical && b.isCritical) return 1;
            if (a.isIncomplete && !b.isIncomplete) return -1;
            if (!a.isIncomplete && b.isIncomplete) return 1;
            return new Date(a.scheduled_time) - new Date(b.scheduled_time);
        });
    }, [trips, viewMode, showMissingFlightsOnly, showIncidentsOnly, filterIncidentStatus, appliedFilters.provider]);

    useEffect(() => {
        if (filteredTrips.some(t => t.isCritical) && viewMode === 'active') {
            playAlertSound();
        }
    }, [filteredTrips, viewMode, playAlertSound]);

    const showProcessBtn = useMemo(() => selectedTrips.length > 0 && selectedTrips.some(id => trips.find(t => t.id === id)?.admin_status !== 'processed'), [selectedTrips, trips]);
    const showRevertBtn = useMemo(() => selectedTrips.length > 0 && selectedTrips.some(id => trips.find(t => t.id === id)?.admin_status === 'processed'), [selectedTrips, trips]);

    const summaryStats = useMemo(() => {
        const count = filteredTrips.length;
        const total = filteredTrips.reduce((sum, t) => {
            const base = parseFloat(t.final_price) || parseFloat(t.estimated_price) || 0;
            const wait = parseFloat(t.waiting_time_amount) || 0;
            const extras = (t.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
            return sum + base + wait + extras;
        }, 0);
        return { count, total };
    }, [filteredTrips]);

    const paginatedTrips = useMemo(() => {
        const skip = (page - 1) * pageSize;
        return filteredTrips.slice(skip, skip + pageSize);
    }, [filteredTrips, page, pageSize]);

    const statusOptions = useMemo(() => {
        const keys = viewMode === 'active' ? ACTIVE_STATUSES : HISTORY_STATUSES;
        return keys.map(k => ({
            id: k,
            name: STATUS_LABELS[k] || k
        }));
    }, [viewMode]);

    const adminStatusOptions = [
        { id: 'pending_review', name: 'Por Revisar' },
        { id: 'processed', name: 'Procesado' },
        { id: 'rejected', name: 'Rechazado' },
        { id: 'settled', name: 'Pagado' },
        { id: 'pending_conciliation', name: 'Por Conciliar' }
    ];



    return (
        <div className="p-3">
            <style>
                {`
                @keyframes pulse-red {
                    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
                    70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
                    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                }
                .pulse-animation {
                    animation: pulse-red 2s infinite;
                    box-shadow: 0 0 0 0 rgba(220, 53, 69, 1);
                }
                `}
            </style>
            {panicAlerts.length > 0 && (
                <div className="mb-3">
                    {panicAlerts.map(alert => (
                        <Alert key={alert.id} variant="danger" className="shadow border-4 border-danger d-flex justify-content-between align-items-center animate-pulse py-2">
                            <div className="d-flex align-items-center gap-3">
                                <AlertTriangle size={28} className="text-danger" />
                                <div>
                                    <strong className="d-block">🚨 ALERTA DE PÁNICO ACTIVA</strong>
                                    <small>Conductor: {alert.driver?.full_name || alert.user_email}</small>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {alert.driver?.phone && (
                                    <>
                                        <Button variant="success" size="sm" onClick={() => window.open(`https://wa.me/${alert.driver.phone.replace(/[^0-9]/g, '')}`, '_blank')}>
                                            <MessageCircle size={14} className="me-1" /> WHATSAPP
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => window.open(`tel:${alert.driver.phone}`, '_blank')}>
                                            <Phone size={14} className="me-1" /> LLAMAR
                                        </Button>
                                    </>
                                )}
                                <Button variant="outline-dark" size="sm" onClick={() => handleResolvePanic(alert.id)}>
                                    <CheckCircle size={14} className="me-1" /> CONTROLADO
                                </Button>
                            </div>
                        </Alert>
                    ))}
                </div>
            )}

            {unfairFreeCancellations.length > 0 && (
                <Alert
                    variant="danger"
                    className="shadow-sm mb-3 border-start border-4 border-danger p-3"
                    style={{ borderRadius: '12px', backgroundColor: '#fff5f5', borderColor: '#feb2b2' }}
                >
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <span style={{ fontSize: '1.7rem' }}>🚨</span>
                            <div>
                                <h6 className="mb-0 fw-bold text-danger">
                                    Ojo: Cancelaron &ldquo;Free Cancellation&rdquo; y la reserva era en menos de 24 horas ({unfairFreeCancellations.length})
                                </h6>
                                <small className="text-muted">
                                    Booking.com envió cancelación gratuita fuera del plazo contractual de 24 hrs. Revisa el historial/WhatsApp para presentar el reclamo en Booking.
                                </small>
                            </div>
                        </div>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="fw-bold"
                            onClick={() => setShowUnfairFreeDetails(!showUnfairFreeDetails)}
                        >
                            {showUnfairFreeDetails ? 'Ocultar Detalle' : `Ver Reservas (${unfairFreeCancellations.length})`}
                        </Button>
                    </div>

                    {showUnfairFreeDetails && (
                        <div className="mt-3 d-flex flex-column gap-2">
                            {unfairFreeCancellations.map(item => (
                                <div
                                    key={item.id}
                                    className="bg-white rounded border border-danger border-opacity-25 p-2 d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-xs"
                                >
                                    <div className="small">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <Badge bg="danger">OS: {item.external_reference}</Badge>
                                            {item.customer_reference && (
                                                <Badge bg="secondary">CustomerRef: {item.customer_reference}</Badge>
                                            )}
                                            <strong className="text-dark">👤 {item.passenger_name} ({item.passenger_count} pax)</strong>
                                            {item.origin_base_name && (
                                                <span className="text-muted fw-semibold">· 📍 {item.origin_base_name}</span>
                                            )}
                                            {item.provider_name && (
                                                <span className="text-muted">({item.provider_name})</span>
                                            )}
                                            {item.scheduled_time && (
                                                <span className="text-primary fw-bold">
                                                    · 🕒 Servicio: {format(new Date(item.scheduled_time), 'dd/MM/yyyy HH:mm')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.82rem' }}>
                                            {item.alert_message}
                                        </div>
                                        <div className="text-muted mt-0.5" style={{ fontSize: '0.75rem' }}>
                                            Ruta: {item.pickup_address} ➔ {item.dropoff_address}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="fw-bold"
                                            onClick={() => handleShowHistory(item.id)}
                                            title="Ver bitácora completa de horas y mensajes de WhatsApp para el reclamo"
                                        >
                                            📜 Ver Logs / Pruebas
                                        </Button>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            className="fw-bold"
                                            onClick={() => handleResolveUnfairFreeCancellation(item.id, item.external_reference)}
                                            title="Marcar como ya reclamada en Booking.com"
                                        >
                                            ✅ Ya Reclamado
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Alert>
            )}

            {['admin', 'gestor', 'operaciones', 'locutor'].includes(userRole) && (
                <EquityRadarWidget baseId={1} />
            )}

            <Row className="mb-3">
                <Col md={12}>
                    <div className="d-flex gap-3 justify-content-end align-items-center">
                        <div className="bg-white border rounded shadow-sm px-4 py-2 d-flex flex-column align-items-end">
                            <span className="text-muted fw-bold mb-0" style={{fontSize:'10px', letterSpacing:'1px'}}>SERVICIOS FILTRADOS</span>
                            <h4 className="mb-0 fw-black text-primary">{summaryStats.count}</h4>
                        </div>
                        <div className="bg-dark text-white rounded shadow-sm px-4 py-2 d-flex flex-column align-items-end border-start border-4 border-success">
                            <span className="text-success fw-bold mb-0" style={{fontSize:'10px', letterSpacing:'1px'}}>TOTAL VENTA</span>
                            <h4 className="mb-0 fw-black text-success">${Math.round(summaryStats.total).toLocaleString()}</h4>
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="d-flex justify-content-between mb-2 flex-wrap gap-2 align-items-end">
                <div>
                    <h3>Despacho Diario</h3>
                    <ButtonGroup size="sm">
                        <Button variant={filterMode === 'today' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('today')}>Hoy</Button>
                        <Button variant={filterMode === 'this_month' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('this_month')}>Mes</Button>
                        <Button variant={filterMode === 'custom' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('custom')}>📅</Button>
                    </ButtonGroup>
                    {filterMode === 'custom' && <div className="d-inline-flex gap-1 ms-2 align-middle"><Form.Control type="date" size="sm" value={customStart} onChange={e => setCustomStart(e.target.value)}/><Form.Control type="date" size="sm" value={customEnd} onChange={e => setCustomEnd(e.target.value)}/></div>}
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="py-1 px-2 d-flex align-items-center justify-content-center" 
                        style={{ height: '30px', fontSize: '12px' }}
                        onClick={() => loadTrips(true)}
                        title="Recargar Viajes"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} className="me-1" />
                        ) : (
                            "🔄"
                        )}
                        <span className="ms-1 fw-bold">Recargar</span>
                    </Button>
                    <span className="text-muted me-2" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                        Act: {format(lastUpdated, 'HH:mm:ss')}
                    </span>

                    {viewMode === 'history' && (
                        <Button variant="outline-warning" size="sm" onClick={handleShowPendingReviewOnly} className="fw-bold">
                            🔍 Ver Pendientes de Procesar
                        </Button>
                    )}
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleBulkImport} 
                        accept=".xlsx, .xls" 
                        style={{ display: 'none' }} 
                    />
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="fw-bold" 
                        onClick={downloadBulkImportTemplate}
                        title="Descargar plantilla limpia de Excel"
                    >
                        📥 Plantilla
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        className="fw-bold" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing || analyzing}
                    >
                        <Upload size={16} className="me-1" />
                        {analyzing ? '🔍 Analizando...' : importing ? 'Cargando...' : 'Cargar Reservas'}
                    </Button>
                    <Button 
                        variant="outline-info" 
                        size="sm" 
                        className="fw-bold text-dark d-flex align-items-center gap-1"
                        onClick={handleOpenNotificationLogs}
                    >
                        🔔 Bitácora Notificaciones
                        {pendingCount > 0 && (
                            <Badge bg="danger" pill style={{ fontSize: '10px' }}>
                                {pendingCount}
                            </Badge>
                        )}
                    </Button>
                    <Button variant="success" size="sm" onClick={handleOpenCreate}>+ Nuevo Viaje</Button>

                </div>
            </div>

            <Card className="mb-3 bg-light border-0 shadow-sm">
                <Card.Body className="py-2 px-3">
                    <Row className="g-2 align-items-center">
                        <Col md="auto" className="d-flex align-items-center gap-2">
                            <Nav variant="pills" activeKey={viewMode} onSelect={(key) => { setViewMode(key); setShowMissingFlightsOnly(false); }} className="small me-2">
                                <Nav.Item><Nav.Link eventKey="active" className="py-1 px-3">Locución</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="smart" className="py-1 px-3 fw-bold">🧠 Asignación</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="history" className="py-1 px-3">Historial</Nav.Link></Nav.Item>
                            </Nav>
                        </Col>
                        <Col>
                            {viewMode !== 'smart' ? (
                                <div className="d-flex gap-2 flex-wrap align-items-center">
                                    <div style={{width:'180px'}}>
                                        <SearchableMultiSelect 
                                            options={clients} 
                                            selected={selectedClient} 
                                            onChange={setSelectedClient} 
                                            placeholder="Clientes..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'180px'}}>
                                        <SearchableMultiSelect 
                                            options={providerOptions} 
                                            selected={selectedProviders} 
                                            onChange={setSelectedProviders} 
                                            placeholder="🏢 Proveedores..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'240px'}}>
                                        <SearchableMultiSelect 
                                            options={driverOrVehicleOptions} 
                                            selected={selectedDriverOrVehicle} 
                                            onChange={setSelectedDriverOrVehicle} 
                                            placeholder="Móvil o Conductor..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'160px'}}>
                                        <SearchableMultiSelect 
                                            options={statusOptions} 
                                            selected={filterStatus} 
                                            onChange={setFilterStatus} 
                                            placeholder="Estados..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'160px'}}>
                                        <SearchableMultiSelect 
                                            options={areaOptions} 
                                            selected={selectedAreas} 
                                            onChange={setSelectedAreas} 
                                            placeholder="Áreas..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'180px'}}>
                                        <SearchableMultiSelect 
                                            options={costCenterOptions} 
                                            selected={selectedCostCenters} 
                                            onChange={setSelectedCostCenters} 
                                            placeholder="Centros de Costo..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'180px'}}>
                                        <SearchableMultiSelect 
                                            options={requesterOptions} 
                                            selected={selectedRequesters} 
                                            onChange={setSelectedRequesters} 
                                            placeholder="Solicitantes..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'130px'}}>
                                        <SearchableMultiSelect 
                                            options={countryOptions} 
                                            selected={selectedCountries} 
                                            onChange={setSelectedCountries} 
                                            placeholder="País..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'150px'}}>
                                        <SearchableMultiSelect 
                                            options={baseOptions} 
                                            selected={selectedBases} 
                                            onChange={setSelectedBases} 
                                            placeholder="Base..."
                                            displayField="name"
                                        />
                                    </div>
                                    <div style={{width:'150px'}}>
                                        <SearchableMultiSelect 
                                            options={vehicleTypes} 
                                            selected={selectedVehicleTypes} 
                                            onChange={setSelectedVehicleTypes} 
                                            placeholder="Tipo Vehículo..."
                                            displayField="name"
                                        />
                                    </div>
                                    {viewMode === 'history' && (<>
                                        <div style={{width:'160px'}}>
                                            <SearchableMultiSelect 
                                                options={adminStatusOptions} 
                                                selected={filterAdminStatus} 
                                                onChange={setFilterAdminStatus} 
                                                placeholder="Revisión..."
                                                displayField="name"
                                            />
                                        </div>
                                        <Form.Select size="sm" value={filterLiquidated} onChange={e => setFilterLiquidated(e.target.value)} style={{width:'130px'}} className={filterLiquidated ? "fw-bold border-primary text-primary" : ""}><option value="">- Pago Liq. -</option><option value="liquidated">✅ Liquidadas</option><option value="not_liquidated">⏳ No Liquidadas</option></Form.Select>
                                        <Form.Control type="text" placeholder="Nº Liq" size="sm" value={searchSettlementId} onChange={e => setSearchSettlementId(e.target.value)} style={{width:'70px'}} />
                                    </>)}
                                    <Form.Select 
                                        size="sm" 
                                        value={filterBillingStatus} 
                                        onChange={e => setFilterBillingStatus(e.target.value)} 
                                        style={{width:'140px'}} 
                                        className={filterBillingStatus ? "fw-bold border-primary text-primary" : ""}
                                    >
                                        <option value="">- Facturación -</option>
                                        <option value="not_preinvoiced">📄 No prefacturado</option>
                                        <option value="preinvoiced">⏳ Prefacturado</option>
                                        <option value="invoiced">✅ Facturado</option>
                                    </Form.Select>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="ID AQ" 
                                        size="sm" 
                                        value={searchAquivoyId} 
                                        onChange={e => setSearchAquivoyId(e.target.value)} 
                                        style={{width:'80px'}} 
                                    />
                                    <Form.Control 
                                        type="text" 
                                        placeholder="OS" 
                                        size="sm" 
                                        value={searchOsId} 
                                        onChange={e => setSearchOsId(e.target.value)} 
                                        style={{width:'130px'}} 
                                    />
                                    <Form.Control 
                                        type="text" 
                                        placeholder="File" 
                                        size="sm" 
                                        value={searchFile} 
                                        onChange={e => setSearchFile(e.target.value)} 
                                        style={{width:'110px'}} 
                                    />
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Pasajero" 
                                        size="sm" 
                                        value={searchPassengerName} 
                                        onChange={e => setSearchPassengerName(e.target.value)} 
                                        style={{width:'140px'}} 
                                    />
                                    <Form.Control type="text" placeholder="Voucher" size="sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{width:'90px'}} />
                                    
                                    <Form.Check 
                                        type="checkbox" 
                                        id="filter-incidents-only" 
                                        label="⚠️ Solo Incidencias" 
                                        className="mb-0 text-danger fw-bold d-flex align-items-center gap-1 cursor-pointer"
                                        checked={showIncidentsOnly}
                                        onChange={e => setShowIncidentsOnly(e.target.checked)}
                                    />
                                    {showIncidentsOnly && (
                                        <Form.Select 
                                            size="sm" 
                                            value={filterIncidentStatus} 
                                            onChange={e => setFilterIncidentStatus(e.target.value)} 
                                            style={{width:'150px'}} 
                                            className="fw-bold border-danger text-danger"
                                        >
                                            <option value="all">Todas</option>
                                            <option value="DISPUTED">Disputadas</option>
                                            <option value="RESOLVED">Ganadas</option>
                                            <option value="PENALIZED">Penalizadas</option>
                                        </Form.Select>
                                    )}

                                    <Button variant="primary" size="sm" onClick={handleSearchClick} disabled={isLoading} style={{ minWidth: '85px' }}>{isLoading ? '...' : '🔍 Filtrar'}</Button>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        onClick={handleSyncBookingIncidents} 
                                        disabled={isSyncingIncidents} 
                                        style={{ minWidth: '130px' }}
                                        className="d-flex align-items-center gap-1"
                                        title="Sincronizar incidencias de Booking.com recientes"
                                    >
                                        🔄 {isSyncingIncidents ? 'Sincronizando...' : 'Sincronizar Incidencias'}
                                    </Button>
                                    <Button 
                                        variant="success" 
                                        size="sm" 
                                        onClick={handleExportExcel} 
                                        disabled={isLoading || filteredTrips.length === 0} 
                                        style={{ minWidth: '100px' }}
                                        className="d-flex align-items-center gap-1"
                                    >
                                        📥 Exportar Excel ({filteredTrips.length})
                                    </Button>
                                </div>
                            ) : (
                                <div className="text-muted small italic">
                                    Selecciona un viaje de la izquierda para ver el ranking inteligente de conductores.
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {viewMode === 'smart' ? (
                <SmartAssignmentView 
                    trips={trips} 
                    drivers={drivers} 
                    vehicles={vehicles} 
                    vehicleTypes={vehicleTypes}
                    onRefresh={() => loadTrips(true)}
                />
            ) : (
                <Row className="g-3">
                    <Col lg={auditSidebarTrip ? 8 : 12} xl={auditSidebarTrip ? 8 : 12} className="transition-all duration-300">
                        {viewMode === 'active' && criticalAirportTripsWithoutFlight.length > 0 && (
                            <Alert variant="danger" className="d-flex align-items-center justify-content-between p-3 mb-3 border-start border-4 border-danger shadow-sm animate-pulse" style={{ borderRadius: '12px', backgroundColor: '#fff5f5', borderColor: '#feb2b2' }}>
                                <div className="d-flex align-items-center gap-3">
                                    <span style={{ fontSize: '1.8rem' }} className="d-none d-sm-inline">✈️</span>
                                    <div>
                                        <h6 className="mb-1 fw-bold text-danger" style={{ letterSpacing: '0.3px' }}>Reservas al Aeropuerto sin Número de Vuelo (menos de 24 horas)</h6>
                                        <p className="mb-0 text-muted small" style={{ fontSize: '0.85rem' }}>
                                            Hay <strong className="text-danger">{criticalAirportTripsWithoutFlight.length}</strong> reservas activas al Aeropuerto programadas para las próximas 24 horas que no tienen número de vuelo válido o están marcadas como pendiente/0.
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant={showMissingFlightsOnly ? "danger" : "outline-danger"} 
                                    size="sm" 
                                    className="fw-bold px-3 py-2 ms-3 flex-shrink-0 align-self-center"
                                    style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                                    onClick={() => setShowMissingFlightsOnly(!showMissingFlightsOnly)}
                                >
                                    {showMissingFlightsOnly ? "Mostrar Todos" : "Filtrar Pendientes"}
                                </Button>
                            </Alert>
                        )}
                        {selectedTrips.length > 0 && viewMode === 'history' && (
                        <div className="mb-2 p-2 bg-warning bg-opacity-10 rounded d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="small fw-bold ms-2">{selectedTrips.length} seleccionados</span>
                            <div className="d-flex align-items-center gap-2">
                                {canOperateDLocal && (
                                    <Button 
                                        variant="primary" 
                                        size="sm"
                                        onClick={handleOpenBulkDLocalModal}
                                        className="d-flex align-items-center gap-1 shadow-sm fw-semibold"
                                        style={{ backgroundColor: '#2563eb', borderColor: '#2563eb' }}
                                    >
                                        💳 Link de Pago Grupal ({selectedTrips.length})
                                    </Button>
                                )}
                                <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => setShowBulkVoucherModal(true)}
                                    className="d-flex align-items-center gap-1 shadow-sm fw-semibold bg-white"
                                >
                                    📄 Comprobante Grupal ({selectedTrips.length})
                                </Button>
                                {can('can_process_vouchers') && (
                                    <ButtonGroup size="sm">
                                        <Button variant="success" onClick={() => handleBulkProcess('processed')} disabled={!showProcessBtn || isBulkProcessing}>
                                            {isBulkProcessing ? <Spinner size="sm" animation="border" /> : '✅ Procesar'}
                                        </Button>
                                        <Button variant="outline-danger" onClick={() => handleBulkProcess('pending_review')} disabled={!showRevertBtn || isBulkProcessing}>↩ Deshacer</Button>
                                    </ButtonGroup>
                                )}
                            </div>
                        </div>
                    )}

                    {selectedTrips.length > 0 && viewMode === 'active' && (
                        <div className="mb-2 p-2 bg-light rounded d-flex justify-content-between align-items-center border flex-wrap gap-2" style={{ borderColor: '#e9d5ff', backgroundColor: 'rgba(139, 92, 246, 0.05)' }}>
                            <span className="small fw-bold ms-2 text-dark">{selectedTrips.length} servicios seleccionados</span>
                            <div className="d-flex align-items-center gap-2">
                                {canOperateDLocal && (
                                    <Button 
                                        variant="primary" 
                                        size="sm"
                                        onClick={handleOpenBulkDLocalModal}
                                        className="d-flex align-items-center gap-1 shadow-sm fw-semibold"
                                        style={{ backgroundColor: '#2563eb', borderColor: '#2563eb' }}
                                    >
                                        💳 Link de Pago Grupal ({selectedTrips.length})
                                    </Button>
                                )}
                                <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => setShowBulkVoucherModal(true)}
                                    className="d-flex align-items-center gap-1 shadow-sm fw-semibold bg-white"
                                >
                                    📄 Comprobante Grupal ({selectedTrips.length})
                                </Button>
                                <Button 
                                    variant="purple" 
                                    size="sm"
                                    style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6', color: 'white' }}
                                    onClick={handleBulkSendPassengerConfirmations}
                                    disabled={isSendingConfirmations}
                                >
                                    {isSendingConfirmations ? <Spinner size="sm" animation="border" className="me-1" /> : null}
                                    💬 Enviar Confirmaciones por WhatsApp API
                                </Button>
                            </div>
                        </div>
                    )}

                    <Card className="shadow-sm border-0 d-none d-md-block">
                        <Table hover responsive size="sm" className="align-middle small mb-0">
                            <thead className="bg-light"><tr>
                                <th style={{width:'30px'}}><Form.Check type="checkbox" onChange={handleSelectAll} checked={selectedTrips.length > 0 && selectedTrips.length === trips.length}/></th>
                                <th style={{width:'60px'}}>ID</th>
                                {viewMode === 'history' && <th>Voucher</th>}
                                {viewMode === 'history' && <th>Rev.</th>}
                                <th style={{width:'110px'}}>Fecha/Hora</th>
                                <th style={{width:'60px'}} className="text-center">Tipo</th>
                                <th>Cliente</th>
                                <th>Pasajero</th>
                                <th>Solicitante</th>
                                <th>Ruta (Origen / Destino)</th>
                                <th className="text-center" style={{width:'60px'}}>T. Est</th>
                                <th>Estado</th>
                                <th className="text-end">Valor</th>
                                <th className="text-center">Móvil</th>
                                <th className="text-end">Acciones</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedTrips.map(t => {
                                    const statusLower = t.status?.toLowerCase();
                                    const isLocked = LOCKED_STATUSES.includes(statusLower);
                                    const isVip = t.requested_vehicle_type?.name?.toLowerCase().includes('executive') || 
                                                   t.requested_vehicle_type?.name?.toLowerCase().includes('luxury');
                                    
                                    // Colores según estado de liquidación y facturación
                                    const isSettled = t.settlement_id != null;
                                    const invoiceStatus = t.client_invoice?.status;
                                    const isPrefacturado = invoiceStatus === 'pre_invoiced';
                                    const isFacturado = invoiceStatus === 'invoiced' || invoiceStatus === 'paid';

                                    let rowStyle = {};
                                    if (!selectedTrips.includes(t.id)) {
                                        if (isSettled && isFacturado) {
                                            rowStyle = { backgroundColor: '#e8f8f5' }; // Verde Esmeralda Pastel
                                        } else if (isSettled && isPrefacturado) {
                                            rowStyle = { backgroundColor: '#d1e7dd' }; // Verde Claro
                                        } else if (isFacturado) {
                                            rowStyle = { backgroundColor: '#ebf5fb' }; // Azul Pastel
                                        } else if (isPrefacturado) {
                                            rowStyle = { backgroundColor: '#cff4fc' }; // Celeste Claro
                                        } else if (isSettled) {
                                            rowStyle = { backgroundColor: '#fff3cd' }; // Amarillo Pastel
                                        }
                                    }

                                    return (
                                    <tr key={t.id} className={selectedTrips.includes(t.id) ? "table-active" : ""} style={{ ...rowStyle, '--bs-table-bg': rowStyle.backgroundColor }}>
                                        <td>
                                            <Form.Check type="checkbox" checked={selectedTrips.includes(t.id)} onChange={() => handleSelectTrip(t.id)}/>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <span className="fw-bold cursor-pointer text-primary" onClick={() => handleShowHistory(t.id)}>#{t.id}</span>
                                                {isVip && (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Servicio VIP ({t.requested_vehicle_type?.name})</Tooltip>}>
                                                        <span style={{ cursor: 'help' }}>⭐</span>
                                                    </OverlayTrigger>
                                                )}

                                                {t.admin_status === 'processed' && (!t.validation_details || t.validation_details.length === 0) && (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Validación OK</Tooltip>}>
                                                        <ShieldCheck size={14} className="text-success cursor-help" />
                                                    </OverlayTrigger>
                                                )}
                                                <span className="cursor-pointer" onClick={() => handleOpenAudit(t)}>🗺️</span>
                                                {!isLocked && (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Editar Servicio</Tooltip>}>
                                                        <span className="cursor-pointer ms-1" onClick={() => handleEditTrip(t)} style={{ fontSize: '1.2em' }}>✏️</span>
                                                     </OverlayTrigger>
                                                )}
                                                {isTripDLocalEligible(t) && (
                                                    <>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>{t.is_paid ? 'Link de Pago / Cobro Adicional' : 'Generar Link de Pago'}</Tooltip>}>
                                                            <span 
                                                                className="cursor-pointer ms-1" 
                                                                onClick={() => handleOpenDLocalModal(t)} 
                                                                style={{ fontSize: '1.1em' }}
                                                            >
                                                                💳
                                                            </span>
                                                        </OverlayTrigger>
                                                        {t.is_paid && ['DLocal', 'Webpay'].includes(t.payment_method) && isAlejandroUser && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Resetear Pago a Pendiente (Pruebas)</Tooltip>}>
                                                                <span 
                                                                    className="cursor-pointer ms-1 text-danger" 
                                                                    onClick={() => handleResetDLocalFromTable(t.id)} 
                                                                    style={{ fontSize: '1.0em' }}
                                                                >
                                                                    🔄
                                                                </span>
                                                            </OverlayTrigger>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            {t.aquivoy_id && (
                                                <div className="bg-info bg-opacity-10 text-info fw-bold px-1 rounded border border-info border-opacity-25" style={{fontSize: '0.85em', width:'fit-content', marginTop: '2px'}}>AQ: {t.aquivoy_id}</div>
                                            )}
                                            <div className="text-muted" style={{fontSize:'0.7em'}}>
                                                {t.trip_source === 'api' ? '🌐 API' :
                                                 t.trip_source === 'web' ? '💻 Web' :
                                                 t.trip_source === 'import' ? '📥 Import' :
                                                 '📱 APP'}
                                            </div>
                                            {t.external_reference && (
                                                <div className="d-flex flex-column gap-1 mt-1">
                                                    <div className="text-primary fw-bold" style={{fontSize:'0.65em', lineHeight: 1.1}}>OS: {t.external_reference}</div>
                                                    {t.booking_incident_status === 'DISPUTED' && (
                                                        <Badge bg="warning" text="dark" style={{ fontSize: '7.5px', padding: '2px 4px', width: 'fit-content' }}>
                                                            ⚠️ Disputa Activa
                                                        </Badge>
                                                    )}
                                                    {t.booking_incident_status === 'PENALIZED' && (
                                                        <Badge bg="danger" style={{ fontSize: '7.5px', padding: '2px 4px', width: 'fit-content' }}>
                                                            🔴 Penalizado
                                                        </Badge>
                                                    )}
                                                    {t.booking_incident_status === 'RESOLVED' && (
                                                        <Badge bg="success" style={{ fontSize: '7.5px', padding: '2px 4px', width: 'fit-content' }}>
                                                            🟢 Reclamo Ganado
                                                        </Badge>
                                                    )}
                                                    {t.booking_incident_dispute_sent && (
                                                        <Badge bg="info" style={{ fontSize: '7px', padding: '1px 3px', width: 'fit-content' }} title="Se enviaron respaldos / disputó en Partner Portal">
                                                            ✓ Respaldos Enviados
                                                        </Badge>
                                                    )}
                                                     {t.provider_appeal_status === 'submitted' && (
                                                        <Badge bg="purple" style={{ fontSize: '7px', padding: '1px 3px', width: 'fit-content', backgroundColor: '#6f42c1', color: 'white' }} title="El proveedor ingresó descargo y fotos de evidencia">
                                                            💬 Descargo Recibido
                                                        </Badge>
                                                    )}
                                                    {Array.isArray(t.validation_details) && t.validation_details.some(v => String(v).includes('ALERTA RECLAMO BOOKING')) && (
                                                        <Badge bg="danger" className="animate-pulse" style={{ fontSize: '7.5px', padding: '2px 4px', width: 'fit-content' }} title={t.validation_details.find(v => String(v).includes('ALERTA RECLAMO BOOKING'))}>
                                                            🚨 Free &lt; 24h (Reclamar)
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        {viewMode === 'history' && <td>{t.validation_folio || '-'}</td>}
                                        {viewMode === 'history' && (
                                            <td>
                                                {(t.admin_status !== 'processed' && t.admin_status !== 'settled' && (discrepancies[t.id]?.is_discrepancy || (t.validation_details && t.validation_details.length > 0))) ? (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`disc-${t.id}`}>
                                                                <strong>Revisión requerida (Click para Auditar):</strong>
                                                                <ul className="text-start mb-0 ps-3">
                                                                    {(discrepancies[t.id]?.reasons || t.validation_details).map((r, i) => <li key={i}>{r}</li>)}
                                                                </ul>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span style={{ cursor: 'pointer' }} onClick={() => setAuditSidebarTrip(t)}>{getAdminStatusBadge(t.admin_status, t.is_paid)}</span>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <span style={{ cursor: t.admin_status === 'pending_review' ? 'pointer' : 'default' }} onClick={() => t.admin_status === 'pending_review' && setAuditSidebarTrip(t)}>
                                                        {getAdminStatusBadge(t.admin_status, t.is_paid)}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            {(() => {
                                                let tripTimezone = t.origin_base?.timezone || t.timezone;
                                                if (!tripTimezone) {
                                                    if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                                                    else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                                                    else if (t.local_currency_code === 'EUR') tripTimezone = 'Europe/Madrid';
                                                    else if (t.local_currency_code === 'UYU') tripTimezone = 'America/Montevideo';
                                                    else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                                                    else if (t.pickup_lat >= -35.5 && t.pickup_lat <= -30.0 && t.pickup_lng >= -58.5 && t.pickup_lng <= -53.0) tripTimezone = 'America/Montevideo';
                                                    else if (t.pickup_lat >= 36.0 && t.pickup_lat <= 44.0 && t.pickup_lng >= -9.5 && t.pickup_lng <= 4.5) tripTimezone = 'Europe/Madrid';
                                                    else tripTimezone = 'America/Santiago';
                                                }
                                                const isInternational = tripTimezone !== 'America/Santiago';
                                                const flag = getFlagEmoji(tripTimezone, t.local_currency_code, t.pickup_lat, t.pickup_lng, t.origin_base);
                                                if (isInternational) {
                                                    return (
                                                        <>
                                                            <div className="fw-bold text-primary">
                                                                {flag} {new Intl.DateTimeFormat('es-CL', {
                                                                    day: '2-digit', month: '2-digit', year: '2-digit',
                                                                    hour: '2-digit', minute: '2-digit',
                                                                    timeZone: tripTimezone,
                                                                    hour12: false
                                                                }).format(new Date(t.scheduled_time))} LOCAL
                                                            </div>
                                                            <div className="text-muted" style={{fontSize: '0.75em'}}>
                                                                {format(new Date(t.scheduled_time), 'HH:mm')} CHILE {format(new Date(t.scheduled_time), 'dd/MM/yy')}
                                                            </div>
                                                        </>
                                                    );
                                                } else {
                                                    return (
                                                        <>
                                                            <div className="fw-bold">{flag} {format(new Date(t.scheduled_time), 'HH:mm')}</div>
                                                            <div className="text-muted small">{format(new Date(t.scheduled_time), 'dd/MM/yy')}</div>
                                                        </>
                                                    );
                                                }
                                            })()}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <VehicleTypeIcon type={t.requested_vehicle_type} />
                                                <Badge bg="secondary" className="px-1 py-0.5" style={{ fontSize: '10px' }}>
                                                    x{(() => {
                                                        const uniqueStopsPax = t.stops?.filter(s => 
                                                            s.passenger_name && 
                                                            s.passenger_name.trim() !== '' && 
                                                            s.passenger_name !== t.passenger_name &&
                                                            !(t.additional_passengers?.some(ap => ap.full_name === s.passenger_name))
                                                        ).length || 0;
                                                        return Math.max(t.passenger_count || 1, 1 + (t.additional_passengers?.length || 0) + uniqueStopsPax);
                                                    })()}
                                                </Badge>
                                            </div>
                                        </td>
                                        <td className="fw-bold text-dark">
                                            <div>{t.client?.name || getClientName(t.client_id)}</div>
                                            {(t.cost_center || t.cost_center_label) && (
                                                <div className="text-muted fw-normal" style={{ fontSize: '0.72em', marginTop: '2px' }}>
                                                    🏢 {t.cost_center ? `${t.cost_center.name || ''} ${t.cost_center.code ? `(${t.cost_center.code})` : ''}`.trim() : t.cost_center_label}
                                                </div>
                                            )}
                                            {(t.file || t.client_file_number) && (
                                                <div className="text-success fw-semibold" style={{ fontSize: '0.72em', marginTop: '1px' }}>
                                                    📂 File: {t.file || t.client_file_number}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="fw-bold d-inline-flex align-items-center gap-1">
                                                    👤 {t.passenger_name}
                                                    {t.passenger_confirmed && (
                                                        <Badge bg="success" style={{ fontSize: '0.65rem' }}>✅ Confirmado</Badge>
                                                    )}
                                                    {t.passenger_issue && (
                                                        <Badge 
                                                             bg="danger" 
                                                             className="animate-pulse" 
                                                             style={{ fontSize: '0.65rem', cursor: 'pointer' }}
                                                             onClick={(e) => {
                                                                 e.stopPropagation();
                                                                 handleResolvePassengerAlert(t.id, t.passenger_name);
                                                             }}
                                                             title="Click para resolver alerta"
                                                         >
                                                             ⚠️ Alerta
                                                         </Badge>
                                                    )}
                                                </div>
                                                {(() => {
                                                    const companions = [];
                                                    if (t.additional_passengers) {
                                                        t.additional_passengers.forEach(p => {
                                                            companions.push({
                                                                name: p.full_name,
                                                                phone: p.phone,
                                                                type: 'Acompañante'
                                                            });
                                                        });
                                                    }
                                                    if (t.stops) {
                                                        t.stops.forEach(s => {
                                                            if (s.passenger_name && s.passenger_name.trim() !== '') {
                                                                const alreadyAdded = companions.some(c => c.name === s.passenger_name);
                                                                if (!alreadyAdded) {
                                                                    companions.push({
                                                                        name: s.passenger_name,
                                                                        phone: s.passenger_phone,
                                                                        type: 'Parada Intermedia'
                                                                    });
                                                                }
                                                            }
                                                        });
                                                    }
                                                    
                                                    if (companions.length === 0) return null;
                                                    
                                                    return (
                                                        <OverlayTrigger
                                                            trigger={['hover', 'focus']}
                                                            placement="right"
                                                            overlay={
                                                                <Popover id={`popover-pax-${t.id}`} className="shadow border-primary">
                                                                    <Popover.Header as="h3" className="bg-primary text-white py-1 small fw-bold">Acompañantes ({companions.length})</Popover.Header>
                                                                    <Popover.Body className="py-2 px-3 small">
                                                                        <ul className="list-unstyled mb-0">
                                                                            {companions.map((c, cIdx) => (
                                                                                <li key={cIdx} className="border-bottom py-1 last:border-0">
                                                                                    • {c.name} {c.phone ? `(${c.phone})` : ''} 
                                                                                    <span className="text-muted small ms-1">({c.type})</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </Popover.Body>
                                                                </Popover>
                                                            }
                                                        >
                                                            <Badge bg="info" pill className="cursor-help px-1" style={{fontSize:'0.75em'}}>👥 {companions.length}</Badge>
                                                        </OverlayTrigger>
                                                    );
                                                })()}
                                                {t.notes && (
                                                    <OverlayTrigger
                                                        trigger={['hover', 'focus']}
                                                        placement="right"
                                                        overlay={
                                                            <Popover id={`popover-notes-${t.id}`} className="shadow border-warning">
                                                                <Popover.Header as="h3" className="bg-warning text-dark py-1 small fw-bold">Notas del Servicio</Popover.Header>
                                                                <Popover.Body className="py-2 px-3 small">{t.notes}</Popover.Body>
                                                            </Popover>
                                                        }
                                                    >
                                                        <span className="text-warning cursor-help">📝</span>
                                                    </OverlayTrigger>
                                                )}
                                                {/* --- ICONOS DE EXTRAS --- */}
                                                {(t.extra_services || [])
                                                    .filter(s => s.id !== 'parada_intermedia' && s.id !== 'reimbursable_expense')
                                                    .map(s => {
                                                        const extraIconMap = {
                                                            baby_seat:       { icon: '👶', label: 'Silla de Bebé / Booster' },
                                                            water_still:     { icon: '💧', label: 'Agua Mineral sin Gas' },
                                                            water_sparkling: { icon: '🫧', label: 'Agua Mineral con Gas' },
                                                            pet:             { icon: '🐾', label: 'Mascota Pequeña' },
                                                            extra_luggage:   { icon: '🧳', label: 'Maleta Extra' },
                                                        };
                                                        const info = extraIconMap[s.id] || { icon: '➕', label: s.name || s.id };
                                                        return (
                                                            <OverlayTrigger
                                                                key={s.id}
                                                                placement="top"
                                                                overlay={<Tooltip>{info.label}</Tooltip>}
                                                            >
                                                                <span style={{ cursor: 'help' }}>{info.icon}</span>
                                                            </OverlayTrigger>
                                                        );
                                                    })
                                                }
                                            </div>
                                            <div className="text-primary small" style={{fontSize:'0.85em'}}>{t.passenger_phone || 'Sin tel.'}</div>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{t.requester?.full_name || t.external_solicitante || '---'}</div>
                                        </td>
                                        <td style={{maxWidth:'250px'}}>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-route-${t.id}`} className="shadow">
                                                        <div className="text-start">
                                                            <div><strong>Vehículo:</strong> {t.requested_vehicle_type?.name || 'Cualquiera'}</div>
                                                            {t.flight_number && <div><strong>Vuelo:</strong> {t.flight_number}</div>}
                                                        </div>
                                                    </Tooltip>
                                                }
                                            >
                                                <div className="cursor-help">
                                                    <div className="small text-truncate" title={t.pickup_address}>
                                                        <span className="text-success me-1">🟢</span>
                                                        {isAirport(t.pickup_address) && <Plane size={12} className="me-1 text-info"/>}
                                                        {t.pickup_address}
                                                    </div>

                                                    {/* Paradas Intermedias */}
                                                    {t.stops && t.stops.length > 0 && (
                                                        <div className="ps-2 my-1 border-start border-info" style={{ fontSize: '0.85em' }}>
                                                            {t.stops.slice().sort((a,b) => a.order - b.order).map((stop, idx) => (
                                                                <div key={idx} className="text-truncate text-muted" title={stop.address}>
                                                                    <span className="text-info me-1">📍</span>
                                                                    {stop.address} {stop.passenger_name ? `(${stop.passenger_name})` : ''}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="small text-truncate mt-1" title={t.dropoff_address}>
                                                        <span className="text-danger me-1">🏁</span>
                                                        {isAirport(t.dropoff_address) && <Plane size={12} className="me-1 text-info"/>}
                                                        {t.dropoff_address}
                                                    </div>
                                                </div>
                                            </OverlayTrigger>
                                        </td>
                                        <td className="text-center" style={{backgroundColor: 'rgba(13, 110, 253, 0.03)'}}>
                                            <div className="fw-bold text-primary" style={{fontSize: '1.1em'}}>
                                                <Clock size={12} className="me-1"/>
                                                {t.estimated_duration_minutes || '-'} '
                                            </div>
                                            {t.distance_km > 0 && <div className="text-muted" style={{fontSize:'0.7em'}}>{t.distance_km.toFixed(1)} km</div>}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span 
                                                    className={t.isCritical ? 'pulse-animation' : ''}
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(t.status, t.isCritical),
                                                        display: 'inline-block',
                                                        flexShrink: 0
                                                    }}
                                                ></span>
                                                <span className="fw-bold" style={{ color: getStatusColor(t.status, t.isCritical), fontSize: '0.9rem' }}>
                                                    {STATUS_LABELS[t.status] || t.status}
                                                </span>
                                            </div>
                                            {t.status?.toLowerCase() === 'draft' && !t.is_paid && (
                                                <div className="mt-1">
                                                    <Badge bg="warning" text="dark" style={{ fontSize: '9px' }}>
                                                        {(t.client_id && Number(t.client_id) !== 7 && t.payment_method !== 'DLocal')
                                                            ? '⏳ Fuera de Plazo (Por Confirmar)'
                                                            : '⏳ Esperando Pago'}
                                                    </Badge>
                                                </div>
                                            )}
                                            {t.is_asap && !['completed', 'cancelled', 'no_show'].includes(t.status?.toLowerCase()) && (
                                                <div className="mt-1">
                                                    <Badge
                                                        bg={t.asap_status === 'offered' ? 'danger' : t.asap_status === 'searching' ? 'warning' : 'info'}
                                                        className="d-flex align-items-center gap-1 pulse-animation"
                                                        style={{fontSize: '9px', width: 'fit-content'}}
                                                    >
                                                        <Zap size={10}/> {t.asap_status?.toUpperCase() || 'ASAP'}
                                                    </Badge>
                                                </div>
                                            )}

                                            {t.isCritical && <div className="text-danger fw-bold" style={{fontSize: '0.7rem'}}>¡GESTIONAR AHORA!</div>}
                                        </td>
                                        <td className="text-end fw-bold">
                                            {(() => {
                                                const basePrice = parseFloat(t.final_price) || parseFloat(t.estimated_price) || 0;
                                                const waitAmount = parseFloat(t.waiting_time_amount) || 0;
                                                const extras = (t.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
                                                const totalVenta = basePrice + waitAmount + extras;
                                                
                                                if (waitAmount > 0 || extras > 0) {
                                                    return (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`tooltip-venta-${t.id}`}>
                                                                    <div className="text-start">
                                                                        <strong>Venta Base:</strong> ${Math.round(basePrice).toLocaleString()}<br/>
                                                                        {waitAmount > 0 && <><strong>Espera:</strong> ${Math.round(waitAmount).toLocaleString()}<br/></>}
                                                                        {extras > 0 && <><strong>Extras:</strong> ${Math.round(extras).toLocaleString()}</>}
                                                                    </div>
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <div className="cursor-help text-primary">${Math.round(totalVenta).toLocaleString()} <small>🕒</small></div>
                                                        </OverlayTrigger>
                                                    );
                                                }
                                                return (
                                                    <div>
                                                        <div>${Math.round(totalVenta).toLocaleString()}</div>
                                                        {t.is_paid && (
                                                            <div className="mt-1">
                                                                <Badge bg={t.payment_method === 'DLocal' ? 'primary' : 'success'} style={{ fontSize: '0.65rem', padding: '2px 4px' }}>
                                                                    ✓ {t.payment_method === 'DLocal' ? 'dLocal Pagado' : (t.payment_method || 'Pagado')}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="text-center">
                                            {t.driver_id ? (
                                                <div>
                                                    <OverlayTrigger 
                                                        placement="top" 
                                                        overlay={
                                                            <Tooltip>
                                                                <div className="text-start">
                                                                    <strong>Conductor:</strong> {getDriverName(t.driver_id)} <br/>
                                                                    <strong>Pago:</strong> ${t.driver_earnings?.toLocaleString() || '0'} {t.provider_currency_code || t.local_currency_code || 'CLP'}
                                                                    {(t.provider?.name || t.driver?.provider?.name) && (
                                                                        <><br/><strong>Proveedor:</strong> {t.provider?.name || t.driver?.provider?.name}</>
                                                                    )}
                                                                </div>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div className="fw-bold text-dark cursor-help">
                                                            {getVehicleCode(t)}
                                                            {(t.provider?.name || t.driver?.provider?.name) && (
                                                                <div className="text-muted text-truncate" style={{ fontSize: '9px', maxWidth: '110px', margin: '0 auto' }} title={t.provider?.name || t.driver?.provider?.name}>
                                                                    🏢 {t.provider?.name || t.driver?.provider?.name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </OverlayTrigger>
                                                    {nearbyDriverConflictsByTripId.has(t.id) && (() => {
                                                        const nearList = nearbyDriverConflictsByTripId.get(t.id);
                                                        const firstNear = nearList[0];
                                                        const isCompletedRow = statusLower === 'completed';
                                                        return (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip>
                                                                        <div className="text-start">
                                                                            <strong>⚠️ Reserva muy cercana en este conductor:</strong>
                                                                            {nearList.map((nc, idx) => (
                                                                                <div key={idx} className="mt-1">
                                                                                    • <strong>#{nc.id}</strong> ({nc.timeStr}) — {nc.passenger}<br/>
                                                                                    <small>Diferencia: {nc.diffMins} min (mín. sugerido: {nc.requiredGap} min)</small>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className="mt-1">
                                                                    <Badge
                                                                        bg={isCompletedRow ? 'warning' : 'danger'}
                                                                        text={isCompletedRow ? 'dark' : 'light'}
                                                                        className={!isCompletedRow ? 'pulse-animation cursor-help' : 'cursor-help'}
                                                                        style={{ fontSize: '9px', padding: '3px 5px' }}
                                                                    >
                                                                        ⚠️ Cercana #{firstNear.id} ({firstNear.timeStr}){nearList.length > 1 ? ` +${nearList.length - 1}` : ''}
                                                                    </Badge>
                                                                </div>
                                                            </OverlayTrigger>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <div>
                                                    <Badge bg={statusLower === 'draft' ? 'warning' : 'danger'} text={statusLower === 'draft' ? 'dark' : 'light'}>
                                                        {statusLower === 'draft' ? 'Pre-Reserva' : 'Sin Asignar'}
                                                    </Badge>
                                                    {(t.suggested_provider?.name || t.provider?.name) && (
                                                        <div className="text-muted text-truncate mt-1" style={{ fontSize: '9px', maxWidth: '110px', margin: '0 auto' }} title={t.suggested_provider?.name || t.provider?.name}>
                                                            🏢 {t.suggested_provider?.name || t.provider?.name}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-end"><div className="d-flex justify-content-end gap-1">
                                            {statusLower === 'draft' && (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Confirmar Pre-Reserva y pasar a Pendiente para asignar móvil</Tooltip>}>
                                                    <Button size="sm" variant="warning" className="py-0 px-1 fw-bold text-dark" onClick={() => handleStatusChange(t, 'pending')}>
                                                        ✅ Confirmar
                                                    </Button>
                                                </OverlayTrigger>
                                            )}
                                            {statusLower === 'pending' && !t.driver_id && <OverlayTrigger placement="top" overlay={<Tooltip>Asignar Conductor</Tooltip>}><Button size="sm" variant="danger" className="py-0 px-1" onClick={() => handleOpenAssignDriver(t)}>Asignar</Button></OverlayTrigger>}
                                            {statusLower === 'pending' && t.driver_id && <OverlayTrigger placement="top" overlay={<Tooltip>Confirmar Asignación</Tooltip>}><Button size="sm" variant="primary" className="py-0 px-1" onClick={() => handleStatusChange(t, 'assigned')}>Confirmar</Button></OverlayTrigger>}
                                            {statusLower === 'assigned' && <OverlayTrigger placement="top" overlay={<Tooltip>Aceptar Servicio (Conductor)</Tooltip>}><Button size="sm" variant="info" className="py-0 px-1 text-white" onClick={() => handleStatusChange(t, 'confirmed')}>Aceptar</Button></OverlayTrigger>}
                                            {statusLower === 'confirmed' && <OverlayTrigger placement="top" overlay={<Tooltip>En Camino al Origen</Tooltip>}><Button size="sm" variant="outline-primary" className="py-0 px-1" onClick={() => handleStatusChange(t, 'en_route')}>▶</Button></OverlayTrigger>}
                                            {statusLower === 'en_route' && <OverlayTrigger placement="top" overlay={<Tooltip>En Posición (Llegó)</Tooltip>}><Button size="sm" variant="outline-info" className="py-0 px-1" onClick={() => handleStatusChange(t, 'arrived')}>📍</Button></OverlayTrigger>}
                                            {statusLower === 'arrived' && <OverlayTrigger placement="top" overlay={<Tooltip>Pasajeros a Bordo (En Ruta)</Tooltip>}><Button size="sm" variant="outline-warning" className="py-0 px-1" onClick={() => handleStatusChange(t, 'on_board')}>👋</Button></OverlayTrigger>}
                                            {statusLower === 'on_board' && <OverlayTrigger placement="top" overlay={<Tooltip>Finalizar Servicio</Tooltip>}><Button size="sm" variant="success" className="py-0 px-1" onClick={() => handleOpenComplete(t)}>🏁</Button></OverlayTrigger>}
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Clonar Servicio</Tooltip>}><Button size="sm" variant="outline-success" className="py-0 px-1" onClick={() => handleCloneTrip(t)}>👯</Button></OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Ver Detalles</Tooltip>}><Button size="sm" variant="outline-secondary" className="py-0 px-1" onClick={() => { setCurrentTrip(t); setShowDetailsModal(true); }}>📑</Button></OverlayTrigger>
                                            {['completed', 'cancelled', 'cancelled_paid', 'no_show'].includes(statusLower) && (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Generar Evidencia GPS de Espera</Tooltip>}>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-success" 
                                                        className="py-0 px-1 ms-1 text-success border-success" 
                                                        onClick={() => {
                                                            setEvidenceTrip(t);
                                                            setShowEvidenceModal(true);
                                                        }}
                                                    >
                                                        🛡️
                                                    </Button>
                                                </OverlayTrigger>
                                            )}
                                            {t.driver_id && !isLocked && ['pending', 'assigned'].includes(statusLower) && <OverlayTrigger placement="top" overlay={<Tooltip>Liberar Conductor (Quitar)</Tooltip>}><Button size="sm" variant="outline-warning" className="py-0 px-1" onClick={() => handleUnassign(t)}>✕</Button></OverlayTrigger>}
                                            {statusLower === 'completed' && (
                                                <>
                                                    {can('can_process_vouchers') && (
                                                        <>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>{t.admin_status === 'processed' ? 'Revertir a "Por Revisar"' : 'Marcar como Procesado'}</Tooltip>}>
                                                                <Button 
                                                                    size="sm" 
                                                                    variant={t.admin_status === 'processed' ? "outline-secondary" : "success"} 
                                                                    className="py-0 px-1" 
                                                                    onClick={() => {
                                                                        if (t.admin_status === 'processed') {
                                                                            if (window.confirm('¿Está seguro de revertir este servicio a "Por Revisar"?')) {
                                                                                handleAdminStatusChange(t, 'pending_review');
                                                                            }
                                                                        } else {
                                                                            handleAdminStatusChange(t, 'processed');
                                                                        }
                                                                    }}
                                                                >
                                                                    {t.admin_status === 'processed' ? '↩' : '✅'}
                                                                </Button>
                                                            </OverlayTrigger>
                                                            {t.admin_status === 'pending_review' && (
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Auditar Discrepancias (Panel Lateral)</Tooltip>}>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="warning" 
                                                                        className="py-0 px-1 ms-1 text-dark" 
                                                                        onClick={() => setAuditSidebarTrip(t)}
                                                                    >
                                                                        🔍
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            )}
                                                        </>
                                                    )}
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Cancelar Servicio</Tooltip>}>
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline-danger" 
                                                            className="py-0 px-1" 
                                                            disabled={t.admin_status === 'processed' || t.settlement_id != null || t.client_invoice_id != null}
                                                            onClick={() => { 
                                                                if(window.confirm('¿Está seguro de cancelar este servicio?')) {
                                                                    handleStatusChange(t, 'cancelled'); 
                                                                }
                                                            }}
                                                        >
                                                            ✕
                                                        </Button>
                                                    </OverlayTrigger>
                                                    {can('can_void_trips') && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Anular Servicio</Tooltip>}>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline-warning" 
                                                                className="py-0 px-1 ms-1 text-danger fw-bold" 
                                                                disabled={t.settlement_id != null || t.client_invoice_id != null || t.status === 'anulado' || t.status === 'ANULADO'}
                                                                onClick={() => handleVoidClick(t)}
                                                            >
                                                                🚫
                                                            </Button>
                                                        </OverlayTrigger>
                                                    )}
                                                </>
                                            )}
                                            {statusLower === 'anulado' && (
                                                <>
                                                    {can('can_void_trips') && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Revertir Anulación</Tooltip>}>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline-success" 
                                                                className="py-0 px-1 ms-1" 
                                                                disabled={t.settlement_id != null || t.client_invoice_id != null}
                                                                onClick={() => handleUnvoidClick(t)}
                                                            >
                                                                🔄
                                                            </Button>
                                                        </OverlayTrigger>
                                                    )}
                                                </>
                                            )}
                                        </div></td>
                                    </tr>
                                );})}
                            </tbody>
                        </Table>
                        <Card.Footer className="d-flex justify-content-between align-items-center bg-white border-top-0 pt-2">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">Ver:</span>
                                <Form.Select 
                                    size="sm" 
                                    value={pageSize} 
                                    onChange={e => {
                                        setPageSize(parseInt(e.target.value));
                                        setPage(1);
                                    }} 
                                    style={{width:'70px'}}
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>&lt;</Button>
                                <span className="small">Pág {page} de {Math.max(1, Math.ceil(filteredTrips.length / pageSize))}</span>
                                <Button variant="outline-secondary" size="sm" disabled={page >= Math.ceil(filteredTrips.length / pageSize)} onClick={() => setPage(p => p + 1)}>&gt;</Button>
                            </div>
                        </Card.Footer>
                    </Card>
                </Col>
                {auditSidebarTrip && (
                    <Col lg={4} xl={4} className="transition-all duration-300">
                        <AuditSidebar 
                            trip={auditSidebarTrip}
                            onClose={() => setAuditSidebarTrip(null)}
                            onSave={handleSaveAudit}
                            clients={clients}
                            drivers={drivers}
                            vehicles={vehicles}
                            vehicleTypes={vehicleTypes}
                            areas={areas}
                            costCenters={costCenters}
                            onRefreshCostCenters={setCostCenters}
                        />
                    </Col>
                )}
            </Row>
        )}

            {/* MODAL DE PROCESAMIENTO ASÍNCRONO */}
            <Modal show={isBulkProcessing} backdrop="static" keyboard={false} centered size="sm">
                <Modal.Body className="text-center p-4">
                    <Loader size={48} className="text-primary animate-spin mb-3" />
                    <h5 className="fw-bold">Procesando...</h5>
                    <p className="text-muted small mb-0">{bulkProcessMessage}</p>
                    <ProgressBar animated now={100} className="mt-3" style={{height:'4px'}} />
                </Modal.Body>
            </Modal>

            {showModal && currentTrip && (
                <TripModal 
                    key={isEditing && currentTrip?.id ? `trip-edit-${currentTrip.id}` : 'trip-new'} 
                    show={showModal} 
                    onHide={() => {
                        setShowModal(false);
                        setCurrentTrip(null);
                    }} 
                    trip={currentTrip} 
                    isEditing={isEditing} 
                    clients={clients} 
                    drivers={drivers} 
                    vehicleTypes={vehicleTypes} 
                    vehicles={vehicles} 
                    contracts={contracts} 
                    onSave={handleSaveTrip} 
                    isLoaded={isLoaded} 
                    isLoading={isLoading} 
                />
            )}
            
            {/* Modal de Motivo de Anulación */}
            <Modal show={showVoidModal} onHide={() => !isVoiding && setShowVoidModal(false)} centered>
                <Modal.Header closeButton={!isVoiding} className="bg-danger text-white">
                    <Modal.Title className="fw-bold">🚫 Motivo de Anulación - Servicio #{voidTripId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Por favor, escriba la razón de la anulación:</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            placeholder="Ej: Cliente canceló a último minuto por cambio de planes" 
                            value={voidReason} 
                            onChange={e => setVoidReason(e.target.value)}
                            disabled={isVoiding}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVoidModal(false)} disabled={isVoiding}>
                        Cancelar
                    </Button>
                    <Button variant="danger" className="fw-bold" onClick={handleConfirmVoid} disabled={isVoiding || !voidReason.trim()}>
                        {isVoiding && <Spinner animation="border" size="sm" className="me-2" />}
                        Confirmar Anulación
                    </Button>
                </Modal.Footer>
            </Modal>
            <TripDetailsAccordion show={showDetailsModal} onHide={() => setShowDetailsModal(false)} trip={currentTrip} isLoaded={isLoaded} areas={areas} costCenters={costCenters} />
            <BulkVoucherModal show={showBulkVoucherModal} onHide={() => setShowBulkVoucherModal(false)} selectedTripIds={selectedTrips} trips={trips} />
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered backdrop="static">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold text-dark">
                        📜 Historial de Auditoría - Reserva #{historyTripId}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    {selectedTripLogs.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <Clock size={48} className="mb-3 text-secondary animate-pulse" />
                            <p className="mb-0">No se encontraron registros de auditoría para esta reserva.</p>
                        </div>
                    ) : (
                        <div className="position-relative ps-4 ms-2" style={{ borderLeft: '3px solid #dee2e6' }}>
                            {selectedTripLogs.map((log) => {
                                let badgeColor = 'bg-secondary';
                                let iconText = '📝';
                                const actionLower = log.action.toLowerCase();
                                
                                if (actionLower.includes('cread')) {
                                    badgeColor = 'bg-success text-white';
                                    iconText = '🌟';
                                } else if (actionLower.includes('rechaz')) {
                                    badgeColor = 'bg-danger text-white';
                                    iconText = '🚫';
                                } else if (actionLower.includes('actualiz') || actionLower.includes('modific')) {
                                    badgeColor = 'bg-primary text-white';
                                    iconText = '✏️';
                                } else if (actionLower.includes('evidenc') || actionLower.includes('foto')) {
                                    badgeColor = 'bg-warning text-dark';
                                    iconText = '📸';
                                } else if (actionLower.includes('parada')) {
                                    badgeColor = 'bg-info text-white';
                                    iconText = '📍';
                                } else if (actionLower.includes('cancel')) {
                                    badgeColor = 'bg-danger text-white';
                                    iconText = '❌';
                                }

                                const detailItems = log.details ? log.details.split(' | ') : [];

                                return (
                                    <div key={log.id} className="position-relative mb-4">
                                        <div 
                                            className={`position-absolute rounded-circle d-flex align-items-center justify-content-center ${badgeColor}`}
                                            style={{
                                                left: '-37px',
                                                top: '2px',
                                                width: '26px',
                                                height: '26px',
                                                fontSize: '0.85rem',
                                                border: '3px solid #f8f9fa',
                                                boxShadow: '0 0 0 1px #dee2e6',
                                                zIndex: 2
                                            }}
                                        >
                                            {iconText}
                                        </div>

                                        <Card className="border-0 shadow-sm">
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                    <div>
                                                        <Badge className={`${badgeColor} me-2 px-2 py-1`}>
                                                            {log.action}
                                                        </Badge>
                                                        <span className="text-muted small">por </span>
                                                        <strong className="text-secondary small">{log.user_email}</strong>
                                                    </div>
                                                    <span className="text-muted small fw-medium">
                                                        📅 {new Date(log.timestamp).toLocaleString('es-CL', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        })}
                                                    </span>
                                                </div>

                                                <div className="text-dark">
                                                    {detailItems.length > 1 ? (
                                                        <ul className="list-unstyled mb-0 ps-0">
                                                            {detailItems.map((item, i) => (
                                                                <li key={i} className="py-1 border-bottom border-light d-flex align-items-start gap-2">
                                                                    <span className="text-primary mt-1" style={{fontSize: '0.75rem'}}>•</span>
                                                                    <span style={{fontSize: '0.9rem'}}>{translateLogDetails(item)}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="mb-0 small" style={{fontSize: '0.9rem'}}>{translateLogDetails(log.details)}</p>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
            <DriverAssignModal 
                show={showDriverModal} 
                onHide={() => setShowDriverModal(false)} 
                drivers={drivers} 
                vehicles={vehicles} 
                onAssign={handleDriverSelected} 
                baseId={assigningTrip?.origin_base_id}
                allTrips={trips}
                currentTrip={assigningTrip}
            />
            <CompleteTripModal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} trip={completingTrip} onConfirm={handleCompleteTrip} />
            <TripAuditModal show={showAuditModal} onHide={() => setShowAuditModal(false)} trip={auditTrip} isLoaded={isLoaded} />
            <GPSEvidenceGeneratorModal show={showEvidenceModal} onHide={() => setShowEvidenceModal(false)} trip={evidenceTrip} />

            {/* MODAL DE ITINERARIOS CONSOLIDADOS PARA CONDUCTORES */}
            <Modal show={showItineraryModal} onHide={() => setShowItineraryModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="fw-bold">📋 Recordatorios de Itinerarios (Conductores)</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    <div className="bg-white p-3 rounded shadow-sm mb-3">
                        <Row className="align-items-center">
                            <Col md={6}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="fw-bold text-muted small text-uppercase">Fecha del Itinerario</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={itineraryDate} 
                                        onChange={e => setItineraryDate(e.target.value)} 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex justify-content-end align-items-end mt-3 mt-md-0">
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => loadItineraryTrips(itineraryDate)} 
                                    disabled={itineraryLoading}
                                    size="sm"
                                >
                                    {itineraryLoading ? <Spinner size="sm" animation="border" /> : "🔄 Recargar"}
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {itineraryLoading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Buscando viajes del día...</p>
                        </div>
                    ) : groupedItineraries.length === 0 ? (
                        <div className="text-center p-5 bg-white rounded shadow-sm">
                            <span className="display-4 text-muted">📭</span>
                            <h5 className="mt-3 text-muted">No hay viajes con conductor asignado para esta fecha.</h5>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {groupedItineraries.map((group) => {
                                const hasPhone = !!group.driver.phone;
                                return (
                                    <Card key={group.driver.id} className="border-0 shadow-sm overflow-hidden">
                                        <Card.Header className="bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div>
                                                <h5 className="fw-bold mb-0 text-dark">
                                                    👨‍✈️ {group.driver.full_name}
                                                </h5>
                                                <small className="text-muted">
                                                    Móvil: <strong className="text-primary">{group.vehicle.internal_code || '---'}</strong> 
                                                    {group.driver.phone && ` | Tel: ${group.driver.phone}`}
                                                </small>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {getItineraryStatusBadge(group)}
                                                <Button 
                                                    variant="purple" 
                                                    size="sm" 
                                                    onClick={() => handleSendItineraryOfficial(group)}
                                                    disabled={!hasPhone || isSendingItinerary[group.driver.id]}
                                                    className="fw-bold d-flex align-items-center gap-1 text-white"
                                                    style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
                                                >
                                                    {isSendingItinerary[group.driver.id] ? <Spinner size="sm" animation="border" className="me-1" /> : <MessageCircle size={14} />} 
                                                    API WhatsApp
                                                </Button>
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm" 
                                                    onClick={() => handleSendItinerary(group)}
                                                    disabled={!hasPhone}
                                                    className="fw-bold d-flex align-items-center gap-1"
                                                >
                                                    Enlace Manual
                                                </Button>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="p-0 border-top">
                                            <Table hover size="sm" className="mb-0 align-middle table-borderless" style={{ fontSize: '0.85rem' }}>
                                                <thead className="bg-light text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                                                    <tr>
                                                        <th className="ps-3" style={{ width: '80px' }}>Hora</th>
                                                        <th style={{ width: '150px' }}>Cliente / Pax</th>
                                                        <th>Ruta (Origen / Destino)</th>
                                                        <th className="pe-3" style={{ width: '150px' }}>Acompañantes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.trips.map(t => {
                                                        const isAirportPickup = isAirport(t.pickup_address);
                                                        const isAirportDropoff = isAirport(t.dropoff_address);
                                                        return (
                                                            <tr key={t.id} className="border-bottom border-light">
                                                                <td className="ps-3">
                                                                    <div className="fw-bold text-primary">
                                                                        {(() => {
                                                                            let tz = t.origin_base?.timezone || t.timezone;
                                                                            if (!tz) {
                                                                                if (t.local_currency_code === 'PEN') tz = 'America/Lima';
                                                                                else if (t.local_currency_code === 'ARS') tz = 'America/Argentina/Mendoza';
                                                                                else if (t.local_currency_code === 'COP') tz = 'America/Bogota';
                                                                                else if (t.local_currency_code === 'BRL') tz = 'America/Sao_Paulo';
                                                                                else tz = 'America/Santiago';
                                                                            }
                                                                            try {
                                                                                return new Date(t.scheduled_time).toLocaleTimeString('es-CL', {
                                                                                    timeZone: tz,
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                    hour12: false
                                                                                });
                                                                            } catch (err) {
                                                                                return format(new Date(t.scheduled_time), 'HH:mm');
                                                                            }
                                                                        })()}
                                                                    </div>
                                                                    {t.local_currency_code && t.local_currency_code !== 'CLP' && (
                                                                        <div className="text-muted fw-bold" style={{ fontSize: '9px', marginTop: '2px' }}>
                                                                            {(() => {
                                                                                const country = t.origin_base?.country_code || t.country_code;
                                                                                if (country === 'DO') return '🇩🇴 RD';
                                                                                if (t.local_currency_code === 'PEN' || country === 'PE') return '🇵🇪 LIMA';
                                                                                if (t.local_currency_code === 'COP' || country === 'CO') return '🇨🇴 BOG';
                                                                                if (t.local_currency_code === 'ARS' || country === 'AR') return '🇦🇷 MDZ';
                                                                                if (t.local_currency_code === 'BRL' || country === 'BR') return '🇧🇷 SP';
                                                                                return t.local_currency_code;
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-1 flex-wrap">
                                                                        <Badge bg="secondary" className="px-1 py-0.5" style={{ fontSize: '9px', fontFamily: 'monospace' }}>#{t.id}</Badge>
                                                                        <span className="fw-semibold">{t.client?.name || '---'}</span>
                                                                    </div>
                                                                    <div className="text-dark">👤 {t.passenger_name}</div>
                                                                    <div className="text-muted small">{t.passenger_phone || 'Sin tel.'}</div>
                                                                </td>
                                                                <td>
                                                                    <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                                        <span className="text-success me-1">🛫</span>
                                                                        {isAirportPickup && <span className="badge bg-info text-white me-1" style={{ fontSize: '8px' }}>AERO</span>}
                                                                        {t.pickup_address}
                                                                    </div>
                                                                    <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                                        <span className="text-danger me-1">🏁</span>
                                                                        {isAirportDropoff && <span className="badge bg-info text-white me-1" style={{ fontSize: '8px' }}>AERO</span>}
                                                                        {t.dropoff_address}
                                                                    </div>
                                                                </td>
                                                                <td className="pe-3 text-muted">
                                                                    {t.additional_passengers && t.additional_passengers.length > 0 ? (
                                                                        <div style={{ fontSize: '0.75rem' }}>
                                                                            {t.additional_passengers.map(ap => (
                                                                                <div key={ap.id}>• {ap.full_name} ({ap.phone || 'S/T'})</div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted italic small">Ninguno</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* ── MODAL: Bitácora de Notificaciones Push a Conductores ── */}
            <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-info text-dark">
                    <Modal.Title className="fw-bold">🔔 Bitácora de Notificaciones Push</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <span className="small text-muted">Historial de alertas push diarias (últimos 5 días) y confirmaciones de lectura.</span>
                        <Form.Control
                            placeholder="🔍 Buscar por conductor..."
                            value={logsSearchTerm}
                            onChange={(e) => setLogsSearchTerm(e.target.value)}
                            style={{ maxWidth: '250px' }}
                            size="sm"
                        />
                    </div>

                    <Nav variant="pills" activeKey={logsTab} onSelect={(key) => setLogsTab(key)} className="mb-3 justify-content-center small">
                        <Nav.Item>
                            <Nav.Link eventKey="tomorrow" className="py-1 px-3 fw-bold">
                                📅 Agenda de Mañana
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="history" className="py-1 px-3 fw-bold">
                                🕒 Historial / Anteriores
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {/* Tarjeta de Resumen Visual - Solo visible para la agenda de mañana */}
                    {logsTab === 'tomorrow' && !isLoadingLogs && notificationLogs.length > 0 && (() => {
                        const today = new Date();
                        const tomorrow = new Date(today);
                        tomorrow.setDate(today.getDate() + 1);
                        
                        const year = tomorrow.getFullYear();
                        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
                        const day = String(tomorrow.getDate()).padStart(2, '0');
                        const tomorrowDateStr = `${year}-${month}-${day}`;
                        
                        const tomorrowLogs = notificationLogs.filter(log => log.scheduled_for_date === tomorrowDateStr);
                        
                        const confirmed = tomorrowLogs.filter(l => l.acknowledged);
                        const pending = tomorrowLogs.filter(l => !l.acknowledged);
                        
                        return (
                            <Row className="mb-3 g-2">
                                <Col md={6}>
                                    <Card className="border-success bg-light h-100">
                                        <Card.Body className="p-2">
                                            <div className="fw-bold text-success small mb-1">
                                                🟢 Confirmados / Enterados para Mañana ({confirmed.length}):
                                            </div>
                                            {confirmed.length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                                    {confirmed.map(l => (
                                                        <Badge key={l.id} bg="success" className="fw-normal">
                                                            {l.driver?.full_name || `Conductor #${l.driver_id}`}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-muted small">Nadie ha confirmado todavía.</div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="border-danger bg-light h-100">
                                        <Card.Body className="p-2">
                                            <div className="fw-bold text-danger small mb-1">
                                                🔴 Pendientes / Sin Confirmar para Mañana ({pending.length}):
                                            </div>
                                            {pending.length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                                    {pending.map(l => (
                                                        <Badge key={l.id} bg="danger" className="fw-normal" title={getFriendlyErrorMessage(l.error_message)}>
                                                            {l.driver?.full_name || `Conductor #${l.driver_id}`}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-muted small">No hay pendientes.</div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        );
                    })()}

                    {isLoadingLogs ? (
                        <div className="text-center p-4 text-muted">
                            <Spinner animation="border" variant="info" size="sm" className="me-2" />
                            Cargando registros de notificaciones...
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover responsive size="sm" className="align-middle small mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Fecha Envío</th>
                                        <th>Conductor</th>
                                        <th>Fecha Agenda</th>
                                        <th className="text-center">Servicios</th>
                                        <th>Estado Envío</th>
                                        <th className="text-center">Confirmación / Lectura</th>
                                        <th>Detalle / Error</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notificationLogs
                                        .filter(log => {
                                            // 1. Filtrar por búsqueda de conductor
                                            if (logsSearchTerm) {
                                                const name = log.driver?.full_name || '';
                                                if (!name.toLowerCase().includes(logsSearchTerm.toLowerCase())) {
                                                    return false;
                                                }
                                            }

                                            // 2. Determinar si es de mañana
                                            const today = new Date();
                                            const tomorrow = new Date(today);
                                            tomorrow.setDate(today.getDate() + 1);
                                            const year = tomorrow.getFullYear();
                                            const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
                                            const day = String(tomorrow.getDate()).padStart(2, '0');
                                            const tomorrowDateStr = `${year}-${month}-${day}`;

                                            const isTomorrow = log.scheduled_for_date === tomorrowDateStr;

                                            // 3. Filtrar por pestaña seleccionada
                                            if (logsTab === 'tomorrow') {
                                                return isTomorrow;
                                            } else {
                                                return !isTomorrow;
                                            }
                                        })
                                        .map(log => (
                                            <tr key={log.id}>
                                                <td>{new Date(log.sent_at).toLocaleString('es-CL')}</td>
                                                <td className="fw-bold text-dark">{log.driver?.full_name || `Conductor ID ${log.driver_id}`}</td>
                                                <td>{new Date(log.scheduled_for_date + 'T00:00:00').toLocaleDateString('es-CL')}</td>
                                                <td className="text-center fw-bold">{log.trips_count}</td>
                                                <td>
                                                    <Badge bg={log.status === 'sent' ? 'success' : (log.status === 'app_view' ? 'primary' : 'danger')}>
                                                        {log.status === 'sent' ? 'Enviado' : (log.status === 'app_view' ? 'Visto App' : 'Fallido')}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    {log.acknowledged ? (
                                                        <Badge bg="success" className="px-2 py-1" style={{ fontSize: '10px' }}>
                                                            ✓ Enterado ({new Date(log.acknowledged_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })})
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="danger" className="px-2 py-1" style={{ fontSize: '10px' }}>
                                                            ⌛ Pendiente
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className={log.status === 'sent' ? 'text-success' : 'text-danger fw-bold'}>
                                                    {getFriendlyErrorMessage(log.error_message)}
                                                </td>
                                                <td className="text-center">
                                                    {(() => {
                                                        const today = new Date();
                                                        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                                        const isPastDate = log.scheduled_for_date < todayStr;
                                                        if (isPastDate) return <span className="text-muted small">—</span>;
                                                        
                                                        return (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="py-0 px-2 fw-bold"
                                                                style={{ fontSize: '10px' }}
                                                                disabled={isResending[`${log.driver_id}_${log.scheduled_for_date}`]}
                                                                onClick={() => handleResendNotification(log.driver_id, log.scheduled_for_date, log.driver?.full_name || 'Conductor')}
                                                            >
                                                                {isResending[`${log.driver_id}_${log.scheduled_for_date}`] ? (
                                                                    <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
                                                                ) : (
                                                                    "🔄 Re-enviar Push"
                                                                )}
                                                            </Button>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        ))
                                    }
                                    {notificationLogs.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center p-3 text-muted">No hay registros de notificaciones.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button 
                        variant="success" 
                        onClick={handleTriggerNotifications} 
                        disabled={isTriggering || isLoadingLogs}
                    >
                        {isTriggering ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Despachando...
                            </>
                        ) : (
                            "🚀 Enviar Agenda de Mañana"
                        )}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowLogsModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            {/* ── MODAL: Previsualización de Envío WhatsApp (Pasajeros) ── */}
            <Modal show={showWhatsAppPreview} onHide={() => !isSendingConfirmations && setShowWhatsAppPreview(false)} size="lg" centered>
                <Modal.Header closeButton={!isSendingConfirmations} className="bg-success text-white">
                    <Modal.Title className="fw-bold">💬 Enviar Confirmaciones de WhatsApp</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    <Alert variant="info" className="py-2 px-3 small mb-3">
                        Se enviará la plantilla aprobada de WhatsApp a los teléfonos válidos del pasajero principal y acompañantes de las reservas seleccionadas.
                    </Alert>

                    <h6 className="fw-bold text-dark mb-2">Destinatarios a Notificar:</h6>
                    <div className="table-responsive mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm" className="small">
                            <thead className="bg-light sticky-top">
                                <tr>
                                    <th style={{ width: '40px', textAlign: 'center' }}>
                                        <Form.Check 
                                            type="checkbox"
                                            checked={
                                                whatsappRecipients.filter(r => r.isValid).length > 0 &&
                                                whatsappRecipients.filter(r => r.isValid).every(r => selectedRecipientKeys.includes(`${r.tripId}-${r.phone}`))
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedRecipientKeys(whatsappRecipients.filter(r => r.isValid).map(r => `${r.tripId}-${r.phone}`));
                                                } else {
                                                    setSelectedRecipientKeys([]);
                                                }
                                            }}
                                        />
                                    </th>
                                    <th>Servicio ID</th>
                                    <th>Destinatario</th>
                                    <th>Teléfono</th>
                                    <th>Rol</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {whatsappRecipients.map((r, idx) => {
                                    const key = `${r.tripId}-${r.phone}`;
                                    const isChecked = selectedRecipientKeys.includes(key);
                                    return (
                                        <tr key={idx} className={!r.isValid ? "table-warning" : ""}>
                                            <td style={{ textAlign: 'center' }}>
                                                <Form.Check 
                                                    type="checkbox"
                                                    disabled={!r.isValid}
                                                    checked={r.isValid && isChecked}
                                                    onChange={() => handleToggleRecipient(key)}
                                                />
                                            </td>
                                            <td className="fw-bold">#{r.tripId}</td>
                                            <td>{r.name}</td>
                                            <td>{r.phone || <span className="text-danger italic">Sin Teléfono</span>}</td>
                                            <td className="text-muted">{r.role}</td>
                                            <td>
                                                {r.isValid ? (
                                                    <div className="d-flex flex-column gap-1 align-items-start">
                                                        <Badge bg="success"><CheckCircle size={10} className="me-1"/> Válido</Badge>
                                                        {r.alreadySent && <Badge bg="warning" text="dark" style={{ fontSize: '9px' }}><Clock size={10} className="me-1"/> Re-envío (Ya Notificado)</Badge>}
                                                    </div>
                                                ) : (
                                                    <Badge bg="danger"><AlertTriangle size={10} className="me-1"/> Inválido / Falso</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <h6 className="fw-bold text-dark mb-2">Vista Previa del Mensaje (Plantilla Oficial Meta):</h6>
                    <div className="p-3 bg-light rounded border mb-2 small text-dark" style={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}>
                        {"Hola [Nombre], Somos Transportes Emilia. Le informamos su reserva del día *[Fecha]*.\n\n" +
                         "Reserva: *[Servicio ID]*\n" +
                         "Hora: [Hora]\n" +
                         "Origen: [Dirección Origen]\n" +
                         "Destino: [Dirección Destino]\n" +
                         "Conductor: [Nombre Conductor si es Booking / 'Por confirmar' para los demás]\n" +
                         "Teléfono Conductor: [Teléfono Conductor si es Booking / 'Por confirmar' para los demás]\n\n" +
                         "*Transportes Emilia*"}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWhatsAppPreview(false)} disabled={isSendingConfirmations}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={triggerSendWhatsApp} 
                        disabled={isSendingConfirmations || selectedRecipientKeys.length === 0}
                    >
                        {isSendingConfirmations ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2"/> Enviando...
                            </>
                        ) : (
                            <>
                                <MessageCircle size={16} className="me-2"/> Enviar a {selectedRecipientKeys.length} Destinatarios
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── MODAL: Preview de Importación Masiva ── */}
            <Modal show={showPreviewModal} onHide={() => { if (!importing) { setShowPreviewModal(false); setPreviewData(null); setPreviewFile(null); }}} size="lg" centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">📋 Previsualización de Importación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {importing && (
                        <div className="alert alert-primary mb-3 p-3 border-0 d-flex align-items-center gap-3 shadow-sm" style={{ backgroundColor: '#eff6ff', color: '#1e40af', borderRadius: '8px' }}>
                            <Spinner animation="border" variant="primary" />
                            <div>
                                <strong className="d-block">⏳ Procesando e importando reservas en la base de datos...</strong>
                                <small style={{ color: '#1d4ed8' }}>Por favor espera un momento sin cerrar esta pantalla.</small>
                            </div>
                        </div>
                    )}
                    {previewData && (
                        <>
                            <p className="text-muted small mb-3">
                                Archivo: <strong>{previewFile?.name}</strong> · {previewData.total_rows} filas procesadas
                            </p>

                            {/* Clientes Faltantes */}
                            {previewData.missing_clients && previewData.missing_clients.length > 0 && (
                                <div className="alert alert-danger mb-3 p-3 border-0 d-flex flex-column gap-2" style={{ backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '8px' }}>
                                    <div className="d-flex align-items-center gap-2 fw-bold">
                                        <span>⚠️ Clientes no registrados en el sistema:</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                        {previewData.missing_clients.map((client, idx) => (
                                            <span key={idx} className="badge bg-danger px-2 py-1" style={{ fontSize: '0.85em', fontWeight: '600' }}>
                                                {client}
                                            </span>
                                        ))}
                                    </div>
                                    <small style={{ color: '#b91c1c', marginTop: '4px', display: 'block' }}>
                                        <strong>Acción requerida:</strong> Debes registrar estos clientes en la plataforma (sección de clientes) antes de poder importar este archivo.
                                    </small>
                                </div>
                            )}

                            {/* Diagnóstico de columnas detectadas */}
                            {previewData.excel_columns && (
                                <details className="mb-3">
                                    <summary className="small text-primary" style={{cursor:'pointer'}}>
                                        🔍 Ver columnas detectadas en el Excel ({previewData.excel_columns.length} columnas)
                                        {' — '}
                                        {['Area','Área','area','área'].some(c => previewData.excel_columns.includes(c))
                                            ? <span className="text-success">✅ Área encontrada</span>
                                            : <span className="text-danger">❌ Área NO encontrada</span>}
                                        {' · '}
                                        {['Código Centro de Costo','Codigo Centro de Costo','Código CC','Codigo CC','CC'].some(c => previewData.excel_columns.includes(c))
                                            ? <span className="text-success">✅ CC encontrado</span>
                                            : <span className="text-danger">❌ CC NO encontrado</span>}
                                        {' · '}
                                        {['Solicitante','solicitante','SOLICITANTE'].some(c => previewData.excel_columns.includes(c))
                                            ? <span className="text-success">✅ Solicitante encontrado</span>
                                            : <span className="text-danger">❌ Solicitante NO encontrado</span>}
                                    </summary>
                                    <div className="mt-2 p-2 bg-light rounded small font-monospace" style={{maxHeight:'120px', overflowY:'auto', fontSize:'0.75em'}}>
                                        {previewData.excel_columns.map((col, i) => (
                                            <span key={i} className={
                                                ['Area','Área','area','área'].includes(col) ? 'badge bg-success me-1 mb-1' :
                                                ['Código Centro de Costo','Codigo Centro de Costo','Código CC','Codigo CC','CC'].includes(col) ? 'badge bg-primary me-1 mb-1' :
                                                ['Solicitante','solicitante','SOLICITANTE'].includes(col) ? 'badge bg-info me-1 mb-1' :
                                                'badge bg-secondary me-1 mb-1 opacity-50'
                                            }>{col}</span>
                                        ))}
                                    </div>
                                </details>
                            )}

                            {/* Resumen de contadores */}
                            <Row className="g-2 mb-4">
                                <Col xs={6} md={3}>
                                    <div className="border rounded p-3 text-center h-100" style={{ background: '#f0fdf4', borderColor: '#bbf7d0 !important' }}>
                                        <div className="fw-bold fs-4 text-success">{previewData.to_create}</div>
                                        <div className="small text-muted">✅ Se crearán</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="border rounded p-3 text-center h-100" style={{ background: '#eff6ff', borderColor: '#bfdbfe !important' }}>
                                        <div className="fw-bold fs-4 text-primary">{previewData.to_update_area_cc}</div>
                                        <div className="small text-muted">🔄 Se rellena Área/CC/Sol</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="border rounded p-3 text-center h-100" style={{ background: '#f9fafb' }}>
                                        <div className="fw-bold fs-4 text-secondary">{previewData.already_complete}</div>
                                        <div className="small text-muted">⏭️ Ya tienen Área/CC/Sol</div>
                                    </div>
                                </Col>
                                <Col xs={6} md={3}>
                                    <div className="border rounded p-3 text-center h-100" style={{ background: '#fefce8' }}>
                                        <div className="fw-bold fs-4 text-warning">{(previewData.no_changes_existing || 0) + (previewData.skipped_invalid || 0)}</div>
                                        <div className="small text-muted">⚠️ Sin cambios</div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Filtros y Alerta de Dirección */}
                            {previewData.preview_rows?.length > 0 && (
                                <>
                                    {previewData.preview_rows.some(r => (!r.pickup_geo_ok || !r.dropoff_geo_ok) && r.action === 'create') && (
                                        <div className="alert alert-warning mb-2 py-2 px-3 small border-0 d-flex align-items-center gap-2" style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                                            <span>📍 <strong>Verificación de Geolocalización:</strong> Algunas direcciones de origen o destino parecen cortas o no tienen numeración de calle. El sistema intentará geolocalizarlas al guardar, pero te recomendamos revisarlas.</span>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                        <span className="fw-bold small text-slate-700">📋 Vista Previa Completa ({previewData.preview_rows.length} reservas en Excel):</span>
                                        <ButtonGroup size="sm">
                                            <Button variant={previewFilter === 'all' ? 'dark' : 'outline-secondary'} onClick={() => setPreviewFilter('all')}>Todas ({previewData.preview_rows.length})</Button>
                                            <Button variant={previewFilter === 'create' ? 'success' : 'outline-success'} onClick={() => setPreviewFilter('create')}>✅ Crear ({previewData.to_create || 0})</Button>
                                            <Button variant={previewFilter === 'update' ? 'primary' : 'outline-primary'} onClick={() => setPreviewFilter('update')}>🔄 Actualizar ({previewData.to_update_area_cc || 0})</Button>
                                            <Button variant={previewFilter === 'skip' ? 'secondary' : 'outline-secondary'} onClick={() => setPreviewFilter('skip')}>⏭️ Sin cambios ({previewData.already_complete + (previewData.no_changes_existing || 0)})</Button>
                                            {previewData.skipped_invalid > 0 && (
                                                <Button variant={previewFilter === 'error' ? 'danger' : 'outline-danger'} onClick={() => setPreviewFilter('error')}>❌ Error ({previewData.skipped_invalid})</Button>
                                            )}
                                        </ButtonGroup>
                                    </div>

                                    <div style={{ maxHeight: '420px', overflowY: 'auto' }} className="border rounded">
                                        <Table size="sm" bordered hover responsive className="small mb-0 align-middle">
                                            <thead className="table-light sticky-top">
                                                <tr style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    <th style={{ minWidth: '130px' }}>Cliente / Ref</th>
                                                    <th style={{ minWidth: '140px' }}>Pasajero</th>
                                                    <th style={{ minWidth: '130px' }}>Fecha y Hora</th>
                                                    <th style={{ minWidth: '200px' }}>Origen (Pickup)</th>
                                                    <th style={{ minWidth: '200px' }}>Destino (Dropoff)</th>
                                                    <th style={{ minWidth: '90px' }} className="text-end">Valor</th>
                                                    <th style={{ minWidth: '140px' }}>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewData.preview_rows
                                                    .filter(r => {
                                                        if (previewFilter === 'create') return r.action === 'create';
                                                        if (previewFilter === 'update') return r.action === 'update_area_cc';
                                                        if (previewFilter === 'skip') return ['skip_complete', 'skip_no_data'].includes(r.action);
                                                        if (previewFilter === 'error') return r.action === 'error';
                                                        return true;
                                                    })
                                                    .map((r, i) => {
                                                        const actionBadge = {
                                                            create:          { bg: 'success',   label: '✅ Se Creará Nuevo' },
                                                            update_area_cc:  { bg: 'primary',   label: '🔄 Actualiza Área/CC' },
                                                            skip_complete:   { bg: 'secondary', label: '⏭️ Ya Existe Completo' },
                                                            skip_no_data:    { bg: 'warning', text: 'dark', label: '⚠️ Sin Cambios' },
                                                            error:           { bg: 'danger',    label: '❌ Error / Inválido' },
                                                        }[r.action] || { bg: 'light', text: 'dark', label: r.action };

                                                        return (
                                                            <tr key={i} className={r.action === 'error' ? 'table-danger' : r.action === 'create' ? 'table-success' : ''}>
                                                                <td>
                                                                    <div className="fw-bold text-dark">{r.client || 'Cliente General'}</div>
                                                                    <span className="badge bg-light text-primary border font-monospace mt-1" style={{ fontSize: '10px' }}>{r.ref}</span>
                                                                </td>
                                                                <td>
                                                                    <div className="fw-semibold text-slate-800">{r.passenger}</div>
                                                                    {r.passenger_phone && <small className="text-muted d-block">{r.passenger_phone}</small>}
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <div className="fw-bold">{r.fecha}</div>
                                                                    <small className="text-muted">{r.status || 'Creada'}</small>
                                                                </td>
                                                                <td style={{ fontSize: '11px' }}>
                                                                    <div className="text-truncate" style={{ maxWidth: '220px' }} title={r.pickup_address}>
                                                                        {r.pickup_address}
                                                                    </div>
                                                                    {r.pickup_address !== '-' && (
                                                                        r.pickup_geo_ok ? (
                                                                            <Badge bg="success" style={{ fontSize: '9px' }}>🟢 Geo OK</Badge>
                                                                        ) : (
                                                                            <Badge bg="warning" text="dark" style={{ fontSize: '9px' }} title="Dirección sin número o muy corta">⚠️ Revisar Nº / Geo</Badge>
                                                                        )
                                                                    )}
                                                                </td>
                                                                <td style={{ fontSize: '11px' }}>
                                                                    <div className="text-truncate" style={{ maxWidth: '220px' }} title={r.dropoff_address}>
                                                                        {r.dropoff_address}
                                                                    </div>
                                                                    {r.dropoff_address !== '-' && (
                                                                        r.dropoff_geo_ok ? (
                                                                            <Badge bg="success" style={{ fontSize: '9px' }}>🟢 Geo OK</Badge>
                                                                        ) : (
                                                                            <Badge bg="warning" text="dark" style={{ fontSize: '9px' }} title="Dirección sin número o muy corta">⚠️ Revisar Nº / Geo</Badge>
                                                                        )
                                                                    )}
                                                                </td>
                                                                <td className="text-end fw-bold text-slate-700">
                                                                    ${Math.round(r.price || 0).toLocaleString()}
                                                                </td>
                                                                <td>
                                                                    <Badge bg={actionBadge.bg} text={actionBadge.text} className="fw-bold py-1 px-2 mb-1 d-block text-center">
                                                                        {actionBadge.label}
                                                                    </Badge>
                                                                    <small className="text-muted d-block" style={{ fontSize: '10px', lineHeight: '1.2' }}>{r.detail}</small>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="outline-secondary" onClick={() => { setShowPreviewModal(false); setPreviewData(null); setPreviewFile(null); }} disabled={importing}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleConfirmImport} 
                        disabled={importing || (previewData?.missing_clients && previewData.missing_clients.length > 0)}
                        className="fw-bold px-4"
                    >
                        {importing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Importando reservas...
                            </>
                        ) : (previewData?.missing_clients && previewData.missing_clients.length > 0)
                            ? '❌ Bloqueado: Clientes faltantes'
                            : `✅ Confirmar e Importar (${(previewData?.to_create || 0) + (previewData?.to_update_area_cc || 0)} cambios)`}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Rápido de Link de Pago (Transbank CLP / dLocal USD) */}
            <Modal show={showDLocalModal} onHide={() => setShowDLocalModal(false)} centered size={dlocalSelectedTrips.length > 1 ? "lg" : "md"}>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold text-primary mb-0 d-flex align-items-center gap-2">
                        💳 Link de Pago <Badge bg="success" style={{ fontSize: '0.65rem' }}>ONLINE</Badge>
                        {dlocalSelectedTrips.length > 1 ? (
                            <span className="text-dark fw-bold">| Cobro Grupal ({dlocalSelectedTrips.length} Reservas)</span>
                        ) : (
                            dlocalTrip && <span className="text-dark fw-normal">| Viaje #{dlocalTrip.id}</span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    {(() => {
                        const isMulti = dlocalSelectedTrips.length > 1;
                        const effectiveUsdRate = dlocalUsdRate > 1 ? dlocalUsdRate : 925;
                        const multiTotalCLP = isMulti 
                            ? dlocalSelectedTrips.reduce((acc, t) => acc + Number(t.final_price || t.estimated_price || 0), 0)
                            : Number(dlocalTrip?.final_price || dlocalTrip?.estimated_price || 0);
                        const multiTotalUSD = (Math.round((multiTotalCLP / effectiveUsdRate) * 100) / 100).toFixed(2);
                        const isAllPaid = isMulti ? dlocalSelectedTrips.every(t => t.is_paid) : dlocalTrip?.is_paid;

                        if (!dlocalTrip && !isMulti) return null;

                        return (
                            <div>
                                {isMulti ? (
                                    <div className="mb-3 p-2 bg-light rounded border">
                                        {(() => {
                                            const firstTrip = dlocalSelectedTrips[0];
                                            const clientName = firstTrip?.client?.name || firstTrip?.client_name || 'Particular / Convenio';
                                            return (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div>
                                                        <span className="fw-bold small text-primary">📦 {dlocalSelectedTrips.length} Reservas Agrupadas</span>
                                                        <span className="badge bg-light text-dark border ms-2 small">{clientName}</span>
                                                    </div>
                                                    {isAllPaid ? (
                                                        <Badge bg="success">✓ Todos Pagados</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">Pendiente de Pago</Badge>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div style={{ maxHeight: '150px', overflowY: 'auto' }} className="border rounded bg-white p-1 mb-2">
                                            <table className="table table-sm table-borderless mb-0" style={{ fontSize: '0.75rem' }}>
                                                <thead>
                                                    <tr className="border-bottom text-muted">
                                                        <th># ID</th>
                                                        <th>Pasajero</th>
                                                        <th>Fecha / Hora</th>
                                                        <th>Ruta</th>
                                                        <th className="text-end">Monto</th>
                                                        <th className="text-center">Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dlocalSelectedTrips.map(st => (
                                                        <tr key={st.id} className="border-bottom">
                                                            <td className="fw-bold text-primary">#{st.id}</td>
                                                            <td className="text-truncate" style={{ maxWidth: '110px' }}>{st.passenger_name || 'Particular'}</td>
                                                            <td className="text-muted">{st.scheduled_time ? String(st.scheduled_time).slice(5, 16) : '-'}</td>
                                                            <td className="text-truncate text-muted" style={{ maxWidth: '140px' }} title={`${st.pickup_address} ➔ ${st.dropoff_address}`}>
                                                                {st.pickup_address || '---'} ➔ {st.dropoff_address || '---'}
                                                            </td>
                                                            <td className="text-end fw-semibold">${Number(st.final_price || st.estimated_price || 0).toLocaleString('es-CL')}</td>
                                                            <td className="text-center">
                                                                {st.is_paid ? <Badge bg="success" style={{ fontSize: '0.65rem' }}>Pagado</Badge> : <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Pendiente</Badge>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-1 border-top">
                                            <span className="text-muted small fw-bold">Total Acumulado a Cobrar:</span>
                                            <span className="fw-bold text-primary" style={{ fontSize: '1.05rem' }}>
                                                ${multiTotalCLP.toLocaleString('es-CL')} CLP
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-3 p-2 bg-light rounded border">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted small">Cliente:</span>
                                            <span className="fw-bold small">{dlocalTrip.client?.name || dlocalTrip.client_name || 'Particular / Emilia'}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted small">Pasajero:</span>
                                            <span className="small">{dlocalTrip.passenger_name || 'Sin pasajero'}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted small">Tarifa Base (CLP):</span>
                                            <span className="fw-bold text-primary small">
                                                ${Number(dlocalTrip.final_price || dlocalTrip.estimated_price || 0).toLocaleString('es-CL')}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="text-muted small">Estado de Pago:</span>
                                            {dlocalTrip.is_paid ? (
                                                <Badge bg={dlocalTrip.payment_method === 'DLocal' ? 'primary' : 'success'}>
                                                    ✓ {dlocalTrip.payment_method === 'DLocal' ? 'Pagado vía dLocal' : dlocalTrip.payment_method === 'Webpay' ? 'Pagado vía Transbank' : (dlocalTrip.payment_method || 'Pagado')}
                                                </Badge>
                                            ) : (
                                                <Badge bg="secondary">Pendiente de Pago</Badge>
                                            )}
                                        </div>
                                        {!dlocalTrip.is_paid && dlocalTrip.payment_auth_code && String(dlocalTrip.payment_auth_code).startsWith('ORDER:') && (
                                            <div className="d-flex justify-content-between align-items-center mt-1 pt-1 border-top">
                                                <span className="text-muted" style={{ fontSize: '0.72rem' }}>Orden Generada:</span>
                                                <span className="badge bg-warning text-dark font-monospace" style={{ fontSize: '0.7rem' }}>
                                                    {String(dlocalTrip.payment_auth_code).replace('ORDER:', '').split('|')[0]}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!isAllPaid ? (
                                    <div>
                                        {dlocalTrip?.id && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="w-100 fw-bold py-2 mb-3 d-flex align-items-center justify-content-center gap-2"
                                                onClick={() => handleCheckDLocalStatus(dlocalTrip.id)}
                                                disabled={dlocalLoading}
                                            >
                                                {dlocalLoading ? (
                                                    <>
                                                        <Spinner size="sm" animation="border" />
                                                        Verificando estado de pago...
                                                    </>
                                                ) : (
                                                    <>🔄 Verificar Estado de Pago</>
                                                )}
                                            </Button>
                                        )}

                                        <div className="mb-3">
                                            <Form.Label className="small fw-bold">Seleccionar Moneda de Cobro:</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Button 
                                                    variant={dlocalCurrency === 'CLP' ? 'primary' : 'outline-secondary'}
                                                    className="flex-fill d-flex flex-column align-items-center py-2"
                                                    onClick={() => { setDlocalCurrency('CLP'); setDlocalLinkData(null); }}
                                                >
                                                    <span className="fw-bold">Transbank en CLP ($)</span>
                                                    <small style={{ fontSize: '0.75rem' }}>Pesos Chilenos · Webpay Plus</small>
                                                </Button>
                                                <Button 
                                                    variant={dlocalCurrency === 'USD' ? 'success' : 'outline-secondary'}
                                                    className="flex-fill d-flex flex-column align-items-center py-2"
                                                    onClick={() => { setDlocalCurrency('USD'); setDlocalLinkData(null); }}
                                                >
                                                    <span className="fw-bold">dLocal en USD (US$)</span>
                                                    <small style={{ fontSize: '0.75rem' }}>Dólares (TC EmiGo: ${effectiveUsdRate})</small>
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Form.Label className="small fw-bold mb-0">Monto a Cobrar:</Form.Label>
                                                <Button 
                                                    variant="link" 
                                                    size="sm" 
                                                    className="p-0 text-decoration-none small"
                                                    onClick={() => setShowCustomAmountInput(!showCustomAmountInput)}
                                                >
                                                    {showCustomAmountInput ? 'Usar Tarifa Estándar' : '✏️ Ingresar Monto / Diferencia'}
                                                </Button>
                                            </div>
                                            {showCustomAmountInput ? (
                                                <InputGroup size="sm" className="mb-2">
                                                    <InputGroup.Text>{dlocalCurrency === 'USD' ? 'US$' : '$'}</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        placeholder={`Monto en ${dlocalCurrency}`}
                                                        value={dlocalCustomAmount}
                                                        onChange={e => setDlocalCustomAmount(e.target.value)}
                                                    />
                                                </InputGroup>
                                            ) : (
                                                dlocalCurrency === 'USD' ? (
                                                    <div className="text-muted small mb-2">
                                                        Total acumulado: <strong>${multiTotalCLP.toLocaleString('es-CL')} CLP</strong>
                                                        <span className="ms-1 text-success fw-bold">
                                                            (aprox. US$ {multiTotalUSD})
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="text-muted small mb-2">
                                                        Se cobrará el total {isMulti ? `de las ${dlocalSelectedTrips.length} reservas` : 'del servicio'}: <strong>${multiTotalCLP.toLocaleString('es-CL')} CLP</strong>
                                                    </div>
                                                )
                                            )}
                                        </div>

                                        <Button 
                                            variant={dlocalCurrency === 'USD' ? 'success' : 'primary'} 
                                            className="w-100 fw-bold py-2 mb-3"
                                            onClick={() => handleGenerateDLocalLinkFromTable(dlocalCurrency === 'CLP' ? 'webpay' : 'dlocal')}
                                            disabled={dlocalLoading}
                                        >
                                            {dlocalLoading ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" />
                                                    {dlocalCurrency === 'CLP' ? 'Conectando con Transbank...' : 'Generando link seguro...'}
                                                </>
                                            ) : dlocalCurrency === 'CLP' ? (
                                                `💳 Generar Link Transbank en CLP ($${dlocalCustomAmount ? Number(dlocalCustomAmount).toLocaleString('es-CL') : multiTotalCLP.toLocaleString('es-CL')})${isMulti ? ` - ${dlocalSelectedTrips.length} Reservas` : ''}`
                                            ) : (
                                                `⚡ Generar Link dLocal en USD (US$ ${dlocalCustomAmount ? dlocalCustomAmount : multiTotalUSD})${isMulti ? ` - ${dlocalSelectedTrips.length} Reservas` : ''}`
                                            )}
                                        </Button>

                                        {dlocalLinkData && (() => {
                                            const paymentUrl = dlocalLinkData.checkout_url || dlocalLinkData.redirect_url;
                                            const isWebpayLink = dlocalLinkData.provider === 'webpay';
                                            return (
                                                <div className="p-3 bg-white rounded border border-success">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="fw-bold text-success">
                                                            ✅ Link {isWebpayLink ? 'Transbank (Webpay Plus)' : 'dLocal'} Creado: {dlocalLinkData.currency} {dlocalLinkData.currency === 'USD' ? `US$ ${dlocalLinkData.amount}` : `$${Number(dlocalLinkData.amount).toLocaleString('es-CL')}`}
                                                            {dlocalLinkData.exchange_rate && dlocalLinkData.currency === 'USD' && (
                                                                <span className="badge bg-light text-dark border ms-2" style={{ fontSize: '0.7rem' }}>TC: ${dlocalLinkData.exchange_rate}</span>
                                                            )}
                                                            {isMulti && (
                                                                <Badge bg="info" className="ms-2">Grupal ({dlocalSelectedTrips.length})</Badge>
                                                            )}
                                                        </span>
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm"
                                                            onClick={() => {
                                                                if (paymentUrl) {
                                                                    navigator.clipboard.writeText(paymentUrl);
                                                                    toast.success("Link copiado al portapapeles!");
                                                                }
                                                            }}
                                                        >
                                                            📋 Copiar Link
                                                        </Button>
                                                    </div>
                                                    {isWebpayLink && dlocalLinkData.environment === 'sandbox' && dlocalLinkData.token_ws && (
                                                        <div className="d-flex justify-content-between align-items-center p-2 mb-2 rounded" style={{ backgroundColor: '#f3ebff', border: '1px solid #d8b4fe', fontSize: '0.72rem' }}>
                                                            <div className="text-truncate me-2" style={{ color: '#5b21b6', fontFamily: 'monospace' }}>
                                                                <strong>token_ws:</strong> {dlocalLinkData.token_ws}
                                                            </div>
                                                            <Button
                                                                variant="outline-dark"
                                                                size="sm"
                                                                style={{ fontSize: '0.68rem', padding: '2px 8px' }}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(dlocalLinkData.token_ws);
                                                                    toast.success("token_ws copiado!");
                                                                }}
                                                            >
                                                                Copiar Token
                                                            </Button>
                                                        </div>
                                                    )}
                                                    <div className="p-2 bg-light rounded text-break small mb-2 text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {paymentUrl || 'Generando enlace...'}
                                                    </div>
                                                    <div className="d-flex gap-2 mb-2">
                                                        {paymentUrl ? (
                                                            <a 
                                                                href={paymentUrl} 
                                                                target="_blank" 
                                                                rel="noreferrer" 
                                                                className="btn btn-outline-success flex-grow-1 fw-bold"
                                                            >
                                                                Abrir Checkout ↗
                                                            </a>
                                                        ) : (
                                                            <Button variant="secondary" disabled className="flex-grow-1 fw-bold">
                                                                Enlace no disponible
                                                            </Button>
                                                        )}
                                                        <Button 
                                                            variant="success" 
                                                            className="flex-grow-1 fw-bold text-white d-flex align-items-center justify-content-center gap-1"
                                                            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                                                            onClick={handleSendDLocalWhatsApp}
                                                            disabled={dlocalSendingWhatsApp || !paymentUrl}
                                                        >
                                                            {dlocalSendingWhatsApp ? (
                                                                <>
                                                                    <Spinner size="sm" animation="border" className="me-1" />
                                                                    Enviando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span>💬 Enviar por WhatsApp</span>
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>

                                                    <div className="mt-2 pt-2 border-top">
                                                        <div className="d-flex align-items-center justify-content-between text-muted small mb-1" style={{ fontSize: '0.75rem' }}>
                                                            <span>Destinatario WhatsApp:</span>
                                                            <span className="fw-bold text-dark">{dlocalTrip?.passenger_name || 'Pasajero'}</span>
                                                        </div>
                                                        <InputGroup size="sm">
                                                            <InputGroup.Text style={{ fontSize: '0.75rem' }}>📞</InputGroup.Text>
                                                            <Form.Control 
                                                                size="sm"
                                                                type="text"
                                                                placeholder="Teléfono móvil (+569...)"
                                                                value={dlocalPhone}
                                                                onChange={e => setDlocalPhone(e.target.value)}
                                                                style={{ fontSize: '0.75rem' }}
                                                            />
                                                        </InputGroup>
                                                    </div>
                                                    <div className="mt-2 text-center">
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="text-decoration-none small text-muted"
                                                            onClick={() => handleCheckDLocalStatus(dlocalTrip.id)}
                                                            disabled={dlocalLoading}
                                                        >
                                                            🔄 ¿Ya pagaste? Clic aquí para sincronizar y verificar
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="text-center p-3">
                                        <div className="text-success mb-2" style={{ fontSize: '1.5rem' }}>✓</div>
                                        <h6 className="fw-bold">
                                            {isMulti ? 'Todas las reservas seleccionadas figuran como Pagadas' : 'Este viaje ya figura como Pagado'}
                                        </h6>
                                        {dlocalTrip?.payment_auth_code && (
                                            <div className="text-muted small mb-3">ID / Cód: {dlocalTrip.payment_auth_code}</div>
                                        )}
                                        <div className="d-flex flex-column gap-2">
                                            <Button 
                                                variant="success"
                                                size="sm"
                                                className="fw-bold d-flex align-items-center justify-content-center gap-1"
                                                onClick={() => window.open(`/comprobante/${dlocalTrip.id}`, '_blank')}
                                            >
                                                📄 Ver Comprobante de Pago Oficial
                                            </Button>
                                            <Button 
                                                variant="outline-primary"
                                                size="sm"
                                                className="fw-bold"
                                                onClick={() => {
                                                    setDlocalTrip(prev => ({ ...prev, is_paid: false }));
                                                    setDlocalSelectedTrips(prev => prev.map(t => ({ ...t, is_paid: false })));
                                                    setShowCustomAmountInput(true);
                                                }}
                                            >
                                                ➕ Generar Cobro Adicional / Diferencia
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer className="border-0 bg-light py-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowDLocalModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TripsLogView;
